/**
 * SDMS Data Package — batch review + workflow detail view.
 *
 * Full-screen overlay (or a windowed popup, when opened as the QC batch's Data
 * Package) over one analytical batch:
 *
 *   • Top toolbar:   Validate/Approve · Refresh · Project Notes · SDMS · Audit Trail · Close
 *                    (windowed Data Package mode also shows Submit/Validate/Approve + Rollback)
 *   • Breadcrumb +   "SDMS Result Validation - {Test}"
 *   • Workflow flow  Select a QB › Batch Results › Check Points › Review Log › Validate
 *   • Instruction    ① Check SDMS Result  ② Mark Batch Results & Check Points  ③ Project Notes (if needed)  ④ Mark Validation in Review Log
 *   • Batch Information section (QC Batch ID, Submitted By, Test, Date Submitted,
 *                                Method, Template ID, Matrix, Status)
 *   • Tabs (top):    Batch Results | Check Points | Trend
 *   • Tabs (bottom): Review Log    | Attachments
 *
 * All grids read LIVE SQL — Batch Results + Check Points (dbo.DataPackageChecklistQC),
 * Review Log (dbo.DataPackageReviewLog) and Attachments (dbo.DataPackageAttachments,
 * with add/delete) — via the result-validation endpoints. In SDMS the windowed Data
 * Package popup's only workflow action is Submit (OG QBIDSubmit, 1→2); validation and
 * approval run on the batch axis. Batch-level Validate/Approve routes through the same
 * callback the list-view uses so audit state and optimistic row updates stay consistent.
 */
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  postValidate,
  postApprove,
  postSaveSdmsEdits,
  onReviewLogWrite,
  type SdmsBatch,
} from "./SdmsValidationActions";
import { fmtLocalDate, fmtLocalDateTime, fmtSignatureStamp, nowClientWire } from "../../utils/datetime";
import { useHotkeys, formatCombo, type HotkeyDef } from "../../components/hotkeys";
import { useAuthStore } from "../../auth/store";
import { showApprovedColumns } from "./batchResultColumns";
import { NotesListPopup, useNotesCount, refreshProjectNotes } from "./SdmsProjectNotes";
import { SdmsQualifierPicker } from "./SdmsQualifierPicker";
import { SdmsSpreadsheet, type SdmsNavBatch } from "./SdmsSpreadsheet";
import { dfOverlayKey } from "./sdmsDfOverlay";
import { SdmsTrendTab } from "./SdmsTrendTab";
// Routing the Trend icon — the pure router (which chart belongs to a row) + the
// icon/popup it drives. The icon adds no maths; it reuses the control-chart
// engine and the trend endpoint (standing rule 8). See trendRouting.ts.
import { resolveRoutes, type TrendRow, type TrendRoute } from "../trend/trendRouting";
import { categorizeRow, type ResultCategory } from "./resultCategory";
import {
  resultSelectionGate, reviewMarkGate, unreviewedCategories,
} from "./reviewSelectionGate";
import { parsePermitLimits, permitResultValue, permitRowClassFor, type PermitLimit } from "./permitLimit";
import { usePermitRowColors } from "./permitRowColors";
import { renderPermitLimitCell } from "./permitLimitCell";
import { permitLimitColumnWidth, permitLimitLines } from "./permitLimitWidth";
import { fontOf, DEFAULT_GRID_FONT } from "../../components/AppDataGrid/autoFitWidth";
import { isMicroTest } from "../trend/microClassify";
import { TrendIcon, TrendChartPopup } from "../trend/TrendChartPopup";
import { useRawData, resolveRawDataFileOid, downloadRawDataFile, type ApiRawDataInfo } from "./rawDataPreview";
import { AuditTrailPopup } from "../../components/AuditTrailButton";
import { isAuditEnabled, useAuditTrailConfig } from "../../utils/auditConfig";
import { api } from "../../api/client";
import { fetchPlmDataPackageResults, type PlmDataPackageResultRow } from "./plm/plm.api";
import { runLoading } from "../../components/loading/loadingStore";
import { qcBatchApi } from "./qcBatch.api";
import { navigateToPage } from "../../layouts/navigateTo";
// The "QC Batches" toolbar button opens THIS batch's QC Batch detail as a MODAL
// POPUP over the data-package page (user request 2026-10-30: "open a separate QC
// Batch window, keep this data package page, not fully switch"). Shared with the
// SDMS Result Validation / Approval list's QCBatchID link (standing rule 8).
import { QcBatchModal } from "./QcBatchModal";
// Shared Excel-style column filter (CLAUDE.md §18.6) — same funnel/chevron the
// Project Notes popup and every AppDataGrid use.  Owned by ColumnFilterDropdown.
import {
  ColFilterButton,
  passesColumnFilter,
  cascadeRows,
  type ColumnFilterValue,
} from "../../components/ColumnFilterDropdown";
import { useSort, SortableHeader, type UseSortApi } from "../../components/SortableHeader";
// Shared header right-click menu (Sort · …) — the SAME component AppDataGrid uses,
// so these hand-rolled .dv-tbl grids get the identical global grid features
// (client 2026-08-03: "I want all the grids, list AND detail view — global").
import { HeaderContextMenu } from "../../components/AppDataGrid/HeaderContextMenu";
import { ColumnChooser } from "../../components/AppDataGrid/ColumnChooser";
import { loadGridPrefs, saveGridPrefs, type HeaderMenuColumns, type GridPrefs } from "../../components/AppDataGrid";
// Shared "Export to" dropdown (§14b / standing rule 8) — the SAME full ALPACA
// format set, Save-As picker, global loader and success acknowledgement every
// other form's export uses.  Never re-implemented per form.
import { ExportMenu } from "../../components/AppToolbar";
import {
  BR_FROZEN_ID,
  batchResultColumnCatalog,
  legacyBatchResultColumnCatalog,
  buildGridExport,
  checkPointColumnCatalog,
  reorderableIds,
  visibleExportColumns,
  reviewLogColumnCatalog,
  attachmentColumnCatalog,
} from "./sdmsGridExport";
import { useNotify } from "../../components/notifications";
// Milestone-success CENTERED modal (§18.6) for the Validate / Approve completions.
import { showDialog, useConfirm } from "../../components/confirm";
import { AppDialog } from "../../components/AppDialog/AppDialog";
import "./SdmsPackageDetail.css";

// ── stable row shape (subset of AnalyticalBatchRow we read) ──────────────
export interface SdmsPackageRow extends SdmsBatch {
  createdDate?: string | null;
  matrix?: string | null;
  // Optional fields the list endpoint already returns:
  // - reviewedBy / reviewedDate (peer review, stamped on batch entity)
  // - rolledBackBy / rolledBackDate / rollbackReason
}

interface SdmsPackageDetailProps {
  /** The batch the user clicked "Open Package" on. */
  batch: SdmsPackageRow;
  /** SDMS Result APPROVAL (Level-3) mode — same package view, but the terminal
   *  action is "Approve" not "Validate".  The batch is already SDMS-Validated,
   *  so the approve action + its workflow land with the SkipResultApproval flow
   *  (Part B); here it drives only the labels (button · stepper · banner). */
  approval?: boolean;
  /** Close the overlay. */
  onClose: () => void;
  /** Refetch the list — called after Validate succeeds so DV-1-1/DV-1-2 syncs. */
  onRefresh: () => void;
  /** Called when the user validates from inside the overlay.  Receives the new
   *  server-stamped validatedBy + ISO timestamp so the parent can optimistically
   *  update its row state (same callback wired to the list-view Validate). */
  onValidated: (validatedBy: string, validatedAt: string) => void;
  /** A PLM batch: render legacy's "PLM Results" grid as the top tab instead of
   *  the SDMS category tabs. Legacy's package modal for a PLM batch shows
   *  PLMStereoscopicObservation_ListView_PLMDPPreview, not the spreadsheet, so
   *  one popup serves both kinds without either pretending to be the other.
   *  Pass the batch Oid; omit for an SDMS batch and nothing changes. */
  plmBatchOid?: string | null;
  /** What legacy's "PLM" button does — open the batch's PLM entry screen. When
   *  the popup was opened FROM that screen the default (onClose) lands on it. */
  onOpenPlm?: () => void;
  /** Windowed (SDMS Result Entry) mode only — called after the analyst SUBMITS the Data Package,
   *  which advances the batch to Pending Validation (OG Complete/QBIDSubmit). Lets the entry page
   *  drop to View so a submitted batch can't be re-edited. Optional. */
  onSubmitted?: () => void;
  /** Replaces the built-in "Submitted" success dialog after a submit: the host shows its own
   *  (PLM adds "Go to Validation", owner 2026-11-03). `advanced` is false for a PARTIAL PLM submit
   *  (other analysts still have layers to submit) and true once the batch itself moved on.
   *  Optional — without it the package shows its usual dialog. */
  onSubmitSuccess?: (advanced: boolean) => void;
  /** All SDMS batches — passed through to the SDMS spreadsheet's ribbon
   *  Test / QCBatchID pickers.  Optional. */
  allBatches?: SdmsNavBatch[];
  /** MAC write permission — when false the Validate/Approve button is hidden
   *  and the overlay shows a read-only banner (MAC Level: Read). */
  canWrite?: boolean;
  /** View-visibility check (AppShell `canSee`) used to gate the cross-nav buttons:
   *  QC Batches / Prep Batches are HIDDEN when the user has no permission to
   *  that form (user report 2026-10-27). The SDMS (read-only spreadsheet) button
   *  is NOT gated — see its render below (owner decision 2026-10-29).
   *  Falls open when not provided. */
  canSeeForm?: (id: string) => boolean;
  /** Render as a centered MODAL POPUP over a dimmed backdrop (portalled to
   *  document.body), instead of the default full-content overlay.  Used by the
   *  SDMS Result Entry "Data Package" ribbon button to mirror OG's
   *  TargetWindow.NewModalWindow; Result Validation leaves this unset and keeps
   *  the full-content view.  In popup mode the workflow stepper is not portalled
   *  to the shell top bar (the popup is self-contained). */
  windowed?: boolean;
  /** The DataPackage's own workflow Status (1 PendingSubmission · 2 PendingValidation · 3
   *  Validated · …), from the create-or-find call. In popup (windowed) mode it drives the
   *  DataPackage terminal action — Submit (1) / Validate (2) — instead of the SDMS-results
   *  Validate, and the first-open Peer Review row. Unused in full-content (Result Validation). */
  packageStatus?: number | null;
  /** True when the popup was opened from INSIDE the SDMS Result Entry form (its "Data
   *  Package" ribbon). The SDMS spreadsheet button is then redundant (the user is already
   *  in SDMS), so it's hidden. Opened from the QC Batch list it stays unset → button shown. */
  fromSdms?: boolean;
  /** OG Data Package header fields (from create-or-find, PERSISTED, stable on reopen):
   *  packageCreatedBy = the QC batch's creator (OG QCBatchID.CreatedBy.DisplayName);
   *  packageCreatedDate = DataPackage.DateTimeCreated. Neither is current-user/today. */
  packageCreatedBy?: string | null;
  packageCreatedDate?: string | null;
}

// Status helpers — the list endpoint returns "Pending Validation" / "SDMS Validated"
const isValidated = (s: string) => /validated/i.test(s) && !/pending/i.test(s);
const isPending = (s: string) => /pending/i.test(s);

// ── Batch Results row shape ──────────────────────────────────────────────
//
// One row per (sample × analyte) — same shape the legacy ALPACA "Batch
// Results" tab shows.  Live data comes from
// `GET /api/v1/analysis/result-validation/{batchOid}/batch-results` which
// reads dbo.SpreadSheetEntry_SampleInfo joined to SampleParameter /
// SampleLogIn / QCType / Employee.  When the API returns 0 rows for a batch
// (e.g. the demo Alkalinity batch isn't backed by real SDMS rows in the
// current DB), we fall back to the per-test seed below so the page never
// goes blank in a demo.
// Fields ordered to match the legacy XAF view
// `SpreadSheetEntry_ListView_Copy_DataReview` indices 0-35.  Optional
// because the demo seed only populates a subset; the API returns every
// field (null for missing).
interface BatchResultRow {
  // Not shown in the grid — SpreadSheetEntry_SampleInfo.Oid, the write target
  // for save-edits (persisting inline cell edits before Validate/Approve).
  // Optional: the demo/seed fallback rows (below) have no real DB Oid.
  id?: string;
  // 0..4  identity + result
  sys: string; // 0  SysSampleCode
  param: string; // 1  Parameter (TestParameter.Parameter.ParameterName)
  numeric: string; // 2  NumericResult
  result: string; // 3  Result
  units: string; // 4  Units

  // 5..12 analysis / approval lifecycle (MM/DD/YYYY HH:mm)
  analyzedDate?: string; // 5
  analyzedBy?: string; // 6  Employee.DisplayName
  submittedDate?: string; // 7
  submittedBy?: string; // 8  Employee.DisplayName
  validatedDate?: string; // 9
  validatedBy?: string; // 10 Employee.DisplayName
  /** 10a PrepBatchID — the human PB… label(s), resolved server-side from the
   *  Oid(s) SampleParameter.PrepBatchID actually stores. */
  prepBatch?: string;
  approvedDate?: string; // 11
  approvedBy?: string; // 12 Employee.DisplayName

  // 13..15 sample collection chain
  dateCollected?: string; // 13
  collector?: string; // 14
  dateReceived?: string; // 15

  // 16..19 sample / client / project
  sampleName?: string; // 16 ClientSampleID (e.g. "EWPP Distribution III (D1)")
  siteName?: string; // 17 SampleSites.SiteName (D1 / R4 / P2 NER …)
  client?: string; // 18 Customer.CustomerName
  projectId?: string; // 19 Project.ProjectId

  // 20..31 QC + limits
  // 20 DF — resolved server-side: se.DF when captured, else the QC-sequence dilution
  // (se.Dilution), which is what the SDMS sheet shows. See BatchResultReader.
  df?: string;
  // Raw se.Dilution — the field the SDMS SHEET's DF column is bound to
  // (template M9 = FIELD("DILUTION")). Feeds the sheet's DF overlay, not the grid.
  dilution?: string;
  rptlimit?: string; // 21 RPTLIMIT (legacy column id)
  ucl?: string; // se.UCL — blank Upper Control Limit (UCLLimit column, QC tab)
  mdl?: string; // 22
  uql?: string; // 23
  mcl?: string; // 24 Testparameter.MCL
  loq?: string; // 25
  spike?: string; // 26 SpikeAmount
  dld?: string; // 27
  rpd?: string; // 28
  rpdCtrl?: string; // 29 SampleParameter.RPDHCLimit
  rec?: string; // 30 SampleParameter.Rec    (caption "%Rec")
  recCtrl?: string; // 31 se.[%RecCtrlLimit] range ("70 - 130"); sp.RecHCLimit (high) fallback

  // 32..35 qualifier / status / data-entry
  qualifier?: string; // 32
  qc?: string; // 33 QCStatus ("Pass" highlights green)
  enteredBy?: string; // 34
  enteredDate?: string; // 35

  // NEW columns (column-order rework 2026-09-02).
  // `trend` is an ICON/router column (see "Routing the Trend Icon"): it carries
  // no text — the eight routing fields below drive it. `permitLimit` (replaces
  // the removed MCL) + `alert` await their sources, blank for now.
  trend?: string;
  permitLimit?: string;
  // Permit WARNING threshold (Permitsetup.Limit3) — feeds the Site-trend Warning
  // line (client 2026-09-03). Not a displayed grid column.
  permitWarn?: string;
  alert?: string;
  // Stage 3 (Permit-To-Notification), ADDITIVE — the assigned permit's TYPE name
  // and EVERY applicable limit for this row's parameter. Populated only when the
  // job was registered with a permit (else undefined), so flagging is a no-op on
  // no-permit rows. The scalar `permitLimit` above is untouched and stays the
  // fallback + the Trend popup's source.
  permitType?: string;
  permitLimits?: PermitLimit[];

  // ── Trend routing (NOT displayed) — feed trendRouting.resolveRoutes ──
  qcTypeName?: string;
  qcRole?: string;
  qcRootRole?: string;
  qcSource?: string;
  parentSys?: string;
  testMethodOid?: string;
  parameterOid?: string;
  qcTypeOid?: string;
  isInternalStandard?: boolean;
  // Surrogate flag (SpreadSheetEntry_SampleInfo.uqTestSurrogateID = 1) — buckets a
  // row into the "Surrogate" tab. NOT a displayed grid column.
  isSurrogate?: boolean;

  // ── NOT in legacy Batch Results view (kept for the SDMS spreadsheet
  // tab which uses a different presentation) ──
  // sampleType, rl ARE NOT shown in this tab — removed.
}

/** Stable, content-based row key (matches sorting/filtering across re-renders).
 * Module-scoped so BOTH BatchResultsTable's header "select all" AND the
 * parent's save-edits handler (which needs to map an override's rowKey back
 * to the row's real `id` for the API) use the exact same formula. */
function batchResultRowKey(r: Pick<BatchResultRow, "sys" | "param" | "numeric" | "units">): string {
  const blank = (v: string | undefined | null) => (v && v !== "" ? v : "");
  return `${r.sys}|${r.param}|${blank(r.numeric)}|${blank(r.units)}`;
}

/** Project a Batch Results row onto the minimal shape the Trend router reads.
 *  The id MUST match the key the cell looks the route up by (r.id ?? row key),
 *  so the map built once per data change is read O(1) per cell. */
function toTrendRow(r: BatchResultRow): TrendRow {
  return {
    id: r.id ?? batchResultRowKey(r),
    sys: r.sys || null,
    parameter: r.param || null,
    siteName: r.siteName || null,
    // Project.ProjectId — the Site → Project fallback grouping (v10 §1b), used when
    // the row has no siteName.
    projectName: r.projectId || null,
    parameterOid: r.parameterOid || null,
    qcTypeName: r.qcTypeName || null,
    qcRole: r.qcRole || null,
    qcRootRole: r.qcRootRole || null,
    qcSource: r.qcSource || null,
    parentSys: r.parentSys || null,
    testMethodOid: r.testMethodOid || null,
    qcTypeOid: r.qcTypeOid || null,
    isInternalStandard: r.isInternalStandard ?? false,
    isSurrogate: r.isSurrogate ?? false,
    analyzedDate: r.analyzedDate ?? null,
  };
}

// ── Batch Results row category (drives the split tab bar) ────────────────────
// The legacy single "Batch Results" tab is split into four category tabs. The
// PURE bucketing decision lives in resultCategory.ts (reuses the Trend router's
// QC-type classification, standing rule 8, unit-tested). Here we keep only the
// UI-side tab metadata + the thin BatchResultRow adapter.
const CATEGORY_TABS: { key: ResultCategory; label: string }[] = [
  { key: "sample", label: "Sample Results" },
  { key: "qc", label: "QC Sample Results" },
  { key: "surr", label: "Surrogate" },
  { key: "is", label: "Internal Standard" },
];

const CATEGORY_KEYS = CATEGORY_TABS.map((t) => t.key);
function isCategoryTab(tab: string): tab is ResultCategory {
  return (CATEGORY_KEYS as string[]).includes(tab);
}

/** Which category tab a Batch Results row belongs to (delegates to the pure
 *  categoriser — a BatchResultRow structurally satisfies CategoryRow). */
function categoryOfRow(r: BatchResultRow): ResultCategory {
  return categorizeRow(r);
}

// ── Data Package result-tab column clean-up (client request 2026-09-12) ──────
// Each category tab hides the columns that are blank / irrelevant on ITS rows so
// every tab shows only what matters. The hidden set is per tab:
//   • Sample Results     — QC-metric columns (a client-sample row has no
//     recovery / RPD / QC-status of its own).
//   • QC Sample Results  — client-sample context columns (a QC sample such as a
//     CCV / blank / LCS has no client sample name, site, permit, project, client).
//   • Surrogate + Internal Standard — analyte reporting-limit / RPD / client-
//     context columns that don't apply to a recovery / response row (same set).
// Columns stay on the tabs where they carry real data. Applied to BOTH the grid
// columns and the tab-bar Export so the file and the header row never diverge.
const SURR_IS_HIDDEN_COLS: ReadonlySet<string> = new Set([
  "rptlimit", "ucl", "mdl", "loq", "uql", "dld", "rpd", "rpdCtrl",
  "permitLimit", "alert", "projectId", "client",
  // PrepBatchID was asked for on Sample Results and QC Sample Results ONLY (owner,
  // 2026-09-28) — so it is hidden here, on Surrogate and Internal Standard.
  "prepBatch",
]);
// UCLLimit (se.UCL) is the blank Upper Control Limit — it belongs on the QC Sample
// Results tab only (a client Sample / Surrogate / Internal-Standard row has no UCL),
// so it is hidden everywhere except qc.
const TAB_HIDDEN_COLS: Partial<Record<ResultCategory, ReadonlySet<string>>> = {
  sample: new Set(["dld", "rpd", "rec", "rpdCtrl", "recCtrl", "qc", "ucl"]),
  qc: new Set(["sampleName", "siteName", "permitLimit", "alert", "projectId", "client"]),
  is: SURR_IS_HIDDEN_COLS,
  surr: SURR_IS_HIDDEN_COLS,
};

/** Is this Batch Results column hidden on the given category tab? */
function isColHiddenForCategory(id: string, cat: ResultCategory): boolean {
  return TAB_HIDDEN_COLS[cat]?.has(id) ?? false;
}

/** Filter a Batch Results column-id list for a category tab (drops that tab's
 *  hidden columns; a tab with no entry keeps the full catalogue). */
export function colsForCategory(ids: string[], cat: ResultCategory): string[] {
  return ids.filter((id) => !isColHiddenForCategory(id, cat));
}

// Per-test data sources.  Returns an empty array for "no rows" so callers
// can always iterate without null-checks.
function batchResultsFor(testName: string | null): BatchResultRow[] {
  const t = (testName ?? "").toLowerCase();
  if (t.includes("alkalinity")) return ALKALINITY_RESULTS;
  return ANIONS_RESULTS;
}

// ── Backend payload shape (matches Application/IBatchResultReader.cs) ────
// Field names follow the C# record (camelCased automatically by ASP.NET).
interface ApiBatchResultRow {
  id: string;
  // 0..4
  sys: string | null;
  parameter: string | null;
  numericResult: string | null;
  result: string | null;
  units: string | null;
  // 5..12
  analyzedDate: string | null;
  analyzedBy: string | null;
  submittedDate: string | null;
  submittedBy: string | null;
  validatedDate: string | null;
  validatedBy: string | null;
  prepBatch: string | null;
  approvedDate: string | null;
  approvedBy: string | null;
  // 13..15
  dateCollected: string | null;
  collector: string | null;
  dateReceived: string | null;
  // 16..19
  sampleName: string | null;
  siteName: string | null;
  client: string | null;
  projectId: string | null;
  // 20..31  (C# PascalCase → JSON camelCase via the default ASP.NET policy)
  df: string | null;
  /** Raw se.Dilution — feeds the SDMS sheet's DF overlay, not a grid column. */
  dilution: string | null;
  rptLimit: string | null;
  ucl: string | null;
  mdl: string | null;
  uql: string | null;
  mcl: string | null;
  loq: string | null;
  spikeAmount: string | null;
  dld: string | null;
  rpd: string | null;
  rpdCtrlLimit: string | null;
  rec: string | null;
  recCtrlLimit: string | null;
  /** se.[%RecCtrlLimit] — the persisted OG-SDMS recovery control-limit RANGE
   *  string ("70 - 130", or one-sided "125"). Preferred over recCtrlLimit
   *  (sp.RecHCLimit — high only) so the grid shows the full range (2026-09-12). */
  recCtrlLimitText: string | null;
  // 32..35
  qualifier: string | null;
  qcStatus: string | null;
  enteredBy: string | null;
  enteredDate: string | null;
  // Trend routing (NOT displayed) — the eight fields the Trend icon routes on
  // (see trendRouting.ts / the "Routing the Trend Icon" guide). Read from the
  // dbo.QCType chain server-side; the grid never shows them.
  qcTypeName: string | null;
  qcRole: string | null;
  qcRootRole: string | null;
  qcSource: string | null;
  parentSys: string | null;
  testMethodOid: string | null;
  parameterOid: string | null;
  qcTypeOid: string | null;
  isInternalStandard: boolean;
  // Surrogate flag (se.uqTestSurrogateID = 1) — buckets the row into the Surrogate tab.
  isSurrogate: boolean;
  // Permit columns (client-sample rows) — MCL under the sample's permit + the
  // permit's "Alert Enabled" flag.
  permitLimit: string | null;
  // Permit WARNING threshold (Permitsetup.Limit3) — drives the Site-trend Warning line.
  permitWarn: string | null;
  alert: string | null;
  // Stage 3 (Permit-To-Notification), ADDITIVE — permit type name + a FOR JSON
  // array of every applicable limit for this row's parameter (null when no permit).
  permitType: string | null;
  permitLimitsJson: string | null;
}

/** Convert one API row to the local BatchResultRow shape used by the table. */
function apiRowToBatchRow(r: ApiBatchResultRow): BatchResultRow {
  const dt = (v: string | null) => (v ? fmtLocalDateTime(v) || "" : "");
  const date = (v: string | null) => (v ? fmtLocalDate(v) || "" : "");
  return {
    id: r.id,
    sys: r.sys ?? "",
    param: r.parameter ?? "",
    numeric: r.numericResult ?? "",
    result: r.result ?? "",
    units: r.units ?? "",
    analyzedDate: dt(r.analyzedDate),
    analyzedBy: r.analyzedBy ?? "",
    submittedDate: dt(r.submittedDate),
    submittedBy: r.submittedBy ?? "",
    validatedDate: dt(r.validatedDate),
    validatedBy: r.validatedBy ?? "",
    prepBatch: r.prepBatch ?? "",
    approvedDate: dt(r.approvedDate),
    approvedBy: r.approvedBy ?? "",
    dateCollected: date(r.dateCollected),
    collector: r.collector ?? "",
    dateReceived: dt(r.dateReceived),
    sampleName: r.sampleName ?? "",
    siteName: r.siteName ?? "",
    client: r.client ?? "",
    projectId: r.projectId ?? "",
    df: r.df ?? "",
    dilution: r.dilution ?? "",
    rptlimit: r.rptLimit ?? "",
    ucl: r.ucl ?? "",
    mdl: r.mdl ?? "",
    uql: r.uql ?? "",
    mcl: r.mcl ?? "",
    loq: r.loq ?? "",
    spike: r.spikeAmount ?? "",
    dld: r.dld ?? "",
    rpd: r.rpd ?? "",
    rpdCtrl: r.rpdCtrlLimit ?? "",
    rec: r.rec ?? "",
    // FB 2026-09-12: show the recovery control-limit RANGE, not just the high
    // bound. Prefer the persisted OG-SDMS range string se.[%RecCtrlLimit]
    // (e.g. "70 - 130", or one-sided "125"); fall back to recCtrlLimit
    // (sp.RecHCLimit — high only) when a batch never persisted it, so no row
    // regresses to blank.
    // Bin Yu 2026-09-12: a BLANK never carries a recovery control limit — it is
    // judged by its blank acceptance limit (UCL), not recovery (v10 prototype:
    // Method Blank = "Blank Result vs acceptance limit", recovery "—"). The only
    // place it still showed was the SURROGATE tab (surrogate is spiked into the
    // blank too), so blank it there. Role-driven (qcRootRole='Blank' covers
    // Method / RF / Trip Blank / CCB); isSurrogate scopes it to the Surrogate tab
    // (surrogate rows never appear on another tab), so the QC tab is untouched.
    recCtrl:
      r.isSurrogate && (r.qcRootRole ?? "").trim().toLowerCase() === "blank"
        ? ""
        : r.recCtrlLimitText && r.recCtrlLimitText.trim() !== ""
          ? r.recCtrlLimitText
          : r.recCtrlLimit ?? "",
    qualifier: r.qualifier ?? "",
    qc: r.qcStatus ?? "",
    enteredBy: r.enteredBy ?? "",
    // EnteredDate: legacy xafml has no DisplayFormat on this column →
    // date-only display (matches legacy Batch Results grid).
    enteredDate: date(r.enteredDate),
    // Trend routing (NOT displayed) — pass through so resolveRoutes can classify
    // and pair. Kept undefined (not "") when null so the router's fallback ladder
    // reads a genuine absence rather than an empty configured value.
    qcTypeName: r.qcTypeName ?? undefined,
    qcRole: r.qcRole ?? undefined,
    qcRootRole: r.qcRootRole ?? undefined,
    qcSource: r.qcSource ?? undefined,
    parentSys: r.parentSys ?? undefined,
    testMethodOid: r.testMethodOid ?? undefined,
    parameterOid: r.parameterOid ?? undefined,
    qcTypeOid: r.qcTypeOid ?? undefined,
    isInternalStandard: r.isInternalStandard ?? false,
    isSurrogate: r.isSurrogate ?? false,
    // Permit columns — MCL under the sample's permit + the permit's Alert flag.
    permitLimit: r.permitLimit ?? "",
    permitWarn: r.permitWarn ?? "",
    alert: r.alert ?? "",
    // Stage 3 (ADDITIVE) — permit type + all applicable limits (undefined when no permit).
    permitType: r.permitType ?? "",
    permitLimits: parsePermitLimits(r.permitLimitsJson),
  };
}

// ── Backend payload for Check Points (matches IChecklistReader.ChecklistRow) ─
// One row per (batch × checkpoint) — Yes/No/NA come back as bools (bit columns
// in DataPackageChecklistQC); CheckPoint is the prompt text from the master
// DataPackageCheckList lookup.  Empty batches (no DataPackage yet) return [].
interface ApiChecklistRow {
  id: string;
  checkPoint: string | null;
  yes: boolean;
  no: boolean;
  na: boolean;
  qualifier: string | null;
  comment: string | null;
  /** DataPackageCheckList.[Default] — 0 Yes · 1 No · 2 NA. Backs Calculate. */
  defaultAnswer: number | null;
}

/** Fetch live Check Points rows for one QC batch.  Same shape as the Review
 *  Log hook — returns `{ rows, loading, error, reload }`.  Callers fall back
 *  to the per-test seed when the batch has no DataPackageChecklistQC rows so
 *  demo batches without a checklist still show something on screen. */
function useCheckPoints(batchOid: string) {
  const [rows, setRows] = useState<ApiChecklistRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState<boolean>(false);
  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: ApiChecklistRow[] }>(
        `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/check-points`,
      );
      const live = res.data?.data ?? [];
      setRows(live);
      setIsLive(live.length > 0);
    } catch (e) {
      console.warn("[CheckPoints] fetch failed for", batchOid, e);
      setError(e instanceof Error ? e.message : "Failed to load check points");
      setRows([]);
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [batchOid]);
  return { rows, loading, error, isLive, reload };
}

// ── Backend payload for Attachments (matches IAttachmentReader.AttachmentRow) ─
// FileOid is FileData.Oid — the key for GET /result-validation/attachments/{fileOid}/download.
// Date arrives as ISO string; we format at the row-mapper boundary using the
// shared fmtLocalDate helper (§14b.6, standing rule 3).
interface ApiAttachmentRow {
  id: string;
  fileOid: string | null;
  name: string | null;
  category: string | null;
  /** dbo.Category.Oid — lets the dialogs preselect rather than match on name. */
  categoryOid?: string | null;
  /** dbo.Employee.Oid. */
  operatorOid?: string | null;
  fileName: string | null;
  date: string | null;
  operator: string | null;
}

/** Fetch live Attachments rows for one QC batch.  Same shape as the other
 *  detail-view hooks — returns `{ rows, loading, error, isLive, reload }`.
 *  Falls back to ATTACHMENTS_SEED when the batch has no DataPackage /
 *  attachments yet so demo batches never render an empty grid. */
function useAttachments(batchOid: string) {
  const [rows, setRows] = useState<AttachmentRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState<boolean>(false);
  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: ApiAttachmentRow[] }>(
        `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/attachments`,
      );
      const live = (res.data?.data ?? []).map<AttachmentRow>((r) => ({
        id: r.id,
        fileOid: r.fileOid ?? undefined,
        name: r.name ?? "",
        category: r.category ?? "",
        categoryOid: r.categoryOid ?? null,
        operatorOid: r.operatorOid ?? null,
        // Attachment column shows the FILE NAME (e.g. "CalibrationSingleLine
        // 2026-05-29 15-41-41.pdf") — same as the legacy grid.
        file: r.fileName ?? "",
        date: r.date ? fmtLocalDate(r.date) || "" : "",
        operator: r.operator ?? "",
      }));
      // Always show live rows — empty means empty (LIMS shows an empty grid
      // too for batches without attachments).  Falling back to Alkalinity's
      // seed here was misleading and masked earlier bugs (CheckPoint 500s
      // looked like "loaded seed"), same reason we removed the CP fallback.
      setRows(live);
      setIsLive(live.length > 0);
    } catch (e) {
      console.warn("[Attachments] fetch failed for", batchOid, e);
      setError(e instanceof Error ? e.message : "Failed to load attachments");
      setRows([]);
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [batchOid]);
  return { rows, loading, error, isLive, reload };
}

// RawData Preview datasource + lookup + download now live in the SHARED module
// features/analysis/rawDataPreview (extracted 2026-09-10 so Result Tracking reuses
// the SAME logic — standing rule 8). ApiRawDataFile / ApiRawDataInfo / useRawData /
// resolveRawDataFileOid / downloadRawDataFile are imported at the top of this file;
// behaviour is unchanged.

// ── Backend payload for Review Log (matches IReviewLogReader.ReviewLogRow) ─
interface ApiReviewLogRow {
  id: string;
  processMode: string | null;
  date: string | null;
  user: string | null; // signer CODE (Employee.DisplayName, e.g. E169217)
  operatorName: string | null; // signer real name (FirstName + LastName)
  role: string | null;
  comment: string | null;
  decisionStatus: string | null;
  reviewScope: string | null;
  status: string | null;
  signatureHash: string | null; // computed integrity digest (SHA-256 hex)
  /** True when the CURRENT user signed this row. ALPACA keeps one SDMS Validation
   *  row per validator, so the placeholder is decided on "do I have my own row",
   *  never on "does any sdms row exist". */
  isMine?: boolean;
}

/** Fetch live Review Log rows for one QC batch.  Returns { rows, loading,
 *  error, reload }.  Empty array on demo batches that have no DataPackage
 *  / DataPackageReviewLog rows — the table renders an empty-state message. */
function useReviewLog(batchOid: string) {
  const [rows, setRows] = useState<ApiReviewLogRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: ApiReviewLogRow[] }>(
        `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/review-log`,
      );
      setRows(res.data?.data ?? []);
    } catch (e) {
      console.warn("[ReviewLog] fetch failed for", batchOid, e);
      setError(e instanceof Error ? e.message : "Failed to load review log");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [batchOid]);
  // Re-fetch whenever a validate / approve / save-edits POST for THIS batch
  // completes, wherever it was fired from. The server drops its 15 s review-log
  // cache inside the same write, so this read is authoritative - the analyst
  // sees "Signed by ..." the moment the write returns, with no manual Refresh.
  useEffect(
    () =>
      onReviewLogWrite((oid) => {
        if (oid.toLowerCase() === batchOid.toLowerCase()) void reload();
      }),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [batchOid],
  );
  return { rows, loading, error, reload };
}

// The ONE legacy demo batch the seed data is canonical for — see
// batchResultsFor's own doc comment. Scoping the fallback to exactly this
// batchId (not "any batch with zero rows") matters because a zero-row API
// response is now also the CORRECT, expected result when the self-analyzed
// row filter (hideMyOwn — ALPACA parity, DataReviewController.cs :133)
// legitimately excludes every row in a batch the viewer personally analyzed.
// Falling back to seed data in that case silently replaced a real "No data
// to display" (matching legacy) with fabricated rows from an unrelated test
// — confirmed 2026-07-17 against live data (batch QB26071507_035, all 18
// rows AnalyzedBy=E169217): logged in as that same analyst, the API
// correctly returns 0 rows, but the seed fallback showed 14 fake Anions_IC
// rows instead of the empty state.
const SEED_CANONICAL_BATCH_ID = "QB26052918_035";

/** Fetch live Batch Results for one QC batch.  Returns { rows, loading,
 *  error, isLive, reload }.  Falls back to the per-test seed ONLY for the
 *  one legacy demo batch it's canonical for (see SEED_CANONICAL_BATCH_ID) —
 *  every other batch shows its REAL result set, including a genuinely empty
 *  one (e.g. the viewer's own self-analyzed rows correctly excluded). */
/** Does the Data Package review log already hold MY Peer Review row (mine, or one
 *  nobody has claimed yet)? G5 DataPackageViewController :407 — `Operator == currentUser
 *  || Operator == null`. Another analyst's row does not count, so with PLM Self Submit
 *  each analyst still gets their own Pending row to tick. */
export function hasOwnPeerReviewRow(
  rows: ReadonlyArray<{ processMode?: string | null; isMine?: boolean; user?: string | null; operatorName?: string | null }>,
): boolean {
  return rows.some((r) => (r.processMode ?? "").toLowerCase() === "peer review"
    && (!!r.isMine || (!r.user && !r.operatorName)));
}

function useBatchResults(batchOid: string, batchLabel: string | null, testName: string | null, packageScope = false) {
  const [rows, setRows] = useState<BatchResultRow[]>([]);
  // Data Package popup only (packageScope): OG Self Submit On + the user analysed none of the rows
  // -> every row is listed but cannot be selected (OG SpreadSheetEntry_SDMS_ControlsCreated_ReadOnly).
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState<boolean>(false);
  const isSeedCanonical = batchLabel === SEED_CANONICAL_BATCH_ID;

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      // packageScope = the Data Package popup: OG's SpreadSheetEntry_ListView_Copy_SDMS rule (Self Submit,
      // the user's OWN rows). Without it the Result Validation peer-review rule applies (others' rows only).
      const res = await api.get<{ success: boolean; data: ApiBatchResultRow[]; meta?: { readOnly?: boolean } }>(
        `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/batch-results`,
        packageScope ? { params: { scope: "package" } } : undefined,
      );
      setReadOnly(packageScope && res.data?.meta?.readOnly === true);
      const liveRows = (res.data?.data ?? []).map(apiRowToBatchRow);
      if (liveRows.length > 0 || !isSeedCanonical) {
        // Real result set — including a genuinely empty one, which the grid
        // renders as its own "No data to display" state (isLive stays true
        // so the UI doesn't relabel it as demo data).
        setRows(liveRows);
        setIsLive(true);
      } else {
        setRows(batchResultsFor(testName));
        setIsLive(false);
      }
    } catch (e) {
      console.warn("[BatchResults] fetch failed for", batchOid, e);
      setError(e instanceof Error ? e.message : "Failed to load batch results");
      if (isSeedCanonical) {
        setRows(batchResultsFor(testName));
        setIsLive(false);
      } else {
        setRows([]);
        setIsLive(true);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [batchOid, packageScope]);

  return { rows, loading, error, isLive, reload, readOnly };
}

// Alkalinity batch — matches the legacy ALPACA Batch Results screenshot
// (QB26052918_035 / Alkalinity-Mantech), 22 rows in run-sequence order.
// AnalyzedDate timestamps match the SDMS spreadsheet for cross-tab
// consistency.  SubmittedDate/By + ValidatedDate/By + ApprovedDate/By
// are filled in per the validation/approval pipeline.
const ALK_ANALYSIS_DEFAULTS = {
  analyzedBy: "E169217",
  submittedDate: "05/29/2026 15:42",
  submittedBy: "E169217",
  validatedDate: "06/01/2026 11:25",
  validatedBy: "E129500",
} as const;
function alk(
  row: Omit<
    BatchResultRow,
    "param" | "units" | "analyzedBy" | "submittedDate" | "submittedBy" | "validatedDate" | "validatedBy"
  > &
    Partial<Pick<BatchResultRow, "param" | "units">>,
): BatchResultRow {
  return {
    param: "Alkalinity",
    units: "mg/L",
    ...ALK_ANALYSIS_DEFAULTS,
    ...row,
  };
}
const ALKALINITY_RESULTS: BatchResultRow[] = [
  alk({
    sys: "Method Blank1",
    numeric: "1.48",
    result: "<20",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    analyzedDate: "05/29/2026 10:38",
  }),
  alk({
    sys: "CCV Low1",
    numeric: "52.70",
    result: "53",
    df: "1",
    rptlimit: "20",
    spike: "50",
    mdl: "2.14",
    recCtrl: "90 - 110",
    qc: "Pass",
    analyzedDate: "05/29/2026 10:45",
  }),
  alk({
    sys: "CCV High1",
    numeric: "100.30",
    result: "100",
    df: "1",
    rptlimit: "20",
    spike: "100",
    mdl: "2.14",
    recCtrl: "90 - 110",
    qc: "Pass",
    analyzedDate: "05/29/2026 10:54",
  }),
  alk({
    sys: "LCS1",
    numeric: "53.10",
    result: "53",
    df: "1",
    rptlimit: "20",
    spike: "50",
    mdl: "2.14",
    recCtrl: "80 - 120",
    qc: "Pass",
    analyzedDate: "05/29/2026 11:05",
  }),
  alk({
    sys: "260529002-002",
    siteName: "D1",
    sampleName: "EWPP Distribution III (D1)",
    numeric: "86.70",
    result: "87",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    collector: "Jason Pisio",
    dateReceived: "05/29/2026 09:54",
    analyzedDate: "05/29/2026 11:14",
  }),
  alk({
    sys: "260529002-002Dup1",
    siteName: "D1",
    sampleName: "EWPP Distribution III (D1)",
    numeric: "85.70",
    result: "86",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    qc: "Pass",
    recCtrl: "20",
    dateCollected: "05/29/2026",
    collector: "Jason Pisio",
    dateReceived: "05/29/2026 09:54",
    analyzedDate: "05/29/2026 11:24",
  }),
  alk({
    sys: "260529002-002MS1",
    siteName: "D1",
    sampleName: "EWPP Distribution III (D1)",
    numeric: "132.10",
    result: "132",
    df: "1",
    rptlimit: "20",
    spike: "50",
    mdl: "2.14",
    uql: "2000",
    qc: "Pass",
    recCtrl: "80 - 120",
    dateCollected: "05/29/2026",
    collector: "Jason Pisio",
    dateReceived: "05/29/2026 09:54",
    analyzedDate: "05/29/2026 11:35",
  }),
  alk({
    sys: "260529001-001",
    siteName: "R4",
    sampleName: "",
    numeric: "84.30",
    result: "84",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 11:49",
  }),
  alk({
    sys: "260529001-002",
    siteName: "D4",
    sampleName: "",
    numeric: "87.90",
    result: "88",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 11:59",
  }),
  alk({
    sys: "260529002-001",
    siteName: "R1",
    sampleName: "",
    numeric: "85.20",
    result: "85",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 12:09",
  }),
  alk({
    sys: "260529002-003",
    siteName: "R3",
    sampleName: "",
    numeric: "85.50",
    result: "86",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 12:20",
  }),
  alk({
    sys: "260529002-004",
    siteName: "D3",
    sampleName: "",
    numeric: "88.30",
    result: "88",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 12:31",
  }),
  alk({
    sys: "260529003-001",
    siteName: "P2 NER",
    sampleName: "",
    numeric: "62.30",
    result: "62",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 12:41",
  }),
  alk({
    sys: "260529003-002",
    siteName: "P2Ph1NED1",
    sampleName: "",
    numeric: "73.70",
    result: "74",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 12:50",
  }),
  alk({
    sys: "260529003-006",
    siteName: "P2Ph2CS3",
    sampleName: "",
    numeric: "53.30",
    result: "53",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 13:21",
  }),
  alk({
    sys: "260529003-005",
    siteName: "P2Ph1CS2",
    sampleName: "",
    numeric: "53.90",
    result: "54",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 13:30",
  }),
  alk({
    sys: "CCV Low2",
    numeric: "51.30",
    result: "51",
    df: "1",
    rptlimit: "20",
    spike: "50",
    mdl: "2.14",
    recCtrl: "90 - 110",
    qc: "Pass",
    analyzedDate: "05/29/2026 13:40",
  }),
  alk({
    sys: "CCB1",
    numeric: "1.15",
    result: "<20",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    qc: "Pass",
    analyzedDate: "05/29/2026 13:55",
  }),
  alk({
    sys: "260529003-009",
    siteName: "P2Ph1Rmx2",
    sampleName: "",
    numeric: "56.10",
    result: "56",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 14:01",
  }),
  alk({
    sys: "260529003-010",
    siteName: "P2Ph2Rmx3",
    sampleName: "",
    numeric: "55.70",
    result: "56",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    uql: "2000",
    dateCollected: "05/29/2026",
    analyzedDate: "05/29/2026 14:10",
  }),
  alk({
    sys: "CCV Low3",
    numeric: "52.10",
    result: "52",
    df: "1",
    rptlimit: "20",
    spike: "50",
    mdl: "2.14",
    recCtrl: "90 - 110",
    qc: "Pass",
    analyzedDate: "05/29/2026 14:20",
  }),
  alk({
    sys: "CCB2",
    numeric: "0.39",
    result: "<20",
    df: "1",
    rptlimit: "20",
    mdl: "2.14",
    qc: "Pass",
    analyzedDate: "05/29/2026 14:29",
  }),
];

// Anions/IC seed — the prototype's original Sheet1.  Used as the fallback
// for any test that isn't Alkalinity (until we ship more presets).
const ANIONS_ANALYSIS = {
  analyzedDate: "06/22/2026 12:50",
  analyzedBy: "E176387",
  submittedDate: "06/23/2026 16:51",
  submittedBy: "E176387",
} as const;
function ic(
  row: Omit<BatchResultRow, "units" | "analyzedDate" | "analyzedBy" | "submittedDate" | "submittedBy"> &
    Partial<Pick<BatchResultRow, "units">>,
): BatchResultRow {
  return { units: "mg/L", ...ANIONS_ANALYSIS, ...row };
}
const ANIONS_RESULTS: BatchResultRow[] = [
  ic({
    sys: "CCB1",
    param: "Chloride",
    numeric: "n.a.",
    result: "<1",
    df: "1",
    rptlimit: "1.0",
    mdl: "0.176",
    uql: "100",
    qc: "Pass",
  }),
  ic({
    sys: "CCB1",
    param: "Nitrite as Nitrogen",
    numeric: "n.a.",
    result: "<0.04",
    df: "1",
    rptlimit: "0.0",
    mdl: "0.003",
    uql: "5",
    qc: "Pass",
  }),
  ic({
    sys: "CCB1",
    param: "Bromide",
    numeric: "n.a.",
    result: "<0.02",
    df: "1",
    rptlimit: "0.0",
    mdl: "0.005",
    uql: "5",
    qc: "Pass",
  }),
  ic({
    sys: "CCB1",
    param: "Nitrate as Nitrogen",
    numeric: "n.a.",
    result: "<0.04",
    df: "1",
    rptlimit: "0.0",
    mdl: "0.007",
    uql: "5",
    qc: "Pass",
  }),
  ic({
    sys: "CCB1",
    param: "Sulfate",
    numeric: "n.a.",
    result: "<1",
    df: "1",
    rptlimit: "1.0",
    mdl: "0.337",
    uql: "5",
    qc: "Pass",
  }),
  ic({
    sys: "CCB1",
    param: "O-phosphate as Phosphorus",
    numeric: "n.a.",
    result: "<0.1",
    df: "1",
    rptlimit: "0.1",
    mdl: "0.019",
    uql: "5",
    qc: "Pass",
  }),
  ic({
    sys: "LOQ1",
    param: "Chloride",
    numeric: "0.9797",
    result: "0.9797",
    df: "1",
    mdl: "0.176",
    uql: "100",
    loq: "1",
    spike: "1",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LOQ1",
    param: "Nitrite as Nitrogen",
    numeric: "0.0386",
    result: "0.0386",
    df: "1",
    mdl: "0.003",
    uql: "5",
    spike: "0.04",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LOQ1",
    param: "Bromide",
    numeric: "0.0206",
    result: "0.02",
    df: "1",
    mdl: "0.005",
    uql: "5",
    spike: "0.02",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LOQ1",
    param: "Nitrate as Nitrogen",
    numeric: "0.0441",
    result: "0.0441",
    df: "1",
    mdl: "0.007",
    uql: "5",
    spike: "0.04",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LOQ1",
    param: "Sulfate",
    numeric: "0.7615",
    result: "1",
    df: "1",
    mdl: "0.337",
    uql: "5",
    spike: "1",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LOQ1",
    param: "O-phosphate as Phosphorus",
    numeric: "0.1076",
    result: "0.11",
    df: "1",
    mdl: "0.019",
    uql: "5",
    spike: "0.1",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LCS1",
    param: "Chloride",
    numeric: "50.2681",
    result: "50.2681",
    df: "1",
    mdl: "0.176",
    uql: "100",
    spike: "50",
    qc: "Pass",
    recCtrl: "125",
  }),
  ic({
    sys: "LCS1",
    param: "Nitrite as Nitrogen",
    numeric: "2.4222",
    result: "2.4222",
    df: "1",
    mdl: "0.003",
    uql: "5",
    spike: "2.5",
    qc: "Pass",
    recCtrl: "125",
  }),
];

// ── Check Points row shape ───────────────────────────────────────────────
// One row on the Check Points tab.  The API returns per-column bits
// (yes/no/na); we collapse them here to a single 'decision' + optional
// qualifier / comment for the renderer.
type CheckDecision = "Yes" | "No" | "NA" | "";
interface CheckPointRow {
  // Not shown in the grid — DataPackageChecklistQC.Oid, the write target for
  // save-edits (persisting inline cell edits before Validate/Approve).
  id: string;
  label: string;
  decision: CheckDecision;
  qualifier?: string;
  comment?: string;
  /** DataPackageCheckList.[Default] — 0 Yes · 1 No · 2 NA. Backs Calculate. */
  defaultAnswer?: number | null;
}

// One row on the Attachments tab (legacy DataPackageAttachments_ListView
// columns: Name · Category · Attachment · Date · Operator).  `fileOid` is
// FileData.Oid — required for the click-to-download flow; without it the
// row renders as inert text (never happens in the live path).
interface AttachmentRow {
  id?: string;          // DataPackageAttachments.Oid — the key for delete
  fileOid?: string;
  name: string;
  category: string;
  /** dbo.Category.Oid / dbo.Employee.Oid — the FKs behind the two display names,
   *  so the Edit dialog preselects by KEY rather than by label (two categories
   *  could share a name, and a name match would silently pick the wrong one). */
  categoryOid?: string | null;
  operatorOid?: string | null;
  file: string;
  date?: string;
  operator?: string;
}

// ── helpers ──────────────────────────────────────────────────────────────
const escTxt = (v: string | number | null | undefined) => (v === null || v === undefined ? "" : String(v));

function statusPill(text: string) {
  return <span className={`dv-pill ${isValidated(text) ? "dv-valid" : "dv-pending"}`}>{text}</span>;
}

// ── main component ───────────────────────────────────────────────────────
export function SdmsPackageDetail({
  batch: batchProp,
  approval = false,
  onClose,
  onRefresh,
  onValidated,
  onSubmitted,
  onSubmitSuccess,
  allBatches,
  canWrite = true,
  windowed = false,
  packageStatus = null,
  fromSdms = false,
  packageCreatedBy = null,
  packageCreatedDate = null,
  plmBatchOid = null,
  onOpenPlm,
  canSeeForm = () => true,
}: SdmsPackageDetailProps) {
  const isPlm = !!plmBatchOid;
  // Local optimistic overlay of the batch — after a successful Validate we
  // update this in place so Status, ValidatedByList, ValidatedDateMax all
  // reflect the new SDMS Validated state without waiting for the list to
  // refetch.  Parent stays authoritative; the local copy re-syncs whenever
  // the prop identity changes.
  const [batch, setBatch] = useState<SdmsPackageRow>(batchProp);
  useEffect(() => {
    setBatch(batchProp);
  }, [batchProp]);

  // Focus the TOP main control (QC Batch ID) when the detail opens, so keyboard nav
  // starts at the top of the form and NEVER on a secondary / bottom grid (Batch Results,
  // Review Log). The Batch Information fields are read-only display divs, so the first one
  // is made programmatically focusable (tabIndex=-1, topFieldRef) and focused here.
  // Guarded: never steal focus from a control the user is already in. Re-runs when a new
  // batch opens (prev/next). (user request 2026-09-22 — "if the grid is not the main / is
  // at the bottom, set focus on the top main control like QC Batch ID".)
  const topFieldRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const ae = document.activeElement as HTMLElement | null;
    const inField = !!ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable);
    if (inField) return;
    const body = topFieldRef.current?.closest(".dv-body") ?? null;
    if (ae && body && body.contains(ae)) return;   // already inside this detail
    const raf = requestAnimationFrame(() => topFieldRef.current?.focus());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch.id]);

  const validated = isValidated(batch.statusText);
  const pending = isPending(batch.statusText);

  // Top tab group (Batch Results / Check Points / Trend) + bottom tab group
  // (Review Log / Attachments)
  // The legacy single "results" tab is now four category tabs (sample · qc · surr
  // · is). "sample" is the default; an effect below re-points it to the first
  // NON-EMPTY category once the batch's rows load (a batch with no field samples
  // opens on QC, etc.).
  const [topTab, setTopTab] = useState<ResultCategory | "checks" | "trend" | "plm">(
    plmBatchOid ? "plm" : "sample");

  // PLM Results (legacy PLMStereoscopicObservation_ListView_PLMDPPreview). Only
  // fetched for a PLM batch, so an SDMS package makes no extra request.
  const [plmRows, setPlmRows] = useState<PlmDataPackageResultRow[]>([]);
  const [plmLoading, setPlmLoading] = useState(false);
  // The SUBMIT popup (a package still Pending Submission) reads the PLM Results with G5's
  // Self Submit rule; validation keeps the review read (No-Self-Review). 2026-10-16.
  const plmSubmitScope = !approval && (packageStatus == null || packageStatus <= 1);
  const reloadPlm = useCallback(async () => {
    if (!plmBatchOid) return;
    setPlmLoading(true);
    try {
      const rows = await fetchPlmDataPackageResults(plmBatchOid, plmSubmitScope);
      setPlmRows(rows);
      // Submit popup: G5 pre-ticks EVERY submitted row, whoever submitted it
      // (DataPackageViewController.GetSubmittedSamplesOid :1229-1233). The ticks ARE the
      // submission — an own submitted row the user unticks is un-submitted at Submit, and a
      // batch whose layers are all still submitted after a Reject / Rollback advances on the
      // next Submit (owner 2026-10-17, G5 parity).
      if (plmSubmitScope) {
        const submitted = rows.filter((r) => !!r.submittedBy || !!r.submittedDate);
        if (submitted.length > 0) {
          setBrChecked((prev) => {
            const next = new Map(prev);
            for (const r of submitted) if (!next.has(r.sequenceOid)) next.set(r.sequenceOid, nowClientWire());
            return next;
          });
        }
      }
    }
    finally { setPlmLoading(false); }
  }, [plmBatchOid, plmSubmitScope]);
  /** Why a PLM row cannot be ticked — G5's one message for its disabled rows (another
   *  user's submission or, with Self Submit On, another analyst's layer — submit popup;
   *  own work under No Self-Review — review). Owner 2026-10-17, G5 parity. */
  const plmLockedMsg = "You do not have permission to review results performed by other analysts.";
  useEffect(() => { void reloadPlm(); }, [reloadPlm]);
  // Row selection for the PLM Results grid — legacy's view declares
  // ShowSelectionColumn="True" (PLMStereoscopicObservation_ListView_PLMDPPreview).
  //
  // These ticks go into brChecked, the BATCH-RESULTS selection, because on a PLM
  // package this grid IS the batch-results grid: the SDMS category grids it
  // replaces have no rows at all. An earlier version kept the selection in its own
  // local set on the theory that legacy's grid is read-only so a tick should carry
  // no side effect. That was wrong, and it dead-ended the workflow: ticking a
  // Review Log row requires at least one sample selected (ALPACA
  // Grid_SelectionChanged), so with brChecked permanently empty the analyst got
  // "Select atleast one sample." no matter how many PLM rows they ticked, and
  // could never sign off (user report 2026-09-24). Writing to brChecked makes
  // every existing gate — mark.br, the Review Log stamp, the ReviewScope
  // All/Selected Results rule — work here unchanged.

  const [loTab, setLoTab] = useState<"review" | "attach">("review");
  // Full-screen grid (tab-bar toggle): the top grid card takes the whole content
  // area — Batch Information, the instruction strip and the Review Log card are
  // hidden so a wide grid can be read without horizontal squinting. Purely a
  // VIEW state (nothing is saved or sent), so leaving it is always safe.
  const [fullGrid, setFullGrid] = useState<null | "top" | "lo">(null);
  // Mark-as-reviewed state for the workflow stepper — the row keys CURRENTLY
  // ticked in each grid (Batch Results / Check Points / Review Log), mapped to
  // the ISO instant each was ticked (frozen at click time — ALPACA parity:
  // DataReviewController.cs's ClientSideEvents.SelectionChanged stamps `today`/
  // current user into the grid cell the moment a checkbox is ticked, live in
  // the browser via batchEditApi.SetCellValue, well before the real Submit
  // persists it server-side in DataReviewBatchReviewAction_Execute). Live-
  // derived, not a one-way latch: unchecking every row in a grid drops that
  // step back to not-reviewed, matching legacy's live `SelectedObjects.Count >
  // 0` gate — a row that was checked then unchecked no longer counts as
  // reviewed. (Fixed 2026-07-17: the previous one-way `mark.br/cp/rl` boolean
  // never reverted to false once set, so unchecking every row still let
  // Validate/Approve through.)
  const [brChecked, setBrChecked] = useState<Map<string, string>>(new Map());
  const [cpChecked, setCpChecked] = useState<Map<string, string>>(new Map());
  const [rlChecked, setRlChecked] = useState<Map<string, string>>(new Map());
  const mark = {
    br: brChecked.size > 0,
    cp: cpChecked.size > 0,
    rl: rlChecked.size > 0,
  };
  const toggleFactory = (setter: React.Dispatch<React.SetStateAction<Map<string, string>>>) => ({
    onToggleRow: (key: string, checked: boolean) =>
      setter((prev) => {
        const next = new Map(prev);
        // toWire (not toISOString) — while ADR-0008's UTC kill-switch is on,
        // fmtLocalDateTime reads this value LITERALLY with no shift, so it must
        // already be a naive LOCAL wall-clock string, not a UTC-labelled one
        // (toISOString's 'Z' digits would display as if they were local,
        // showing a time off by the browser's UTC offset — confirmed bug,
        // fixed 2026-07-17). nowClientWire() (not toWire(new Date())) — this is
        // a "preview what the server will stamp" scenario, so it must reflect
        // the LAB's configured timezone, not the viewer's own browser/OS zone
        // (toWire(new Date()) reads the Date's own local getters, correct only
        // for a genuine user date-picker pick — confirmed bug, fixed 2026-07-17:
        // an IST browser previewed a stamp ~10.5h off from the lab's actual time).
        if (checked) next.set(key, nowClientWire());
        else next.delete(key);
        return next;
      }),
    // Select-all stamps every row with the SAME instant (one `today` captured
    // once before the sweep) — matches the legacy JS loop exactly.
    onToggleAll: (keys: string[], checked: boolean) =>
      setter((prev) => {
        const next = new Map(prev);
        const now = nowClientWire();
        keys.forEach((k) => {
          if (checked) next.set(k, now);
          else next.delete(k);
        });
        return next;
      }),
  });
  const brToggle = toggleFactory(setBrChecked);
  const cpToggle = toggleFactory(setCpChecked);
  const rlToggle = toggleFactory(setRlChecked);
  // Current user's display name — used ONLY for the client-side ValidatedBy /
  // ApprovedBy live preview (ALPACA parity, s.cpusername in the legacy JS).
  const currentUserName = useAuthStore((s) => s.user?.name) ?? "";
  // Current user's primary role name for the client-side Role preview on a Review
  // Log tick — the first of the login RoleNames CSV. That CSV is alphabetical
  // (AI_Auth_UserNavPermissions_SP … ORDER BY RoleName), the SAME order the
  // ReviewLogReader's M:N fallback picks, so the preview matches the saved value.
  const currentUserRole = useAuthStore((s) => (s.permissions?.roles ?? "").split(",")[0]?.trim() ?? "");
  // ApprovedDate / ApprovedBy column visibility (client feedback 2026-08-01):
  // the Batch Results grid shows the Approved* pair ONLY when the SDMS approval
  // stage is in use — Default Settings › "SDMS Result Approval" (item 7,
  // dataReviewSettings.sdmsResultApproval), inlined into the login/permissions
  // payload as permissions.dataReviewNav.sdmsResultApproval (no extra fetch).
  // On the SDMS Result APPROVAL form (approval=true) the Approved* pair is the
  // point of the screen, so it always shows regardless of the toggle.
  const sdmsApprovalEnabled = useAuthStore(
    (s) => s.permissions?.dataReviewNav?.sdmsResultApproval === true,
  );
  const showApprovedCols = showApprovedColumns(approval, sdmsApprovalEnabled);
  // Default Settings › "Multi-Tab" (permissions.dataReviewNav.multiTab). Yes → the
  // results pane splits into the four category tabs (Sample Results / QC Sample
  // Results / Surrogate / Internal Standard). No (default) → the single legacy
  // "Batch Results" tab: ALL rows in one grid, with the old column order (see
  // legacyBatchResultColumnCatalog). Rides the login payload, so it takes effect
  // at login — the same as the other Data Review toggles.
  const multiTab = useAuthStore((s) => s.permissions?.dataReviewNav?.multiTab === true);
  // The detail renders INSIDE the shell's content area (sidebar + top bar + top
  // stepper all stay put — only the centre changes).  Its workflow stepper is
  // portalled into the shared top slot AppShell actually renders
  // (#ai-topbar-stepper — the slot was renamed in the 2026-07-07 merge; the old
  // #ai-topbar-island-slot no longer exists, so the detail stepper never showed
  // and the list's static placeholder stepper was left frozen). Driven by this
  // form's step progress while a package is open. (Fix 2026-07-07.)
  const [bannerSlot, setBannerSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setBannerSlot(document.getElementById("ai-topbar-stepper"));
  }, []);
  // Check Points Qualifier picker (ALPACA Qualifiers_ListView_DataPackage).
  //   qualOverrides — per-checkpoint (keyed by row index) chosen Symbols, comma-
  //     joined; the analyst's local edits (persist-on-Validate is phase 2).
  //   qualPicker   — the open picker's target row + current value, or null.
  const [qualOverrides, setQualOverrides] = useState<Record<string, string>>({});
  const [qualPicker, setQualPicker] = useState<{ rowKey: string; value: string } | null>(null);
  // Editable Check Points Comment (ALPACA: DataPackageChecklistQC.Comment is a
  // free-text field).  Keyed by checkpoint label; local edits until Validate
  // persists them (phase 2, alongside the qualifier).
  const [commentOverrides, setCommentOverrides] = useState<Record<string, string>>({});
  // Editable Check Points decision (Yes / No / NA are editable checkboxes in
  // ALPACA — the analyst can change a row's answer).  Keyed by checkpoint label;
  // value is the chosen decision ('Yes' | 'No' | 'NA' | '') overriding the row.
  const [decisionOverrides, setDecisionOverrides] = useState<Record<string, string>>({});
  // Editable Batch Results cells (ALPACA DataReview grid — only RPTLIMIT / MCL /
  // SpikeAmount / DLD / QCStatus are editable, all as plain textboxes; every
  // other column is AllowEdit=False).  brOverrides[rowKey][col] = local edit.
  const [brOverrides, setBrOverrides] = useState<Record<string, Record<string, string>>>({});
  // Editable Review Log cells (ALPACA DataPackage_ReviewLog_ListView_DataReview):
  // only Comment (textbox), DecisionStatus (Accept/Reject dropdown) and
  // ReviewScope (All Results/Selected Results dropdown) are editable; ProcessMode
  // / DateTime / User / Role are AllowEdit=False.  Keyed by review-log row id.
  const [rlOverrides, setRlOverrides] = useState<Record<string, Record<string, string>>>({});
  // Validate flow — no confirmation modal (matches ALPACA).  On click the
  // POST fires immediately once every gate passes; on success we render an
  // ALPACA-style top-of-page green banner ("Samples validated successfully.").
  // The detail overlay STAYS open — the analyst sees the batch's Status
  // flip to SDMS Validated in-place, and the new SDMS Validation row appear
  // in the Review Log — same in-place transition legacy XAF does.
  const [validateBusy, setValidateBusy] = useState(false);
  // Toolbar button refs so the shared hotkeys (Alt+V/Alt+A, Alt+R) can trigger the
  // exact same click path as the buttons (standing rule 14; see the tbKeys block).
  const validateBtnRef = useRef<HTMLButtonElement>(null);
  const refreshBtnRef = useRef<HTMLButtonElement>(null);
  // §13 Notifications (standing rule 7): the post-validate/approve success now
  // goes through the shared global service (notify.success — Tier-2 top banner,
  // "Samples validated/approved successfully."), NOT a per-form banner. The old
  // validateSuccessAt state + timer + JSX banner were removed.
  // ALPACA-parity nag banner text.  Legacy XAF runs its ViewValidation only
  // when the analyst actually clicks Validate — the blue banner is a response
  // to that click, not a proactive state readout.  We match: the banner text
  // lives here in state and is set by the Validate click handler when a
  // Gate / error messages go through the shared top-notification service
  // (useNotify — top-centre, TIMED, semantic colour): matches ALPACA's
  // ShowMessage(Info, Top, timed) and CLAUDE.md §18.6 (never a per-form banner).
  const notify = useNotify();
  // Shared confirm dialog (§18.6) — used for the pre-Validate "some result
  // categories have no reviewed rows, continue?" soft guard (2026-09-07).
  const { confirm } = useConfirm();
  // DataPackage workflow status (windowed popup only) — drives Submit (1 PendingSubmission)
  // vs the DataPackage Validate (2 PendingValidation), updated locally after a transition so
  // the button flips without a reopen.
  const [dpStatus, setDpStatus] = useState<number | null | undefined>(packageStatus);
  useEffect(() => { setDpStatus(packageStatus); }, [packageStatus]);
  // After an in-popup Submit, the parent's `batch` prop (SdmsResultEntryPage.pkgRow) is NOT
  // reloaded and hardcodes submittedByList/Date null + a stale statusText, so the header would
  // keep showing "Pending Submission" + "—". Capture the submit locally (current user + now) so
  // the Batch Information header reflects the submit the moment it lands, without a reopen.
  const [submittedLocal, setSubmittedLocal] = useState<{ by: string; date: string } | null>(null);
  // DataPackage SUBMIT popup: while PendingSubmission (windowed, dpStatus ≤ 1) the ticked rows
  // preview SubmittedBy/Date (OG SelectionChanged). Once submitted (dpStatus ≥ 2) the grid locks
  // read-only (OG makes it read-only from PendingValidation on).
  const submitMode = windowed && (dpStatus == null || dpStatus <= 1);
  const dpLocked = windowed && (dpStatus ?? 0) >= 2;
  // Screen caption: the WINDOWED popup (opened from a QC batch / the SDMS sheet) is the
  // "Data Package" view; the full-content list detail keeps "SDMS Result Validation /
  // Approval". (user UI feedback 2026-09-16.)
  const captionBase = windowed ? "Data Package" : `SDMS Result ${approval ? "Approval" : "Validation"}`;
  // Attachments add/delete is allowed only while the package is editable — OG makes
  // the DataPackageAttachments grid read-only from PendingValidation(2) onward
  // (DataPackageViewController OnActivated/AddSubmit), so gate on dpStatus <= 1 (a
  // null status is a fresh find-or-create ⇒ PendingSubmission) AND the caller's canWrite.
  const [atBusy, setAtBusy] = useState<boolean>(false);
  const [dpBusy, setDpBusy] = useState(false);
  // One Submit at a time: set synchronously on the first click, so a double-click (or Alt+U twice) cannot
  // start a second run while the first is still in its gates / confirm, before dpBusy has re-rendered.
  const dpRunningRef = useRef(false);
  // Project Notes popup state.  The count comes from `useNotesCount`, which
  // triggers a fetch on first read and re-renders whenever the cache mutates,
  // so the toolbar badge always matches the popup.
  const [notesOpen, setNotesOpen] = useState(false);
  // Keyed by the batch Oid (the notes route key), matching <NotesListPopup batchId>. (Merged 2026-07-07.)
  // notesNonce forces THIS badge to refetch after a server-side note write
  // (Submit/Validate persists a Check Point comment → the backend sync SP writes
  // 'Data Package Check Point' notes) — a direct, bump-independent refresh, in
  // addition to refreshProjectNotes() which syncs the popup + other badges. (2026-10-08)
  const [notesNonce, setNotesNonce] = useState(0);
  const notesCount = useNotesCount(batch.id ?? "", undefined, undefined, notesNonce);

  // Audit Trail popup state — uses the shared <AuditTrailPopup> scoped to
  // this batch's Oid (per-batch audit log).  Hidden when Settings → Audit
  // Trail Configuration has audit OFF for "SDMS Result Validation"
  // (standing rule 11).
  const [auditOpen, setAuditOpen] = useState(false);
  useAuditTrailConfig();
  const auditAllowed = isAuditEnabled(approval ? "Result Approval" : "SDMS Result Validation");

  // SDMS spreadsheet popup state.
  const [sdmsOpen, setSdmsOpen] = useState(false);
  // QC Batch detail modal popup state (QC Batches toolbar button).
  const [qcBatchOpen, setQcBatchOpen] = useState(false);

  // Reset transient state whenever a different package is opened.
  useEffect(() => {
    // Default to the first category tab; the "first non-empty category" effect
    // (below the results pipeline) re-points it once this batch's rows arrive.
    // A PLM package has no category tabs — it opens on its own PLM Results tab.
    setTopTab(plmBatchOid ? "plm" : "sample");
    setLoTab("review");
    setBrChecked(new Map());
    setCpChecked(new Map());
    setRlChecked(new Map());
    setValidateBusy(false);
    setNotesOpen(false);
    setAuditOpen(false);
    setSdmsOpen(false);
    setQcBatchOpen(false);
  }, [batch.id]);

  // Esc steps back ONE level: out of the full-screen grid first (if it's on),
  // otherwise out of the package. Never both on a single press — Esc in
  // full screen must not also close the batch the analyst is reviewing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (fullGrid) setFullGrid(null);
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, fullGrid]);

  // Per-row search inside each tab's grid (matches prototype's `gridSearch`).
  const [resultsQ, setResultsQ] = useState("");
  const [checksQ, setChecksQ] = useState("");
  // Excel-style per-column filter state — one Record per grid, keyed by the
  // column id used in the shared ColFilterButton (matches CLAUDE.md §18.6 and
  // legacy XAF's per-header funnel).  `null` (or missing) ⇒ column is inactive.
  const [cpFilters, setCpFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [rlFilters, setRlFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [brFilters, setBrFilters] = useState<Record<string, ColumnFilterValue>>({});
  const [atFilters, setAtFilters] = useState<Record<string, ColumnFilterValue>>({});
  // Click-to-sort state per grid — default sort mirrors legacy LIMS xafml:
  //   • Check Points: CheckPoint ASC   (Model.xafml SortIndex=0 on ChecklistItem.CheckPoint)
  //   • Review Log:   DateTime   DESC  (Model.xafml SortIndex=0, SortOrder=Descending)
  //   • Batch Results: unsorted (server sends xafml order — see BatchResultReader).
  const cpSorter = useSort<CheckPointRow>("checkPoint", "asc");
  const rlSorter = useSort<ApiReviewLogRow>("date", "desc");
  const brSorter = useSort<BatchResultRow>(null, null);
  const atSorter = useSort<AttachmentRow>(null, null);
  // Live Batch Results from the backend (dbo.SpreadSheetEntry_SampleInfo via
  // /api/v1/analysis/result-validation/{batchOid}/batch-results).  Falls back
  // to per-test seed when the batch has no SDMS rows yet.
  const {
    rows: batchRows,
    loading: brLoading,
    error: brError,
    reload: brReload,
    readOnly: brReadOnly,
  } = useBatchResults(batch.id, batch.batchId, batch.test, windowed && !isPlm);
  // The batch's OWN distinct parameter name(s) — scopes the Trend tab's analyte-mode
  // "Parameter" dropdown so it lists only this batch's parameter(s), never the
  // sampling sites' OTHER tests (client 2026-08-06). Empty until batchRows load ⇒
  // the dropdown falls back to every analyte with trend history.
  const batchParameters = useMemo(
    () => Array.from(new Set(batchRows.map((r) => (r.param ?? "").trim()).filter(Boolean))),
    [batchRows],
  );
  // EnteredBy dropdown roster — the full Employee list (ALPACA's Employee lookup
  // on QCResults.EnteredBy), fetched once.  BatchResultsTable falls back to the
  // batch's own analyst codes if this hasn't loaded / is empty.
  const [enteredByOptions, setEnteredByOptions] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ success: boolean; data: string[] }>("/api/v1/analysis/result-validation/entered-by-options")
      .then((res) => {
        if (!cancelled) setEnteredByOptions(res.data?.data ?? []);
      })
      .catch(() => {
        /* keep the in-batch fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Live Review Log rows from the backend (dbo.DataPackageReviewLog via
  // /api/v1/analysis/result-validation/{batchOid}/review-log).
  const { rows: rlRowsRaw, loading: rlLoading, error: rlError, reload: rlReload } = useReviewLog(batch.id);
  // ALPACA-parity: surface an "SDMS Validation" row in the Review Log even
  // when the backend hasn't written one yet, so the analyst always sees the
  // row for their decision — matches the XPO-managed placeholder ALPACA XAF
  // shows in every SDMS Result Validation package.  Two synthetic modes:
  //   • Pending — empty placeholder; user/decisionStatus null so the blue
  //     "Select the review log to Submit." banner keeps nagging.
  //   • Validated — populated with the batch's own ValidatedByList /
  //     ValidatedDateMax (which the read SP already aggregates from the
  //     per-sample stamps our Phase-2 write path just landed), so the row
  //     matches ALPACA's post-validate render even though we don't yet
  //     INSERT into dbo.DataPackageReviewLog directly (Phase-2b).
  // Dropped only when a REAL DataPackageReviewLog row with
  // ProcessMode='SDMS Validation' comes back from the API.
  const rlRows = useMemo<ApiReviewLogRow[]>(() => {
    // DataPackage popup (windowed): the review log follows the DATAPACKAGE workflow, not the
    // SDMS-results validation placeholder below. OG (DataPackageViewController :404-415) shows a
    // "Peer Review / Pending" row on first open of a PendingSubmission package when none exists
    // yet — synthesize it here for display (persisted later by the peer-review workflow).
    if (windowed) {
      // MY row only — G5 matches `Operator == currentUser || Operator == null` (:407). With PLM
      // Self Submit several analysts submit the same package, so the first one's Approved row
      // must not hide the next analyst's Pending row (user report 2026-10-16: nothing to tick).
      const hasPeer = hasOwnPeerReviewRow(rlRowsRaw);
      if (dpStatus === 1 && !hasPeer) {
        return [
          {
            id: `__peer-review-${batch.id}`,
            processMode: "Peer Review",
            date: null, user: null, operatorName: null, role: null,
            comment: null, decisionStatus: null, reviewScope: null,
            status: "Pending", signatureHash: null, isMine: true,
          },
          ...rlRowsRaw,
        ];
      }
      return rlRowsRaw;
    }
    // PER VALIDATOR, not per package. ALPACA creates a Pending SDMS Validation row
    // for whoever OPENS the package (DataReviewController :327-336, matching
    // Operator == currentUser OR NULL) and refuses Validate without your own row
    // (:1333). Testing "does ANY sdms row exist" hid the placeholder the moment a
    // colleague signed: the second analyst then saw the first one's signed row,
    // ticked that, and nothing was written for them — the batch stayed Pending with
    // a single row (client 2026-08-19, QB26081901_040). Only MY OWN row suppresses
    // the placeholder now.
    // ONLY while the batch is still under review. ALPACA creates the Pending row
    // under `(objab.Status == 2 || objab.Status == 3)` (DataReviewController :324),
    // so once the batch is SDMS Validated nobody owes a signature and no row is
    // offered. Without this a viewer opening a FINISHED batch got an extra
    // "Awaiting signature by …" row next to the two real signatures — reported on
    // QB26082002_021, which was fully validated by two analysts (client 2026-08-20).
    if (!pending) return rlRowsRaw;

    const alreadyHasSdms = rlRowsRaw.some(
      // `Operator == currentUser || Operator == null` — an UNCLAIMED row is the one
      // waiting for whoever opens the package, so it counts as mine and suppresses
      // the placeholder rather than sitting beside it.
      (r) => (r.processMode ?? "").toLowerCase() === "sdms validation"
           && (r.isMine || (!r.user && !r.operatorName)),
    );
    if (alreadyHasSdms) return rlRowsRaw;
    // My row is always PENDING, never populated from the batch. A colleague
    // validating PART of the batch leaves it pending (the partial path keeps
    // Status 2), so I still owe a signature and still get this row — but taking its
    // content from batch.validatedByList rendered it as 'Signed by E155503,E173506 ·
    // Accept · All Results' — a row that looks signed by two people and is signed by
    // neither (client 2026-08-19). It stays PENDING until I sign it.
    return [
      ...rlRowsRaw,
      {
        id: `__sdms-placeholder-${batch.id}`,
        isMine: true,
        processMode: "SDMS Validation",
        date: null,
        user: null,
        // Synthetic placeholder row — no resolved real name / integrity digest
        // (a real DataPackageReviewLog row supersedes it once one exists).
        // MY row — name it, so the analyst can see which signature is owed.
        operatorName: currentUserName || null,
        role: currentUserRole || null,
        comment: null,
        decisionStatus: null,
        reviewScope: null,
        status: null,
        signatureHash: null,
      },
    ];
  }, [rlRowsRaw, batch.id, currentUserName, currentUserRole, windowed, dpStatus, pending]);
  // Live Check Points from the backend (dbo.DataPackageChecklistQC via
  // /api/v1/analysis/result-validation/{batchOid}/check-points).  Empty
  // batches render an empty grid (matches LIMS's "No data to display").
  const { rows: cpApiRows, loading: cpLoading, error: cpError, reload: cpReload } = useCheckPoints(batch.id);
  // Live Attachments from the backend (dbo.DataPackageAttachments +
  // dbo.FileData via /api/v1/analysis/result-validation/{batchOid}/attachments).
  const { rows: atRows, loading: atLoading, error: atError, reload: atReload } = useAttachments(batch.id);
  // Batch Results grid's "RawData" Preview column (dbo.RawDataAttachment +
  // dbo.FileData via /api/v1/analysis/result-validation/{batchOid}/raw-data).
  const rawData = useRawData(batch.id);
  // Trend tab scope: the Trend view opens on the row the reviewer SELECTED in
  // Batch Results (its site + analyte) so it continues their train of thought
  // instead of showing an arbitrary first series.  brChecked holds row KEYS, so
  // map back through batchResultRowKey; with nothing ticked, the batch's first
  // row is a sensible default.
  // A batch whose results are all NON-numeric (Colilert-18 Presence/Absence
  // reports "Present"/"Absent", never a number) can never have a numeric trend.
  // Telling the Trend tab lets it say exactly that instead of implying the
  // history is merely missing.  Guarded on rows.length so an empty/still-loading
  // grid doesn't misreport a normal batch as qualitative.
  const isQualitativeBatch = useMemo(() => {
    if (batchRows.length === 0) return false;
    // Check BOTH columns. Many tests leave SpreadSheetEntry.NumericResult null
    // and carry the value in Result instead — the Anions_IC batches do exactly
    // that (172 rows, 0 parseable NumericResult, 123 parseable Result). Reading
    // only `numeric` mis-flagged those quantitative batches as qualitative and
    // hid the Trend pickers on them.
    // A censored result ("<1", ">10") is quantitative too — a value below or
    // above a detection limit, not a Present/Absent answer — so the leading
    // comparator is stripped before testing.
    const looksNumeric = (v: string | undefined) => {
      const s = (v ?? "").trim();
      if (s === "") return false;
      const bare = s.replace(/^[<>≤≥]\s*/, "").trim();
      return bare !== "" && !Number.isNaN(Number(bare));
    };
    return !batchRows.some((r) => looksNumeric(r.numeric) || looksNumeric(r.result));
  }, [batchRows]);
  // Trend scope is driven by the reviewer's ROW SELECTION (client requirement
  // 2026-08-03): with one or more Batch Results rows ticked, the Trend tab
  // auto-scopes to those rows' sampling locations + parameter and displays
  // immediately; with none ticked, it opens on the first row and the pickers
  // drive it manually.  A batch is normally one parameter, so `analytes` is
  // usually length 1 and `sites` is the set of ticked locations.
  const trendFocus = useMemo(() => {
    const checkedRows = batchRows.filter((r) => brChecked.has(batchResultRowKey(r)));
    const hasSelection = checkedRows.length > 0;
    const src = hasSelection ? checkedRows : batchRows.slice(0, 1);
    const norm = (s: string | null | undefined) => (s ?? "").trim();
    const sites = Array.from(new Set(src.map((r) => norm(r.siteName)).filter((s) => s !== "")));
    const analytes = Array.from(new Set(src.map((r) => norm(r.param)).filter((s) => s !== "")));
    // Ticked rows that are QC samples (no sampling location) — labelled for the
    // Trend tab's "nothing to trend" warning.
    const sitelessSelected = hasSelection
      ? Array.from(
          new Set(
            checkedRows
              .filter((r) => norm(r.siteName) === "")
              .map((r) => norm(r.sys) || norm(r.sampleName) || "QC row"),
          ),
        )
      : [];
    return {
      hasSelection,
      sites,
      analytes,
      sitelessSelected,
      // First-of-selection single focus, kept for the tab's default matching.
      site: sites[0] ?? null,
      analyte: analytes[0] ?? null,
    };
  }, [batchRows, brChecked]);
  const visibleResults = useMemo(() => {
    const t = resultsQ.trim().toLowerCase();
    if (!t) return batchRows;
    return batchRows.filter((r) =>
      [
        r.sys,
        r.param,
        r.numeric,
        r.result,
        r.units,
        r.analyzedBy,
        r.submittedBy,
        r.validatedBy,
        r.prepBatch,
        r.approvedBy,
        r.collector,
        r.sampleName,
        r.siteName,
        r.client,
        r.projectId,
        r.qualifier,
        r.qc,
        r.enteredBy,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(t),
    );
  }, [batchRows, resultsQ]);
  // Live checklist rows → the shared CheckPointRow shape so the renderer
  // doesn't care whether the source is live DB or hardcoded fallback.
  // Yes/No/NA priority mirrors legacy XAF: exactly one of the three bits is
  // set per row, but if none are set we render '' (empty) — same as the
  // legacy blank-decision rendering.
  const cpLiveRows = useMemo<CheckPointRow[]>(() => {
    return cpApiRows.map((r) => ({
      id: r.id,
      label: r.checkPoint ?? "",
      decision: (r.yes ? "Yes" : r.no ? "No" : r.na ? "NA" : "") as CheckDecision,
      qualifier: r.qualifier ?? "",
      comment: r.comment ?? "",
      defaultAnswer: r.defaultAnswer,
    }));
  }, [cpApiRows]);

  // Every PLM row ticked? Reads brChecked, which is where the PLM grid's ticks
  // live (see the note beside the PLM fetch).
  // Only reviewable rows count: with a disabled row in the batch the header box
  // could otherwise never reach "all", so it would read as permanently half-done.
  const plmReviewable = plmRows.filter((r) => r.canReview);
  const plmAllChecked = plmReviewable.length > 0
    && plmReviewable.every((r) => brChecked.has(r.sequenceOid));

  const cpSource: CheckPointRow[] = cpLiveRows;

  /** Calculate — legacy DataPackageViewController.Calculate_Execute (:2952).
   *
   *  Clears Yes/No/NA on every checkpoint and re-ticks the one its master row
   *  configures as the default (DataPackageCheckList.[Default] — 0 Yes · 1 No ·
   *  2 NA), which is the same column the seed SP reads when it first creates
   *  these rows. Legacy's second half (CalculateCheckPoint, deriving answers
   *  from SDMS QC pass/fail) is skipped by legacy ITSELF for a PLM package —
   *  `if (!dataPackage.IsPLMDataPackage)` — so it is not ported here.
   *
   *  Writes through decisionOverrides, the same channel the inline cell editor
   *  uses, so the result persists on Submit exactly like a hand-made edit and
   *  nothing is saved behind the analyst's back. */
  const doCalculate = useCallback(() => {
    const withDefault = cpSource.filter((r) => r.defaultAnswer != null);
    if (withDefault.length === 0) {
      // Legacy is a silent no-op here (it iterates an empty collection). Saying
      // so beats a button that appears to do nothing — which is exactly how a
      // PLM package behaves, since no PLM test is on a check list.
      notify.info("No check points with a configured default to calculate.");
      return;
    }
    setDecisionOverrides((prev) => {
      const next = { ...prev };
      for (const r of withDefault) {
        next[r.label] = r.defaultAnswer === 0 ? "Yes" : r.defaultAnswer === 1 ? "No" : "NA";
      }
      return next;
    });
    notify.success(`Reset ${withDefault.length} check point${withDefault.length === 1 ? "" : "s"} to the configured default.`);
  }, [cpSource, notify]);
  // Per-column formatter map — the SAME map is used by the header dropdown and
  // the filter pass so the option list matches what's rendered in each cell.
  // The Yes/No/NA columns collapse to 'Ticked' when their bit matches the row's
  // decision, empty otherwise — same distinct set the analyst sees.
  const CP_FMT: Record<string, (r: CheckPointRow) => string> = useMemo(
    () => ({
      checkPoint: (r) => r.label,
      yes: (r) => (r.decision === "Yes" ? "Ticked" : ""),
      no: (r) => (r.decision === "No" ? "Ticked" : ""),
      na: (r) => (r.decision === "NA" ? "Ticked" : ""),
      qualifier: (r) => r.qualifier ?? "",
      comment: (r) => r.comment ?? "",
    }),
    [],
  );
  const cpFiltered = useMemo(() => {
    const t = checksQ.trim().toLowerCase();
    return cpSource.filter((r) => {
      // Global search (across every column's displayed text).
      if (t && !Object.values(CP_FMT).some((f) => f(r).toLowerCase().includes(t))) return false;
      // Per-column Excel-style filters (AND across columns).
      for (const k of Object.keys(cpFilters)) {
        const sel = cpFilters[k];
        if (!sel) continue;
        if (!passesColumnFilter(sel, CP_FMT[k]?.(r) ?? "")) return false;
      }
      return true;
    });
  }, [cpSource, checksQ, cpFilters, CP_FMT]);
  // Sort AFTER filter — matches the visible-row contract every other grid uses.
  const visibleChecks = useMemo(
    () => cpSorter.applyByFormatted(cpFiltered, (r, k) => CP_FMT[k]?.(r) ?? ""),
    [cpSorter, cpFiltered, CP_FMT],
  );
  // Rows for one column's dropdown — narrowed by every OTHER active filter so
  // picking one column limits the other lists (Excel-style cascade).
  const cpDropdownRowsFor = (key: string) => cascadeRows(cpSource, cpFilters, key, (r, k) => CP_FMT[k]?.(r) ?? "");

  // Shared pre-submit REVIEW gates — OG's ritual the reviewer must complete before the batch is
  // submitted/validated: tick a row in Batch Results (QBIDSubmit :3186 "selectchkbox"), review the
  // Check Points (:3203 "You must review the check points before submission."), and select the
  // Review Log (:3192 "Select the review log to Submit."). Plus the AI category rule (Sample Results
  // mandatory + a soft coverage prompt for QC/Surrogate/Internal Standard). Returns true when all
  // gates pass; false (after a nag / soft confirm) when the caller must abort. Used by BOTH the
  // windowed DataPackage Submit and the full-content Result Validation Validate/Approve so the
  // ritual is identical — the popup previously skipped ALL of this and let the user submit blind.
  const runReviewSelectionGates = async (actionLabel: string): Promise<boolean> => {
    // The RULES live in ./reviewSelectionGate (pure + unit-tested); this owns only
    // the side effects — the nag, the soft confirm and the tab jump. Both callers
    // (this popup's Submit and the full-page Validate/Approve) go through here, so
    // the ritual cannot drift between them again.
    const gateInput = {
      isPlm,
      anyRowChecked: mark.br,
      plmReviewableCount: plmReviewable.length,
      plmCheckedCount: plmReviewable.filter((r) => brChecked.has(r.sequenceOid)).length,
      categoryCounts,
      categorySelected: (key: ResultCategory) =>
        batchRows.some((r) => categoryOfRow(r) === key && brChecked.has(batchResultRowKey(r))),
    };

    const selection = resultSelectionGate(gateInput);
    if (selection.blocked) {
      notify.warning(selection.message);
      if (selection.goTo) setTopTab(selection.goTo);
      return false;
    }

    const unreviewed = unreviewedCategories(gateInput, CATEGORY_KEYS);
    if (unreviewed.length > 0) {
      const labels = unreviewed
        .map((k) => CATEGORY_TABS.find((t) => t.key === k)?.label ?? k)
        .join(", ");
      const proceed = await confirm({
        title: "Not all result categories are reviewed",
        message:
          `No rows are selected in: ${labels}. ` +
          `Those categories are not marked as reviewed. Do you want to ${actionLabel.toLowerCase()} anyway?`,
        confirmLabel: `${actionLabel} anyway`,
        cancelLabel: "Go back",
      });
      if (!proceed) { setTopTab(unreviewed[0]!); return false; }
    }

    // checkPointsApplicable = "is there anything to tick?". Two ways there is
    // not, both of which deadlocked this gate (owner reports 2026-09-25):
    //   • no TAB  — a PLM package renders no Check Points tab (see the tab row
    //     below) and the grid renders only under it, so mark.cp cannot be set;
    //   • no ROWS — the tab IS shown on the full-page SDMS Result Validation,
    //     but a lab with no configured check points gets an EMPTY grid, and an
    //     empty grid cannot be ticked either.
    // A lab that HAS check points has rows, so the gate is unchanged for it.
    const marks = reviewMarkGate({
      checkPoints: mark.cp,
      reviewLog: mark.rl,
      checkPointsApplicable: !isPlm && cpSource.length > 0,
    });
    if (marks.blocked) { notify.warning(marks.message); return false; }
    return true;
  };

  // DataPackage Submit (windowed popup only): OG QBIDSubmit (:3172). This is the ONLY place the batch
  // is submitted — the ribbon "Submit" (Complete) just saved the sheet + opened this popup (OG's
  // batch-advance in Complete is commented out). OG makes the analyst complete the REVIEW RITUAL first
  // (sample selected · check points reviewed · review log selected), then persists the Check Point
  // edits, runs ValidateCheckPoint, advances the package 1→2 AND advances the batch to Pending
  // Validation. The QC-fail confirm already fired at the ribbon Submit (OG Complete :8343) before this
  // popup opened — QBIDSubmit does NOT re-ask — so we submit with force; the server still validates the
  // Check Points and the batch-advance gates. Validation/approval still run on the batch axis.
  const doDpTransition = async () => {
    if (dpRunningRef.current) return;
    dpRunningRef.current = true;
    setDpBusy(true);
    try {
      await submitDataPackage();
    } finally {
      dpRunningRef.current = false;
      setDpBusy(false);
    }
  };

  const submitDataPackage = async () => {
    if (!(await runReviewSelectionGates("Submit"))) return;
    // OG QBIDSubmit null-Result gate (DataPackageViewController :3283-3288): block the
    // submit the moment it's clicked — before any confirm — if ANY reportable row has no
    // Result entered ("Result cannot be empty."). batchRows is already IsReport=1 scoped
    // by BatchResultReader, matching OG's dviSamples (IsReport=True). The transition SP
    // re-checks server-side (authoritative); this mirror gives OG's immediate rejection.
    // …but NOT for a PLM package. A PLM batch's results are not in the SDMS
    // spreadsheet: dbo.SpreadSheetEntry_SampleInfo carries one Default-component
    // row per PLM sample with Result NULL (verified on the live test DB), because
    // a PLM result is the per-LAYER classification held in dbo.PLMStereoscopic-
    // Observation / dbo.PLMExam. So this gate fired on EVERY PLM submit and
    // nothing could ever clear it. PLM's equivalent rule ("Results must be
    // entered for all samples in the batch…") is AI_Plm_SubmitCheck_SP, which the
    // entry page already runs BEFORE this popup opens — so the rule is enforced,
    // just on the right data. (Owner report 2026-09-25; the same guard is on the
    // server side of the transition SP.)
    if (!isPlm && batchRows.some((r) => (r.result ?? "") === "")) {
      notify.error("Result cannot be empty.");
      return;
    }
    const ok = await confirm({
      title: "Submit Data Package?",
      message: isPlm
        // G5 parity (owner 2026-10-17): the ticked layers are submitted, an own layer left
        // unticked is un-submitted; the batch moves on once EVERY layer is submitted.
        ? "This submits the selected layers. The QC batch moves to PLM validation once every layer in it is submitted."
        : "This submits the Data Package and the QC batch for SDMS validation.",
    });
    if (!ok) return;
    const checkpoints = cpSource.map((r) => {
      const d = decisionOverrides[r.label] ?? r.decision;
      return {
        oid: r.id,
        yes: d === "Yes", no: d === "No", na: d === "NA",
        comment: (commentOverrides[r.label] ?? r.comment) || null,
        qualifier: (qualOverrides[r.label] ?? r.qualifier) || null,
      };
    });
    // PLM (G5 QBIDSubmit PLM path, owner 2026-10-17): the WHOLE selection is posted — ticked
    // rows are submitted under this user, an own submitted row left unticked is un-submitted
    // (rows the user may not toggle are ignored by the server); the batch moves on only once
    // every layer of it is submitted.
    const plmTicked = isPlm ? plmRows.filter((r) => brChecked.has(r.sequenceOid)).map((r) => r.sequenceOid) : undefined;
    try {
      const res = await runLoading(qcBatchApi.dataPackageTransition(batch.id, "submit", checkpoints, true, plmTicked),
        { blocking: true, label: "Submitting…" });
      if (isPlm && res?.advanced === false) {
        // Partial: these layers are in, the other analysts still have theirs to submit.
        setBrChecked(new Map());
        void reloadPlm();
        void cpReload();
        void rlReload();
        refreshProjectNotes();
        setNotesNonce((n) => n + 1);
        // G5 QBIDSubmit :3319 — the same message for a partial and a full PLM submit.
        if (onSubmitSuccess) onSubmitSuccess(false);
        else void showDialog({ type: "success", title: "Submitted", message: "Samples submitted successfully." });
        return;
      }
      setDpStatus(2);
      setSubmittedLocal({ by: currentUserName, date: new Date().toISOString() });
      // Leaving Submit mode: the ticks were the submission, and kept they preview as a Validated
      // stamp; the grid's pre-submit rows have no Submitted stamp. Clear and reload so the grid
      // shows the stored Submitted Date / By (user report 2026-10-28).
      setBrChecked(new Map());
      if (isPlm) void reloadPlm(); else void brReload();
      void cpReload();
      void rlReload();
      // Check Point comments were persisted by the transition → the backend sync SP
      // may have written new 'Data Package Check Point' notes; refresh the Project
      // Notes badge/popup immediately. refreshProjectNotes() syncs the popup + the
      // entry-page badge; setNotesNonce forces THIS detail's badge directly. (2026-10-08)
      refreshProjectNotes();
      setNotesNonce((n) => n + 1);
      onSubmitted?.();   // the batch is now submitted — let the entry page drop to View
      if (onSubmitSuccess) { onSubmitSuccess(true); return; }
      void showDialog({
        type: "success", title: "Submitted",
        // PLM: G5 QBIDSubmit :3319's message (owner 2026-10-17, G5 parity).
        message: isPlm ? "Samples submitted successfully." : "Data Package submitted successfully.",
      });
    } catch (e) {
      notify.warning((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? "Could not submit the Data Package.");
    }
  };

  // ── Review Log per-column filter setup (mirrors the Check Points block).
  // Formatters return the SAME displayed string each cell shows so the filter
  // list never disagrees with the visible rows.
  const RL_FMT: Record<string, (r: ApiReviewLogRow) => string> = useMemo(
    () => ({
      processMode: (r) => r.processMode ?? "",
      date: (r) => (r.date ? fmtLocalDateTime(r.date) || "" : ""),
      user: (r) => r.user ?? "",
      role: (r) => r.role ?? "",
      comment: (r) => r.comment ?? "",
      decisionStatus: (r) => r.decisionStatus ?? "",
      reviewScope: (r) => r.reviewScope ?? "",
      // Electronic Signature column sorts by signer then time (its own filter
      // funnel is not shown — the card is unique per row).
      electronicSignature: (r) => `${r.operatorName ?? r.user ?? ""} ${r.date ?? ""}`,
    }),
    [],
  );
  const rlFiltered = useMemo(() => {
    return rlRows.filter((r) => {
      for (const k of Object.keys(rlFilters)) {
        const sel = rlFilters[k];
        if (!sel) continue;
        if (!passesColumnFilter(sel, RL_FMT[k]?.(r) ?? "")) return false;
      }
      return true;
    });
  }, [rlRows, rlFilters, RL_FMT]);
  // DateTime column sorts on the raw ISO string (chronological) via the
  // smart comparator in useSort; other columns sort by displayed text.
  const visibleReview = useMemo(
    () => rlSorter.apply(rlFiltered, (r, k) => (k === "date" ? (r.date ?? "") : (RL_FMT[k]?.(r) ?? ""))),
    [rlSorter, rlFiltered, RL_FMT],
  );
  const rlDropdownRowsFor = (key: string) => cascadeRows(rlRows, rlFilters, key, (r, k) => RL_FMT[k]?.(r) ?? "");

  // ── Batch Results per-column filter setup ────────────────────────────
  // Formatters return the SAME displayed string each cell renders (dates
  // already formatted by apiRowToBatchRow) so the filter option list never
  // disagrees with the visible rows.
  const BR_FMT: Record<string, (r: BatchResultRow) => string> = useMemo(
    () => ({
      sys: (r) => r.sys ?? "",
      param: (r) => r.param ?? "",
      numeric: (r) => r.numeric ?? "",
      result: (r) => r.result ?? "",
      units: (r) => r.units ?? "",
      analyzedDate: (r) => r.analyzedDate ?? "",
      analyzedBy: (r) => r.analyzedBy ?? "",
      submittedDate: (r) => r.submittedDate ?? "",
      submittedBy: (r) => r.submittedBy ?? "",
      validatedDate: (r) => r.validatedDate ?? "",
      validatedBy: (r) => r.validatedBy ?? "",
      prepBatch: (r) => r.prepBatch ?? "",
      approvedDate: (r) => r.approvedDate ?? "",
      approvedBy: (r) => r.approvedBy ?? "",
      dateCollected: (r) => r.dateCollected ?? "",
      collector: (r) => r.collector ?? "",
      dateReceived: (r) => r.dateReceived ?? "",
      sampleName: (r) => r.sampleName ?? "",
      siteName: (r) => r.siteName ?? "",
      client: (r) => r.client ?? "",
      projectId: (r) => r.projectId ?? "",
      df: (r) => r.df ?? "",
      rptlimit: (r) => r.rptlimit ?? "",
      ucl: (r) => r.ucl ?? "",
      mdl: (r) => r.mdl ?? "",
      uql: (r) => r.uql ?? "",
      mcl: (r) => r.mcl ?? "",
      loq: (r) => r.loq ?? "",
      spike: (r) => r.spike ?? "",
      dld: (r) => r.dld ?? "",
      rpd: (r) => r.rpd ?? "",
      rpdCtrl: (r) => r.rpdCtrl ?? "",
      rec: (r) => r.rec ?? "",
      recCtrl: (r) => r.recCtrl ?? "",
      qualifier: (r) => r.qualifier ?? "",
      qc: (r) => r.qc ?? "",
      enteredBy: (r) => r.enteredBy ?? "",
      enteredDate: (r) => r.enteredDate ?? "",
    }),
    [],
  );
  const brFiltered = useMemo(() => {
    return visibleResults.filter((r) => {
      for (const k of Object.keys(brFilters)) {
        const sel = brFilters[k];
        if (!sel) continue;
        if (!passesColumnFilter(sel, BR_FMT[k]?.(r) ?? "")) return false;
      }
      return true;
    });
  }, [visibleResults, brFilters, BR_FMT]);
  const visibleResultsFiltered = useMemo(
    () => brSorter.applyByFormatted(brFiltered, (r, k) => BR_FMT[k]?.(r) ?? ""),
    [brSorter, brFiltered, BR_FMT],
  );

  // ── Split "Batch Results" into category tabs ─────────────────────────────
  // Which categories the batch actually HAS (computed from the full row set, not
  // the search/filter-narrowed one, so a tab never disappears mid-search). Empty
  // categories get no tab (decision: hide when empty). If a batch somehow has zero
  // rows at all, fall back to the Sample tab so the pane still renders its
  // toolbar + empty grid.
  const categoryCounts = useMemo(() => {
    const c: Record<ResultCategory, number> = { sample: 0, qc: 0, surr: 0, is: 0 };
    for (const r of batchRows) c[categoryOfRow(r)]++;
    return c;
  }, [batchRows]);
  const shownCategoryTabs = useMemo(() => {
    const shown = CATEGORY_TABS.filter((t) => categoryCounts[t.key] > 0);
    return shown.length ? shown : [CATEGORY_TABS[0]!];
  }, [categoryCounts]);
  // Multi-Tab OFF → the results pane is ONE "Batch Results" tab (all rows, legacy
  // column order); ON → the split category tabs. shownCategoryTabs still drives the
  // per-category review-coverage gate below regardless of how the pane is presented.
  const resultTabs = useMemo(
    () => (multiTab ? shownCategoryTabs : [{ key: "sample" as ResultCategory, label: "Batch Results" }]),
    [multiTab, shownCategoryTabs],
  );
  // Keep the active category tab valid: if it points at a category the batch no
  // longer has (batch switch, or rows just loaded), jump to the first shown one.
  // With Multi-Tab OFF there is only the single "sample"-keyed Batch Results tab.
  useEffect(() => {
    // A PLM package has no category tabs at all — without this guard the
    // !multiTab branch would yank the user off "plm" onto a "sample" tab that
    // is not rendered, leaving the card blank.
    if (isPlm) return;
    if (topTab === "checks" || topTab === "trend") return;
    if (!multiTab) { if (topTab !== "sample") setTopTab("sample"); return; }
    if (!shownCategoryTabs.some((t) => t.key === topTab)) {
      setTopTab(shownCategoryTabs[0]!.key);
    }
  }, [shownCategoryTabs, topTab, multiTab, isPlm]);
  // The searched/filtered/sorted rows: narrowed to the ACTIVE category tab when
  // Multi-Tab is on; ALL rows (the single Batch Results tab) when it is off.
  const activeCategory: ResultCategory = isCategoryTab(topTab) ? topTab : "sample";
  const categoryResults = useMemo(
    () => (multiTab
      ? visibleResultsFiltered.filter((r) => categoryOfRow(r) === activeCategory)
      : visibleResultsFiltered),
    [visibleResultsFiltered, activeCategory, multiTab],
  );

  // ── Tab-bar "Export" — the ACTIVE top grid, exactly as it reads on screen ──
  // WYSIWYG by construction: the rows are the already searched / filtered /
  // sorted ones the grid renders, the columns come from the same catalogue the
  // grid's headers do (with the analyst's hide + reorder applied, RawData's
  // Preview link skipped), and each cell reuses that grid's own *_FMT formatter
  // with any un-saved inline edits folded in. Writing the file is the shared
  // <ExportMenu>'s job (standing rule 8) — this only shapes headers + rows.
  const brExport = useMemo(() => {
    // Match the grid exactly: legacy catalogue + its own saved-order key + NO
    // per-category hiding when Multi-Tab is off; the split-tab catalogue with the
    // active tab's hidden columns removed when it is on.
    const catalog = multiTab
      ? batchResultColumnCatalog(showApprovedCols)
      : legacyBatchResultColumnCatalog(showApprovedCols);
    const prefsKey = multiTab ? "sdms-batchresults" : "sdms-batchresults-legacy";
    let columns = visibleExportColumns(catalog, loadGridPrefs(prefsKey), BR_FROZEN_ID);
    if (multiTab) columns = columns.filter((c) => !isColHiddenForCategory(c.id, activeCategory));
    return buildGridExport(columns, categoryResults, (r, id) => {
      const edited = brOverrides[batchResultRowKey(r)]?.[id];
      return edited !== undefined ? edited : (BR_FMT[id]?.(r) ?? "");
    });
    // topTab is a dependency so re-opening the tab re-reads the saved column
    // prefs (hide / reorder happen inside the child grid, not in this state).
  }, [categoryResults, brOverrides, BR_FMT, showApprovedCols, topTab, activeCategory, multiTab]);

  const cpExport = useMemo(() => {
    const columns = visibleExportColumns(checkPointColumnCatalog(), loadGridPrefs("sdms-checkpoints"));
    return buildGridExport(columns, visibleChecks, (r, id) => {
      // Check Points edits live in three per-label maps; fold them into the row
      // so CP_FMT renders the analyst's current Yes/No/NA, Qualifier and Comment.
      const merged: CheckPointRow = {
        ...r,
        decision: (decisionOverrides[r.label] ?? r.decision) as CheckPointRow["decision"],
        qualifier: qualOverrides[r.label] ?? r.qualifier,
        comment: commentOverrides[r.label] ?? r.comment,
      };
      return CP_FMT[id]?.(merged) ?? "";
    });
  }, [visibleChecks, CP_FMT, decisionOverrides, qualOverrides, commentOverrides, topTab]);

  // DF overlay for the SDMS sheet popup: the sheet's "DF" column is the DILUTION
  // merge field (template M9 = FIELD("DILUTION")) and LIMS re-merges it on Retrieve,
  // so the viewer renders se.Dilution rather than the saved blob's cell — which the
  // instrument import overwrites per analyte row, leaving an ISTD row at 1 where LIMS
  // shows the sample's 2. Built from the rows already loaded for the grid, so the
  // popup costs no extra request. (Client decision 2026-08-17, option A.)
  const sheetDfOverlay = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of batchRows) {
      const d = (r.dilution ?? "").trim();
      if (d !== "") m.set(dfOverlayKey(r.sys, r.param), d);
    }
    return m;
  }, [batchRows]);

  // Trend's SUMMARY TABLE projection, reported up by <SdmsTrendTab> (the chart
  // itself isn't exportable — the table under it is). Held here so the ONE
  // shared <ExportMenu> instance serves all three tabs.
  const [trendExport, setTrendExport] = useState<{ headers: string[]; rows: string[][] }>({
    headers: [],
    rows: [],
  });

  /** Export payload + filename for whichever top tab is showing. */
  const gridExport =
    topTab === "checks"
      ? { ...cpExport, filename: `${batch.batchId ?? "SDMS"}-CheckPoints` }
      : topTab === "trend"
        ? { ...trendExport, filename: `${batch.batchId ?? "SDMS"}-Trend` }
        : { ...brExport, filename: `${batch.batchId ?? "SDMS"}-BatchResults` };

  // ── Attachments per-column filter + sort setup ───────────────────────
  const AT_FMT: Record<string, (r: AttachmentRow) => string> = useMemo(
    () => ({
      name: (r) => r.name,
      category: (r) => r.category ?? "",
      file: (r) => r.file,
      date: (r) => r.date ?? "",
      operator: (r) => r.operator ?? "",
    }),
    [],
  );
  const atFiltered = useMemo(() => {
    return atRows.filter((r) => {
      for (const k of Object.keys(atFilters)) {
        const sel = atFilters[k];
        if (!sel) continue;
        if (!passesColumnFilter(sel, AT_FMT[k]?.(r) ?? "")) return false;
      }
      return true;
    });
  }, [atRows, atFilters, AT_FMT]);
  const visibleAttachments = useMemo(
    () => atSorter.applyByFormatted(atFiltered, (r, k) => AT_FMT[k]?.(r) ?? ""),
    [atSorter, atFiltered, AT_FMT],
  );
  const atDropdownRowsFor = (key: string) => cascadeRows(atRows, atFilters, key, (r, k) => AT_FMT[k]?.(r) ?? "");

  // Attachments editable only in EmptyStatus(0)/PendingSubmission(1) (OG parity) + canWrite.
  const atCanModify = canWrite && (dpStatus == null || dpStatus <= 1);

  /** Upload one or more files to the batch's Data Package (OG DataPackageAttachments "New").
   *  Sequential (the SP dedupes Name per package), then re-read the live grid. */

  // Attachment NEW dialog — legacy's nested-grid New, which opens
  // "Data Package Attachments": Name* · Category · Attachment* (Browse…) on the
  // left, Date · Operator on the right, OK / Cancel.  The bare file picker this
  // replaces uploaded straight away and named the row after the file, so the
  // analyst could not title or categorise it without a second Edit.
  //
  // Date + Operator are shown but NOT editable: the server stamps them on create
  // (they are provenance, the same reason the Edit dialog will not touch them).
  // Category + Operator are FK LOOKUPS in legacy, not free text
  // (DataPackageAttachments.Category -> dbo.Category, .Operator -> dbo.Employee),
  // so both dialogs offer dropdowns. Fetched once, lazily — a package the analyst
  // never attaches to costs nothing.
  const [atOpts, setAtOpts] = useState<{
    categories: { id: string; name: string | null }[];
    operators: { id: string; name: string | null }[];
  }>({ categories: [], operators: [] });
  const [atOptsLoaded, setAtOptsLoaded] = useState(false);
  const ensureAtOptions = useCallback(async () => {
    if (atOptsLoaded) return;
    try {
      setAtOpts(await qcBatchApi.dataPackageAttachmentOptions());
      setAtOptsLoaded(true);
    } catch {
      // A failed lookup must not block attaching: the dropdowns render empty and
      // the file still uploads with the server's defaults.
      setAtOptsLoaded(true);
    }
  }, [atOptsLoaded]);

  const [atNew, setAtNew] = useState(false);
  const [atNewName, setAtNewName] = useState("");
  const [atNewCat, setAtNewCat] = useState("");
  const [atNewOp, setAtNewOp] = useState("");
  const [atNewFile, setAtNewFile] = useState<File | null>(null);
  const openAtNew = useCallback(() => {
    setAtNewName(""); setAtNewCat(""); setAtNewOp(""); setAtNewFile(null); setAtNew(true);
    void ensureAtOptions();
  }, [ensureAtOptions]);
  const saveAtNew = async () => {
    const name = atNewName.trim();
    // Legacy marks both Name and Attachment required (the red * + the ✖ marker).
    if (!atNewFile) { notify.warning("Attachment is required."); return; }
    if (!name)      { notify.warning("Name is required."); return; }
    setAtBusy(true);
    try {
      // Two existing endpoints, no third: the upload creates the row (naming it
      // after the file), then the update applies the analyst's Name/Category.
      // The PUT is skipped when neither differs, so the common case is one call.
      const created = await qcBatchApi.uploadDataPackageAttachment(batch.id, atNewFile);
      if (created?.oid && (name !== created.name || atNewCat || atNewOp)) {
        await qcBatchApi.updateDataPackageAttachment(
          created.oid, name, atNewCat || null, atNewOp || null);
      }
      setAtNew(false);
      await atReload();
      notify.success("Attachment added.");
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
        ?? (e instanceof Error ? e.message : "Failed to add attachment."));
    } finally {
      setAtBusy(false);
    }
  };

  // Attachment row selection. The rows already rendered a checkbox, but it was
  // inert (`<input type="checkbox" />`, no state), so the only way to remove one
  // was the per-row trash icon — invisible while the grid is empty, which reads
  // as "Delete is missing" (user report 2026-09-24). Legacy's nested grid has a
  // toolbar Delete acting on the ticked rows; this backs it.
  const [atSel, setAtSel] = useState<Set<string>>(new Set());
  const atToggle = useCallback((id: string) => {
    setAtSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  // Drop ids that are no longer on screen (a reload, or someone else's delete),
  // so a stale tick can never target a row the analyst cannot see.
  useEffect(() => {
    setAtSel((prev) => {
      const live = new Set(atRows.map((r) => r.id).filter(Boolean) as string[]);
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [atRows]);

  /** Toolbar Delete — remove every TICKED attachment in one confirm. */
  const onDeleteSelectedAttachments = async () => {
    if (!atCanModify || atSel.size === 0) return;
    const n = atSel.size;
    const ok = await confirm({
      title: n === 1 ? "Remove attachment" : "Remove attachments",
      message: n === 1
        ? "Remove the selected attachment from this data package?"
        : `Remove ${n} attachments from this data package?`,
      tone: "danger",
    });
    if (!ok) return;
    try {
      // Sequential, not Promise.all: each is an audited soft-delete, and a
      // partial failure must leave the earlier ones done rather than racing.
      for (const id of atSel) await qcBatchApi.deleteDataPackageAttachment(id);
      setAtSel(new Set());
      await atReload();
      notify.success(n === 1 ? "Deleted." : `${n} attachments deleted.`);
    } catch (e) {
      await atReload();   // some may have gone through
      notify.error(e instanceof Error ? e.message : "Failed to remove attachment.");
    }
  };

  // Attachment EDIT (OG nested-grid Edit → DataPackageAttachments_DetailView,
  // Name · Category · Attachment | Date · Operator). Only Name + Category are
  // editable: the blob is replaced by an upload, not an edit, and Date/Operator
  // are the server's provenance stamps.
  const [atEdit, setAtEdit] = useState<AttachmentRow | null>(null);
  const [atEditName, setAtEditName] = useState("");
  const [atEditCat, setAtEditCat] = useState("");
  const [atEditBusy, setAtEditBusy] = useState(false);
  const [atEditOp, setAtEditOp] = useState("");
  const openAtEdit = useCallback((row: AttachmentRow) => {
    if (!row.id || !atCanModify) return;
    setAtEditName(row.name ?? "");
    // Preselect by FK Oid, not by display name — two categories could share a
    // label, and a name match would silently pick the wrong row.
    setAtEditCat(row.categoryOid ?? "");
    setAtEditOp(row.operatorOid ?? "");
    setAtEdit(row);
    void ensureAtOptions();
  }, [atCanModify, ensureAtOptions]);
  const saveAtEdit = async () => {
    if (!atEdit?.id) return;
    const name = atEditName.trim();
    if (!name) { notify.warning("Name is required."); return; }
    setAtEditBusy(true);
    try {
      await qcBatchApi.updateDataPackageAttachment(
        atEdit.id, name, atEditCat || null, atEditOp || null);
      setAtEdit(null);
      await atReload();
      notify.success("Saved successfully.");
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
        ?? (e instanceof Error ? e.message : "Failed to save the attachment."));
    } finally {
      setAtEditBusy(false);
    }
  };

  /** Remove one attachment (OG nested-grid Delete) — confirm, soft-delete, re-read. */
  const onDeleteAttachment = async (row: AttachmentRow) => {
    if (!row.id || !atCanModify) return;
    const ok = await confirm({
      title: "Remove attachment",
      message: `Remove "${row.file || row.name}" from this data package?`,
      tone: "danger",
    });
    if (!ok) return;
    try {
      await qcBatchApi.deleteDataPackageAttachment(row.id);
      await atReload();
      notify.success("Deleted successfully.");
    } catch (e) {
      notify.error(e instanceof Error ? e.message : "Failed to remove attachment.");
    }
  };

  // ── Bottom card (Review Log / Attachments) export ─────────────────────────
  // Same contract as the top card: the rows the grid is showing (filtered +
  // sorted), each cell through that grid's own *_FMT, columns from the shared
  // catalogue with the analyst's hide/reorder applied.
  const rlExport = useMemo(() => {
    const columns = visibleExportColumns(reviewLogColumnCatalog(), loadGridPrefs("sdms-reviewlog"));
    return buildGridExport(columns, visibleReview, (r, id) => RL_FMT[id]?.(r) ?? "");
  }, [visibleReview, RL_FMT, loTab]);

  const atExport = useMemo(() => {
    const columns = visibleExportColumns(attachmentColumnCatalog(), loadGridPrefs("sdms-attachments"));
    return buildGridExport(columns, visibleAttachments, (r, id) => AT_FMT[id]?.(r) ?? "");
  }, [visibleAttachments, AT_FMT, loTab]);

  /** Export payload + filename for whichever BOTTOM tab is showing. */
  const loExport =
    loTab === "attach"
      ? { ...atExport, filename: `${batch.batchId ?? "SDMS"}-Attachments` }
      : { ...rlExport, filename: `${batch.batchId ?? "SDMS"}-ReviewLog` };

  // Column-filter dropdown options are scoped to the ACTIVE category tab (search
  // applied, this column's own filter excluded) so each tab's funnel lists only
  // the values that appear in that tab.
  const brDropdownRowsFor = (key: string) =>
    cascadeRows(
      visibleResults.filter((r) => categoryOfRow(r) === activeCategory),
      brFilters,
      key,
      (r, k) => BR_FMT[k]?.(r) ?? "",
    );

  // Workflow stepper (5 steps): Select a QB › Batch Results › Check Points › Review Log › Validate
  const stepStates = (() => {
    // Step completion reflects the analyst's IN-SESSION progress (reference
    // flow) — NOT the batch's stored status.  Opening an already-validated
    // batch still starts the stepper fresh and ticks each step as its tab is
    // visited / a row is ticked, so the flow is identical for pending and
    // validated batches:
    //   • Select a QB — done (a batch is open).
    //   • Batch Results / Check Points / Review Log — done when that tab is
    //     clicked or a row is ticked (mark.br / cp / rl).
    //   • Validate — done only once all three are marked AND the batch is
    //     validated (just now via the Validate button, or previously) — so it
    //     never greens ahead of the middle steps.
    const reviewDone = mark.br && mark.cp && mark.rl;
    // Six steps (prototype (7)): Select a QB + Load are ALWAYS done once inside
    // the package; the rest tick as their rows are marked; the terminal step
    // (Validate / Approve) greens only once the action has happened — validated
    // for the Validation form, approved for the Approval form.
    const approved = /approved/i.test(batch.statusText);
    const done = [true, true, mark.br, mark.cp, mark.rl, reviewDone && (approval ? approved : validated)];
    const states: ("done" | "active" | "todo")[] = done.map((d) => (d ? "done" : "todo"));
    let activeSet = false;
    for (let i = 0; i < states.length; i++) {
      if (states[i] === "todo" && !activeSet) {
        states[i] = "active";
        activeSet = true;
      }
    }
    return states;
  })();
  const flowLabels = [
    "Select a QB",
    "Load",
    "Batch Results",
    "Check Points",
    "Review Log",
    approval ? "Approve" : "Validate",
  ];

  // ── Grid corner actions — rendered INSIDE the grid's own toolbar row (client
  // 2026-08-17: "both icons are moved to that grid"), at its right end, exactly
  // where legacy ALPACA LIMS puts them on a dashboard grid panel.
  //   Export   — the shared <ExportMenu> (standing rule 8): full ALPACA format
  //              set, Save-As picker, global loader and the shared "Exported to
  //              X successfully." acknowledgement. Acts on the ACTIVE grid.
  //   Maximize — view-only toggle; the grid card takes the whole content area.
  //              Esc restores it (see the Esc handler above).
  // Icon-only; the captions live in the hover tooltips. One node reused by both
  // grid panes — only one pane is mounted at a time (the active tab).
  // Built per CARD, because Maximize has to grow the card the analyst clicked in:
  //   "top" = Batch Results / Check Points / Trend,  "lo" = Review Log / Attachments.
  // Clicking Maximize on the card that is ALREADY maximized restores it, so the icon
  // is its own toggle and never leaves the analyst stuck in a maximized pane.
  const gridActionsFor = (
    target: "top" | "lo",
    ex: { filename: string; headers: string[]; rows: string[][] },
  ) => {
    const on = fullGrid === target;
    return (
      <div className="dv-gridacts">
        <ExportMenu
          filename={ex.filename}
          headers={ex.headers}
          rows={ex.rows}
          triggerClassName="dv-gridact"
          iconOnly
        />
        <button
          className={`dv-gridact${on ? " dv-on" : ""}`}
          title={on ? "Restore" : "Maximize"}
          aria-label={on ? "Restore" : "Maximize"}
          aria-pressed={on}
          onClick={() => setFullGrid((v) => (v === target ? null : target))}
        >
          <i className={`ti ${on ? "ti-arrows-minimize" : "ti-arrows-maximize"}`} />
        </button>
      </div>
    );
  };
  const gridActions = gridActionsFor("top", gridExport);

  // Keyboard shortcuts for this form's toolbar (CLAUDE.md standing rule 14, ADR-0013).
  // A bespoke feature form registers its buttons through the ONE shared hotkey layer
  // (never a raw listener). Full-content (SDMS Result Validation / Approval) only — the
  // windowed DataPackage popup is aria-modal so the store yields to it, and it has its
  // own action set. Each key mirrors the visible button (clicks it, so the button's
  // disabled gate + handler apply); the disabled flag greys it in the ? cheat sheet.
  // (Back/Esc is handled by the existing document-level Esc effect above.)
  const svGroup = approval ? "SDMS Result Approval" : "SDMS Result Validation";
  const tbKeys: HotkeyDef[] = [];
  if (!windowed) {
    tbKeys.push({
      combo: approval ? "alt+a" : "alt+v",
      label: approval ? "Approve" : "Validate",
      group: svGroup,
      order: 1,
      run: () => validateBtnRef.current?.click(),
      disabled: (approval ? !validated : !pending) || validateBusy || !canWrite,
    });
    tbKeys.push({
      combo: "alt+r",
      label: "Refresh",
      group: svGroup,
      order: 5,
      run: () => refreshBtnRef.current?.click(),
    });
  }
  // Project Notes (validation-only) + Open SDMS ride the detail toolbar; the windowed
  // popup shows Submit instead of Validate. Each mirrors its visible button's gate.
  if (!approval) {
    tbKeys.push({
      combo: "alt+j", label: "Project Notes", group: svGroup, order: 2,
      run: () => setNotesOpen(true),
    });
  }
  if (windowed && dpStatus === 1) {
    tbKeys.push({
      combo: "alt+u", label: "Submit", group: svGroup, order: 3,
      run: () => void doDpTransition(), disabled: !canWrite || dpBusy,
    });
  }
  if (!fromSdms) {
    tbKeys.push({
      combo: "alt+g", label: "Open SDMS", group: svGroup, order: 6,
      run: () => setSdmsOpen(true),
    });
  }
  useHotkeys(tbKeys);

  const tree = (
    <div
      className={`dv-ov${windowed ? " dv-ov--popup" : ""}${fullGrid === "top" ? " dv-fullgrid" : fullGrid === "lo" ? " dv-fullgrid-lo" : ""}`}
      // Modal ONLY in the windowed popup. The full-content SDMS Result Validation /
      // Approval view is a PAGE, not a modal — marking it aria-modal made the shared
      // hotkey store yield to it (it yields to any open [aria-modal]), so NO shortcut
      // (Alt+V/Alt+R/…) fired on this form (user report 2026-09-22). A page also
      // shouldn't trap focus. So role/aria-modal apply only when windowed.
      role={windowed ? "dialog" : "region"}
      aria-modal={windowed ? true : undefined}
      aria-label={`${captionBase} — ${batch.batchId ?? ""}`}
    >
      {/* Close (×) — POPUP (windowed) mode only. A true modal gets a corner close; the
          full-content Result Validation/Approval view uses the "Back" toolbar link + the
          breadcrumb instead. (user UI feedback 2026-09-16.) */}
      {windowed && (
        <button className="dv-x" title="Close" aria-label="Close" onClick={onClose}>
          <i className="ti ti-x" />
        </button>
      )}
      {/* Post-validate/approve success surfaces via the shared §13 notification
          (notify.success — top banner). The old per-form green banner was
          removed (standing rule 7). */}
      {/* ── workflow stepper — portalled into the shared TOP slot so the shell's
          single top bar reflects THIS form's step progress while a package is
          open (the list's own stepper is suppressed meanwhile).  The overlay
          below fills only the content area, so sidebar + top bar + top stepper
          all stay put — only the centre changes. ───────────────────────────── */}
      {!windowed && bannerSlot &&
        createPortal(
          // Just the steps — no workflow label, no note (the guidance lives in the
          // orange hint strip below, prototype (7) §5).  The `dv-island-detail`
          // marker lets our own CSS hide the list page's placeholder stepper while
          // this live one is docked — no change to the shared list/shell standard.
          // (Popup mode is self-contained — it does not portal the stepper to the
          // shell top bar, which would sit dimmed behind the modal backdrop.)
          <div className="dv-flow dv-island-top dv-island-detail">
            {flowLabels.map((label, i) => (
              <span key={label} className="dv-fseg">
                <span className={`dv-fstep dv-${stepStates[i]}`}>
                  <span className="dv-fn">{stepStates[i] === "done" ? "✓" : i + 1}</span>
                  {label}
                </span>
                {i < flowLabels.length - 1 && (
                  <span className={`dv-farrow${stepStates[i] === "done" ? " dv-done" : ""}`} />
                )}
              </span>
            ))}
          </div>,
          bannerSlot,
        )}
      {/* ── top toolbar (prototype (7)): Back · Refresh · Project Notes · SDMS
          on the left, Validate pushed to the RIGHT (via CSS order). ────────── */}
      <div className="dv-tb">
        {/* Back — the full-content list detail's return link. Hidden in POPUP (windowed)
            mode, which uses the corner × instead. (user UI feedback 2026-09-16.) */}
        {!windowed && (
          <button
            className="dv-lk"
            onClick={onClose}
            title={`Back to the SDMS Result ${approval ? "Approval" : "Validation"} list (${formatCombo("esc")})`}
          >
            <i className="ti ti-arrow-left" /> Back
          </button>
        )}
        {/* Refresh — hidden in popup (windowed) mode along with SDMS; the popup is a
            short-lived modal (reopen to re-pull). Result Validation / Approval keep it. */}
        {!windowed && (
          <button
            ref={refreshBtnRef}
            className="dv-lk"
            title={`Reload this package's data (${formatCombo("alt+r")})`}
            onClick={() => {
              void brReload();
              void rlReload();
              void cpReload();
              void atReload();
            }}
          >
            <i className="ti ti-refresh" /> Refresh
          </button>
        )}
        {/* SDMS-results Validate/Approve — hidden in the DataPackage popup (windowed), which
            uses the DataPackage workflow (Submit/Validate) button below instead. */}
        {!windowed && canWrite && (
        <button
          ref={validateBtnRef}
          className="dv-lk dv-validate"
          disabled={(approval ? !validated : !pending) || validateBusy}
          title={
            (approval
              ? validated
                ? validateBusy
                  ? "Approving…"
                  : "Approve this batch"
                : "Only SDMS Validated batches can be approved"
              : validated
                ? "Already validated"
                : validateBusy
                  ? "Validating…"
                  : "Validate this batch")
            + ` (${formatCombo(approval ? "alt+a" : "alt+v")})`
          }
          onClick={async () => {
            if ((approval ? !validated : !pending) || validateBusy) return;
            // Legacy XAF-parity gate: the reviewer must physically tick a row in
            // EACH of the three grids (Batch Results, Check Points, Review Log)
            // before Submit — the same three-step ritual for Validate AND Approve.
            // First failure wins the nag; server-side gates re-check on submit.
            //   Gate A  Batch Results — "Please select an item to proceed further."
            //   Gate B  Check Points  — "You must review the check points before submission."
            //   Gate C  Review Log    — "Select the review log to Submit."
            // These three are BLOCKING pre-submit guards, so they use the shared
            // CENTERED WARNING MODAL (§13 two-tier standard: warnings → modal), not a
            // transient top banner — the reviewer must acknowledge before proceeding
            // (client request 2026-08-11 "center popup instead of top banner"). ALPACA
            // used InformationType.Info here (DataReviewController.cs lines 1152/1259/
            // 1282/1380, 1501, 1157/1287); we surface them as warnings via useNotify.
            // ONE shared implementation (runReviewSelectionGates) — this button used
            // to carry a verbatim COPY of the same five gates, which is exactly how
            // the PLM branch above came to be missing here (standing rule 8, never
            // reinvent). The gates, their order and their wording are unchanged:
            //   Gate A  Batch Results   — "Please select an item to proceed further."
            //   Gate B  Sample Results  — hard-required when the batch has sample rows
            //                             (a PLM package gates on its PLM Results grid)
            //   Gate C  soft coverage   — QC / Surrogate / Internal Standard
            //   Gate D  Check Points    — "You must review the check points before submission."
            //   Gate E  Review Log      — "Select the review log to Submit."
            if (!(await runReviewSelectionGates(approval ? "Approve" : "Validate"))) return;
            // All gates passed → POST directly (no intermediate confirmation), then
            // flip the batch in place + show the ALPACA-style green success banner.
            setValidateBusy(true);
            try {
              // ── ALPACA parity: persist any pending inline cell edits BEFORE the
              // status flip — legacy's batch-edit grid flushes these via
              // ObjectSpace.CommitChanges() as part of the SAME Validate/Approve
              // action (DataReviewController.cs :1971), so an edited RPTLIMIT /
              // MCL / SpikeAmount / DLD / QCStatus / EnteredBy / EnteredDate,
              // Check Points Yes/No/NA / Qualifier / Comment, or Review Log
              // Comment / DecisionStatus / ReviewScope is never silently
              // discarded. batchRows (unfiltered) — not the search/filtered
              // view — so an edit stays saved even if the row is temporarily
              // hidden by a search/filter.
              const brEdits = batchRows
                .map((r) => ({ id: r.id, ov: brOverrides[batchResultRowKey(r)] }))
                .filter((x): x is { id: string; ov: Record<string, string> } => !!x.id && !!x.ov)
                .map(({ id, ov }) => ({
                  id,
                  rptLimit: ov.rptlimit,
                  mcl: ov.mcl,
                  spikeAmount: ov.spike,
                  dld: ov.dld,
                  qcStatus: ov.qc,
                  enteredBy: ov.enteredBy,
                  enteredDate: ov.enteredDate,
                }));
              const cpEdits = cpSource
                .filter(
                  (r) =>
                    qualOverrides[r.label] !== undefined ||
                    commentOverrides[r.label] !== undefined ||
                    decisionOverrides[r.label] !== undefined,
                )
                .map((r) => ({
                  id: r.id,
                  qualifier: qualOverrides[r.label],
                  comment: commentOverrides[r.label],
                  decision: decisionOverrides[r.label],
                }));
              // The synthesized "SDMS Validation" row (id "__sdms-placeholder-…")
              // is NOT a real dbo.DataPackageReviewLog row — its decision is
              // previewed client-side (auto-filled the moment the row is ticked,
              // ALPACA parity) and rendered post-validate from the batch's own
              // validated status (Phase-2b).  Never POST it: ReviewLogEdit.Id is a
              // Guid, so a synthetic id would 400 the whole save.  Real rows (Peer
              // Review, a persisted SDMS row) still persist DecisionStatus /
              // ReviewScope / Comment.
              const rlEdits = Object.entries(rlOverrides)
                .filter(([id]) => !id.startsWith("__"))
                .map(([id, ov]) => ({
                  id,
                  comment: ov.comment,
                  decisionStatus: ov.decisionStatus,
                  reviewScope: ov.reviewScope,
                }));
              // The synthesized "SDMS Validation"/"SDMS Approval" row (id
              // "__sdms-placeholder-…") has no DB Oid, so its decision + comment
              // can't ride rlEdits (id-keyed) — send it as `sdmsReview`, which the
              // API UPSERTS as a real DataPackageReviewLog row (Operator = signer →
              // Role fills via the reader join; the comment survives Validate).
              // MY row, not merely the FIRST sdms row. rlRows appends the placeholder
              // AFTER the API rows, so once a colleague had a signed SDMS row, find()
              // returned THEIRS — a real Oid, so isPlaceholder was false and no payload
              // was ever built. The samples were stamped and the batch flipped to
              // Validated while the signer got no review-log row at all (client
              // 2026-08-19, QB26081902_040: 8 samples stamped ValidatedBy=E173506 at
              // 16:43:35, still no SDMS Validation row for them).
              const sdmsRows = rlRows.filter((r) => (r.processMode ?? "").toLowerCase().includes("sdms"));
              const sdmsRow = sdmsRows.find((r) => r.id.startsWith("__")) ?? sdmsRows.find((r) => r.isMine) ?? sdmsRows[0];
              const isPlaceholder = !!sdmsRow && sdmsRow.id.startsWith("__");
              const sdmsOv = isPlaceholder && sdmsRow ? rlOverrides[sdmsRow.id] : undefined;
              // TICKED is what commits the row — not "did they touch a dropdown".
              // Requiring an override meant an analyst who accepted the defaults and
              // just ticked the row sent nothing, so their signature was never written
              // and the package kept only the first validator's row (client 2026-08-19,
              // QB26081901_040: 12/12 samples validated, still no row for the second
              // analyst). The defaults below mirror what the row already displays.
              const sdmsTicked = isPlaceholder && !!sdmsRow && rlChecked.has(sdmsRow.id);
              const sdmsReview =
                sdmsRow && (sdmsOv || sdmsTicked)
                  ? {
                      processMode: sdmsRow.processMode ?? "SDMS Validation",
                      comment: sdmsOv?.comment,
                      decisionStatus: sdmsOv?.decisionStatus ?? "Accept",
                      reviewScope: sdmsOv?.reviewScope ?? "All Results",
                    }
                  : undefined;
              if (brEdits.length > 0 || cpEdits.length > 0 || rlEdits.length > 0 || sdmsReview) {
                await postSaveSdmsEdits(batch.id, {
                  batchResults: brEdits,
                  checkPoints: cpEdits,
                  reviewLog: rlEdits,
                  sdmsReview,
                });
                // A Check Point comment just persisted → the backend sync SP may have
                // written new 'Data Package Check Point' notes; refresh the Project
                // Notes badge/popup now (they only auto-refresh on an in-popup note
                // change, not on this server-side write). refreshProjectNotes() syncs
                // the popup + other badges; setNotesNonce forces THIS badge. (2026-10-08)
                if (cpEdits.length > 0) { refreshProjectNotes(); setNotesNonce((n) => n + 1); }
              }
              // PARTIAL VALIDATION (ALPACA parity — DataReviewController.
              // ValidateSamples → Grid.GetSelectedFieldValues("Oid")): send ONLY
              // the ticked Batch-Results rows' DB ids (SpreadSheetEntry_SampleInfo.
              // Oid).  Map each ticked content-key back to the row's real `id` — the
              // same mapping the save-edits path above (:1142-1144) uses.  batchRows
              // (unfiltered) so a ticked row hidden by a search still counts.
              const selectedRowIds = batchRows
                .filter((r) => !!r.id && brChecked.has(batchResultRowKey(r)))
                .map((r) => r.id as string);

              if (approval) {
                // Approve: SDMS Validated → SDMS Approved (DV-1-2 → DV-6-4).  Stamps
                // the verifier on ONLY the selected rows; the batch advances (and the
                // parent drops the row from the pending-approval list) only once every
                // row is approved.
                const resp = await postApprove([batch.id], "", selectedRowIds);
                const skipped = resp.skipped ?? {};
                if (resp.count === 0 && Object.keys(skipped).length > 0) {
                  notify.warning(Object.values(skipped).join(" · "));
                  return;
                }
                if (resp.fullyApproved === false) {
                  // Partial — batch stays in the approval queue.  Clear the ticks
                  // and reload so the just-stamped rows show ApprovedBy/Date and the
                  // rest stay pending.  Do NOT flip the pill or drop it from the list.
                  setBrChecked(new Map());
                  void brReload();
                  // …and the Review Log: the signature row was just written, so without
                  // this it keeps showing the pre-signature state until a manual Refresh
                  // (client 2026-08-19: the row showed the user CODE, and only after a
                  // page refresh resolved to the display name).
                  void rlReload();
                  // A PARTIAL approved is still a committed, audited change, so it gets
                  // the Tier-1 centred dialog like the full one (§18.6) — and here it matters
                  // more: it must be acknowledged, because the batch is NOT finished and the
                  // analyst has to come back for the remaining samples (client 2026-08-19).
                  // The counts become detail rows rather than prose in the message.
                  void showDialog({
                    type: "success",
                    title: "Partially Approved",
                    message: "The batch is not finished — the remaining samples are still pending.",
                    idLabel: "QC Batch ID",
                    idValue: batch.batchId ?? "",
                    rows: [
                      { label: "Approved", value: String(resp.stampedSamples ?? selectedRowIds.length) },
                      { label: "Still pending", value: String(resp.remainingSamples ?? 0) },
                    ],
                  });
                } else {
                  setBatch((b) => ({ ...b, statusText: "SDMS Approved", statusId: "DV-6-4" }));
                  onValidated(resp.approvedBy, resp.approvedAt);
                  // Re-pull BOTH grids before the dialog: the signature row and the
                  // per-sample stamps were just written, and the Review Log in
                  // particular would otherwise keep showing this user's placeholder as
                  // "Awaiting signature" on a row they have just signed (client
                  // 2026-08-19). Only the PARTIAL paths reloaded until now.
                  void rlReload();
                  void brReload();
                  // Milestone (§18.6) → centered modal, not the Tier-2 top banner.
                  void showDialog({ type: "success", title: "Approved", message: "Samples approved successfully." });
                }
              } else {
                const resp = await postValidate([batch.id], "", selectedRowIds);
                const skipped = resp.skipped ?? {};
                if (resp.count === 0 && Object.keys(skipped).length > 0) {
                  notify.warning(Object.values(skipped).join(" · "));
                  return;
                }
                if (resp.fullyValidated === false) {
                  // Partial — keep the batch Pending SDMS Validation.  Clear the ticks
                  // and reload the grid so the just-stamped rows show ValidatedBy/Date;
                  // do NOT flip the pill or drop it from the list (onValidated).
                  setBrChecked(new Map());
                  void brReload();
                  // …and the Review Log: the signature row was just written, so without
                  // this it keeps showing the pre-signature state until a manual Refresh
                  // (client 2026-08-19: the row showed the user CODE, and only after a
                  // page refresh resolved to the display name).
                  void rlReload();
                  // A PARTIAL validated is still a committed, audited change, so it gets
                  // the Tier-1 centred dialog like the full one (§18.6) — and here it matters
                  // more: it must be acknowledged, because the batch is NOT finished and the
                  // analyst has to come back for the remaining samples (client 2026-08-19).
                  // The counts become detail rows rather than prose in the message.
                  void showDialog({
                    type: "success",
                    title: "Partially Validated",
                    message: "The batch is not finished — the remaining samples are still pending.",
                    idLabel: "QC Batch ID",
                    idValue: batch.batchId ?? "",
                    rows: [
                      { label: "Validated", value: String(resp.stampedSamples ?? selectedRowIds.length) },
                      { label: "Still pending", value: String(resp.remainingSamples ?? 0) },
                    ],
                  });
                } else {
                  setBatch((b) => ({
                    ...b,
                    statusText: "SDMS Validated",
                    statusId: "DV-1-2",
                    validatedByList: resp.validatedBy,
                    validatedDateMax: resp.validatedAt,
                  }));
                  onValidated(resp.validatedBy, resp.validatedAt);
                  // Re-pull BOTH grids before the dialog: the signature row and the
                  // per-sample stamps were just written, and the Review Log in
                  // particular would otherwise keep showing this user's placeholder as
                  // "Awaiting signature" on a row they have just signed (client
                  // 2026-08-19). Only the PARTIAL paths reloaded until now.
                  void rlReload();
                  void brReload();
                  // Milestone (§18.6) → centered modal, not the Tier-2 top banner.
                  void showDialog({ type: "success", title: "Validated", message: "Samples validated successfully." });
                }
              }
            } catch (e) {
              notify.error(e instanceof Error ? e.message : approval ? "Failed to approve" : "Failed to validate");
            } finally {
              setValidateBusy(false);
            }
          }}
        >
          <i className="ti ti-shield-check" /> {approval ? "Approve" : "Validate"}
        </button>
        )}
        {/* DataPackage Submit (windowed popup) — OG QBIDSubmit: Submit at PendingSubmission (1). In SDMS
            the analyst only SUBMITS the Data Package; validation/approval run on the batch axis. Shown
            INSTEAD of the SDMS-results Validate above; hidden once submitted (2+). */}
        {windowed && dpStatus === 1 && canWrite && (
          <button
            className="dv-lk dv-validate"
            disabled={dpBusy}
            title={
              (dpBusy ? "Submitting…" : "Submit this Data Package") + ` (${formatCombo("alt+u")})`
            }
            onClick={() => void doDpTransition()}
          >
            <i className="ti ti-shield-check" /> Submit
          </button>
        )}
        {/* Detail toolbar matches legacy ALPACA: Validate · Project Notes · SDMS.
            Refresh + Audit Trail intentionally omitted here (Refresh is on each
            grid's mini-bar; Audit Trail is on the list-view toolbar).
            Project Notes is a VALIDATION-only action — ALPACA's SDMS Result
            Approval (DataVerify) toolbar has only Approve · SDMS, so we hide it
            in approval mode. */}
        {!approval && (
          <button className="dv-lk" title={`Project Notes for this batch (${formatCombo("alt+j")})`} onClick={() => setNotesOpen(true)}>
            <i className="ti ti-notes" /> Project Notes ({notesCount})
          </button>
        )}
        {/* SDMS spreadsheet button — shown when opened from the QC Batch list or Result
            Validation. HIDDEN when opened from inside the SDMS Result Entry form (fromSdms):
            the user is already in SDMS, so it's redundant. (user UI feedback 2026-09-16.)
            NOT gated by canSeeForm("sdms") (owner decision 2026-10-29): this opens the SDMS
            spreadsheet READ-ONLY, which every validator must be able to do while validating —
            the SAME view the Result Validation list's eyeball (Preview) exposes ungated.
            Tying it to the SDMS Result Entry nav permission wrongly hid it for validators
            without that grant (ffc2e34d regression).
            In the windowed popup the sheet opens FULL-SCREEN (startFullscreen below). */}
        {!fromSdms && !isPlm && (
          <button className="dv-lk" title={`Open SDMS spreadsheet (${formatCombo("alt+g")})`} onClick={() => setSdmsOpen(true)}>
            <i className="ti ti-table" /> SDMS
          </button>
        )}
        {/* QC Batches — open THIS batch's QC Batch detail form (user request
            2026-10-27). Shown on BOTH the full-content detail AND the windowed Data
            Package popup opened from the QC Batches list (user follow-up 2026-10-27). */}
        {canSeeForm("qcbatches") && (
          <button className="dv-lk" title="Open this batch's QC Batch in a window (keeps this page)" onClick={() => setQcBatchOpen(true)}>
            <i className="ti ti-chart-dots" /> QC Batches
          </button>
        )}
        {/* PLM — legacy's SAME AddSDMS action, re-captioned: DataPackageViewController
            :175 does `if (parentDataPackage.IsPLMDataPackage) AddSDMS.Caption = "PLM"`.
            It opens the batch's ENTRY screen. Unlike SDMS this is NOT hidden when the
            popup was opened from that screen — legacy shows it there (user 2026-09-24),
            and closing the popup lands on exactly the view legacy would open, so the
            default action is onClose. A caller that reaches this popup from elsewhere
            passes onOpenPlm to navigate instead. */}
        {isPlm && (
          <button
            className="dv-lk"
            title="Open the PLM result entry screen for this batch"
            onClick={() => (onOpenPlm ?? onClose)()}
          >
            <i className="ti ti-microscope" /> PLM
          </button>
        )}
        {/* Calculate — legacy DataPackageViewController.Calculate_Execute :2952.
            Resets EVERY check point to its configured default answer
            (DataPackageCheckList.Default → 0 Yes · 1 No · 2 NA), clearing Yes/No/NA
            first. The second half of legacy's handler (CalculateCheckPoint, which
            derives answers from SDMS QC pass/fail) is explicitly skipped for a PLM
            package by legacy itself — `if (!dataPackage.IsPLMDataPackage)` — so it is
            not ported here either.
            Enabled on the same criterion as legacy's action
            (TargetObjectsCriteria :65 — Status = PendingSubmission or EmptyStatus). */}
        {dpStatus != null && dpStatus <= 1 && canWrite && (
          <button
            className="dv-lk"
            disabled={dpBusy}
            title="Reset every check point to its configured default answer"
            onClick={doCalculate}
          >
            <i className="ti ti-calculator" /> Calculate
          </button>
        )}
        {/* No × close button: the detail lives in the shell (sidebar + top bar
            stay), so the "SDMS Result Validation" breadcrumb link and browser
            Back return to the list. */}
      </div>

      <div className="dv-body">
        {/* Gate / error messages now surface via the shared top-notification
            service (useNotify) — a TIMED top-centre toast, matching ALPACA's
            ShowMessage(Info, Top, timed).  No in-page banner here anymore. */}
        {/* ── breadcrumb + title ──────────────────────────────────── */}
        <div className="dv-crumb">
          <a onClick={onClose}>{captionBase}</a>
          {" / "}
          {batch.test ?? "—"}
        </div>
        {/* Title + Audit Trail chip (prototype (7)) — the chip opens the shared
            AuditTrailPopup already wired below. */}
        <div className="dv-h1-row">
          <div className="dv-h1">
            {captionBase} - {batch.test ?? "—"}
          </div>
          {auditAllowed && (
            <button className="dv-audit-chip" onClick={() => setAuditOpen(true)} title="Audit Trail — view only">
              <i className="ti ti-history" /> Audit Trail
            </button>
          )}
        </div>

        {/* Orange instruction strip removed (user request 2026-09-19). The
            workflow island shows the steps and the Batch Information Status chip
            shows validated/approved state. */}

        {/* ── Read-only MAC banner ──────────────────────────────── */}
        {!canWrite && (
          <div className="dv-readonly-banner">
            <i className="ti ti-eye" /> <b>Read-only access</b> — you can view this batch but cannot Validate or Approve. Contact your administrator to request Write permission.
          </div>
        )}

        {/* ── Batch Information ───────────────────────────────────── */}
        <div className="dv-sec">Batch Information</div>
        <div className="dv-grid">
          <label>QC Batch ID:</label> <div className="dv-fld" ref={topFieldRef} tabIndex={-1}>{escTxt(batch.batchId)}</div>
          {/* OG shows DIFFERENT header fields per view (user UI feedback 2026-09-16):
              • the windowed DATA PACKAGE popup → "Package Created By" (QCBatchID.CreatedBy —
                the QC batch's creator) + "Date Package Created" (DataPackage.DateTimeCreated);
                both PERSISTED, stable on reopen, from create-or-find.
              • the full-content SDMS RESULT VALIDATION → "Submitted By" + "Date Submitted"
                (the batch's persisted SamplesSubmittedBy/Date; blank until submitted, with the
                local capture as the just-submitted fallback). */}
          <label>{windowed ? "Package Created By:" : "Submitted By:"}</label>
          <div className="dv-fld">{(windowed
            ? escTxt(packageCreatedBy)
            : (escTxt(batch.submittedByList) || submittedLocal?.by)) || "—"}</div>
          <label className="dv-req">Test:</label>{" "}
          <div className="dv-fld">
            {escTxt(batch.test)}
          </div>
          <label>{windowed ? "Date Package Created:" : "Date Submitted:"}</label>{" "}
          <div className="dv-fld">
            {windowed
              ? (packageCreatedDate ? fmtLocalDate(packageCreatedDate) || "" : "")
              : (batch.submittedDateMax
                  ? fmtLocalDate(batch.submittedDateMax) || ""
                  : submittedLocal ? fmtLocalDate(submittedLocal.date) || "" : "")}
          </div>
          <label className="dv-req">Method:</label>{" "}
          <div className="dv-fld">
            {escTxt(batch.method)}
          </div>
          <label>Template ID:</label>{" "}
          <div className="dv-fld">
            {batch.templateID == null ? "—" : String(batch.templateID)}
          </div>
          <label className="dv-req">Matrix:</label>{" "}
          <div className="dv-fld">
            {escTxt(batch.matrix) || "Water"}
          </div>
          <label>Status:</label>{" "}
          <div className="dv-fld" style={{ background: "transparent", border: "none", padding: "5px 0" }}>
            {/* After an in-popup Submit the parent's statusText is stale (Pending Submission);
                the batch is now Pending SDMS Validation (NewStatus DV-1-1), so reflect that
                immediately. (The numeric-enum label is "Pending Validation" — the SDMS-specific
                StatusDefinition label is what OG shows.) */}
            {statusPill(submittedLocal ? "Pending SDMS Validation" : batch.statusText)}
          </div>
        </div>

        {/* ── Top tabs: Sample Results · QC Sample Results · Surrogate ·
            Internal Standard · Check Points · Trend ──────────────── */}
        {/* dv-tabs-top marks THIS tab row (not the Review Log one below) as the
            pair that stays visible in full-screen mode. */}
        <div className="dv-tabs dv-tabs-top">
          {/* The legacy single "Batch Results" tab is split by row category. A tab
              shows only when the batch has rows of that category (decision: hide
              when empty), with a per-tab row count. Selecting a row in ANY of these
              category grids ticks the SAME "Batch Results" review step
              (onMarkAny → mark.br) — the split does not change the Validate gate. */}
          {/* A PLM batch has no SDMS spreadsheet rows, so the category tabs would
              all be empty. Legacy shows ONE "PLM Results" tab instead. */}
          {isPlm ? (
            <div
              className={`dv-tab ${topTab === "plm" ? "dv-active" : ""}`}
              onClick={() => setTopTab("plm")}
            >
              PLM Results ({plmRows.length})
            </div>
          ) : resultTabs.map((t) => (
            <div
              key={t.key}
              className={`dv-tab ${topTab === t.key ? "dv-active" : ""}`}
              onClick={() => setTopTab(t.key)}
            >
              {t.label} ({multiTab ? categoryCounts[t.key] : batchRows.length})
            </div>
          ))}
          {/* Check Points is HIDDEN on a PLM package — legacy's PLM popup has no
              such tab (verified against the running legacy app, user 2026-09-24).
              It costs nothing to hide: dbo.DataPackageCheckList names no PLM test
              even in GoLive, so the tab was always empty, and the Submit gate's
              check-point validation is vacuous with no rows (both checks are
              IF EXISTS in AI_Analysis_Sdms_DataPackage_Transition_SP). An earlier
              version kept it on the theory that a lab might configure one; legacy
              parity wins — if that day comes, legacy would have to grow the tab
              first and we follow it. */}
          {!isPlm && (
            <div className={`dv-tab ${topTab === "checks" ? "dv-active" : ""}`} onClick={() => setTopTab("checks")}>
              Check Points
            </div>
          )}
          {/* Trend — historical context for the batch's sampling locations.
              Read-only, so it is NOT part of the Validate gate (the three
              grids stay the only steps a reviewer must tick). Trend reads the
              SDMS result rows a PLM batch does not have, so it is hidden there. */}
          {!isPlm && (
            <div className={`dv-tab ${topTab === "trend" ? "dv-active" : ""}`} onClick={() => setTopTab("trend")}>
              Trend
            </div>
          )}
        </div>
        {/* dv-trend: the Trend pane is a chart + summary table, not a scrolling
            grid, so the card grows to its natural height instead of clipping at
            the grid tabs' 380px .dv-scroll cap (which hid the legend, summary
            table and footnote entirely — and the chart's own axis labels). */}
        <div className={`dv-card dv-up${topTab === "trend" ? " dv-trend" : ""}`}>
          {/* One shared results pane serves all four category tabs — same grid,
              search, filters, sort and selection; only the rows differ (narrowed
              to the active category by `categoryResults`). */}
          {/* PLM Results — legacy PLMStereoscopicObservation_ListView_PLMDPPreview,
              the same 12 columns in the same order, ordered by QCBatchSequence.Sort
              (the SP does the sorting). Read-only, as that view is. Dates go through
              the shared fmtLocal* helpers (§14b.6) — AnalyzedDate is a pure date in
              legacy, SubmittedDate carries a time. */}
          {isPlm && topTab === "plm" && (
            <div className="dv-pane dv-on">
              <div className="dv-mini">
                <button className="dv-rf" onClick={() => { void reloadPlm(); }} disabled={plmLoading}>
                  ↻ {plmLoading ? "Loading…" : "Refresh"}
                </button>
              </div>
              <div className="dv-scroll">
                <table className="dv-tbl">
                  <thead>
                    <tr>
                      <th className="dv-chk">
                        <input
                          type="checkbox"
                          checked={plmAllChecked}
                          disabled={plmRows.length === 0}
                          onChange={() => {
                            const on = !plmAllChecked;
                            // Select-all must not tick rows the user may not review —
                            // legacy unselects them again the moment it happens, which
                            // reads as the grid fighting you. Skip them outright and
                            // say so ONCE, not once per row.
                            const allowed = plmRows.filter((r) => r.canReview);
                            for (const r of allowed) brToggle.onToggleRow(r.sequenceOid, on);
                            if (on && allowed.length !== plmRows.length) {
                              notify.warning(plmLockedMsg);
                            }
                          }}
                          aria-label={plmAllChecked ? "Clear selection" : "Select all rows"}
                        />
                      </th>
                      <th>JobID</th><th>SampleID</th><th>SampleLayer</th><th>SampleName</th>
                      <th>AsbestosFound</th><th>FiberObservation</th><th>Status</th>
                      <th>Material</th><th>AnalyzedBy</th><th>AnalyzedDate</th>
                      <th>SubmittedBy</th><th>SubmittedDate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plmRows.length === 0 && !plmLoading && (
                      <tr><td colSpan={13} style={{ textAlign: "center", padding: 16 }}>
                        No layers in this batch.
                      </td></tr>
                    )}
                    {plmRows.map((r) => (
                      <tr key={r.sequenceOid}>
                        <td className="dv-chk">
                          <input
                            type="checkbox"
                            checked={brChecked.has(r.sequenceOid)}
                            disabled={!r.canReview}
                            onChange={(e) => {
                              // Legacy UNSELECTS a disabled row and explains why
                              // (DataPackageViewController :1544 for the PLM grid).
                              // The checkbox is disabled so this is belt-and-braces —
                              // but a keyboard Space or a programmatic tick would
                              // otherwise slip past the visual state.
                              if (!r.canReview) {
                                notify.warning(plmLockedMsg);
                                return;
                              }
                              brToggle.onToggleRow(r.sequenceOid, e.target.checked);
                            }}
                            title={r.canReview ? undefined : plmLockedMsg}
                            aria-label={`Select ${r.sampleLayer ?? "row"}`}
                          />
                        </td>
                        <td>{r.jobId ?? ""}</td>
                        <td>{r.sampleId ?? ""}</td>
                        <td>{r.sampleLayer ?? ""}</td>
                        <td>{r.sampleName ?? ""}</td>
                        <td style={{ textAlign: "center" }}>
                          <input type="checkbox" checked={r.asbestosFound} readOnly disabled
                                 aria-label={`Asbestos found on ${r.sampleLayer ?? "this layer"}`} />
                        </td>
                        <td>{r.fiberObservation ?? ""}</td>
                        <td>{r.statusText ?? ""}</td>
                        <td>{r.material ?? ""}</td>
                        <td>{r.analyzedBy ?? ""}</td>
                        <td>{r.analyzedDate ? fmtLocalDate(r.analyzedDate) : ""}</td>
                        {/* SUBMIT popup preview, as the SDMS grids do (OG SelectionChanged; owner
                            2026-10-20 "when I select the checkbox it should auto fill current user
                            and date"): a ticked row shows the current user + the tick time until
                            the real Submit stores them; otherwise the stored value. */}
                        <td>
                          {submitMode && brChecked.has(r.sequenceOid) && !r.submittedBy
                            ? currentUserName
                            : r.submittedBy ?? ""}
                        </td>
                        <td>
                          {submitMode && brChecked.has(r.sequenceOid) && !r.submittedDate
                            ? fmtLocalDateTime(brChecked.get(r.sequenceOid)!)
                            : r.submittedDate ? fmtLocalDateTime(r.submittedDate) : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!isPlm && isCategoryTab(topTab) && (
            <div className="dv-pane dv-on">
              <div className="dv-mini">
                <button
                  className="dv-rf"
                  onClick={() => {
                    onRefresh();
                    void brReload();
                  }}
                  disabled={brLoading}
                >
                  ↻ {brLoading ? "Loading…" : "Refresh"}
                </button>
                <span className="dv-srch">
                  <i className="ti ti-search" />
                  <input value={resultsQ} onChange={(e) => setResultsQ(e.target.value)} placeholder="Search…" />
                </span>
                <span className="dv-gap" />
                {/* Only the error condition is user-facing — successful live
                    fetches and demo-seed fallbacks render silently. */}
                {brError && (
                  <span className="dv-note" style={{ color: "var(--ai-red)" }}>
                    Failed to load: {brError}
                  </span>
                )}
                {gridActions}
              </div>
              <div className="dv-scroll">
                <BatchResultsTable
                  cat={activeCategory}
                  rows={categoryResults}
                  batch={batch}
                  approval={approval}
                  showApprovedCols={showApprovedCols}
                  multiTab={multiTab}
                  currentUserName={currentUserName}
                  submitMode={submitMode}
                  locked={dpLocked || brReadOnly}
                  rawData={rawData}
                  checkedKeys={brChecked}
                  onToggleRow={brToggle.onToggleRow}
                  onToggleAll={brToggle.onToggleAll}
                  filters={brFilters}
                  onFiltersChange={setBrFilters}
                  fmt={BR_FMT}
                  dropdownRowsFor={brDropdownRowsFor}
                  sorter={brSorter}
                  enteredByRoster={enteredByOptions}
                  brOverrides={brOverrides}
                  onBrChange={(rowKey, col, value) =>
                    setBrOverrides((prev) => ({
                      ...prev,
                      [rowKey]: { ...(prev[rowKey] ?? {}), [col]: value },
                    }))
                  }
                  canSeeForm={canSeeForm}
                />
              </div>
              <div className="dv-foot">
                <span>Page 1 of 1 ({categoryResults.length} items)</span>
                <span className="dv-psz">
                  Page size{" "}
                  <select defaultValue={200}>
                    <option>20</option>
                    <option>50</option>
                    <option>100</option>
                    <option>200</option>
                  </select>
                </span>
              </div>
            </div>
          )}
          {topTab === "checks" && (
            <div className="dv-pane dv-on">
              <div className="dv-mini">
                <button
                  className="dv-rf"
                  onClick={() => {
                    onRefresh();
                    void cpReload();
                  }}
                  disabled={cpLoading}
                >
                  ↻ {cpLoading ? "Loading…" : "Refresh"}
                </button>
                <span className="dv-srch">
                  <i className="ti ti-search" />
                  <input value={checksQ} onChange={(e) => setChecksQ(e.target.value)} placeholder="Search…" />
                </span>
                <span className="dv-gap" />
                {cpError && (
                  <span className="dv-note" style={{ color: "var(--ai-red)" }}>
                    Failed to load: {cpError}
                  </span>
                )}
                {gridActions}
              </div>
              <div className="dv-scroll">
                <CheckPointsTable
                  rows={visibleChecks}
                  checkedKeys={cpChecked}
                  locked={dpLocked}
                  onToggleRow={cpToggle.onToggleRow}
                  onToggleAll={cpToggle.onToggleAll}
                  filters={cpFilters}
                  onFiltersChange={setCpFilters}
                  fmt={CP_FMT}
                  dropdownRowsFor={cpDropdownRowsFor}
                  sorter={cpSorter}
                  qualOverrides={qualOverrides}
                  onQualifierClick={(rowKey, value) => setQualPicker({ rowKey, value })}
                  commentOverrides={commentOverrides}
                  onCommentChange={(rowKey, value) => setCommentOverrides((prev) => ({ ...prev, [rowKey]: value }))}
                  decisionOverrides={decisionOverrides}
                  onDecisionChange={(rowKey, decision) =>
                    setDecisionOverrides((prev) => ({ ...prev, [rowKey]: decision }))
                  }
                />
              </div>
              <div className="dv-foot">
                <span>Page 1 of 1 ({visibleChecks.length} items)</span>
                <span className="dv-psz">
                  Page size{" "}
                  <select defaultValue={100}>
                    <option>20</option>
                    <option>50</option>
                    <option>100</option>
                  </select>
                </span>
              </div>
            </div>
          )}
          {topTab === "trend" && (
            <SdmsTrendTab
              batchOid={batch.id}
              testName={batch.test}
              focusSite={trendFocus.site}
              focusAnalyte={trendFocus.analyte}
              hasSelection={trendFocus.hasSelection}
              selectedSites={trendFocus.sites}
              selectedAnalytes={trendFocus.analytes}
              sitelessSelected={trendFocus.sitelessSelected}
              batchParameters={batchParameters}
              qualitative={isQualitativeBatch}
              actions={gridActions}
              onExportData={setTrendExport}
            />
          )}
        </div>

        {/* ── Bottom tabs: Review Log · Attachments ───────────────── */}
        <div className="dv-tabs dv-tabs-lo" style={{ marginTop: 12 }}>
          <div className={`dv-tab ${loTab === "review" ? "dv-active" : ""}`} onClick={() => setLoTab("review")}>
            Review Log
          </div>
          <div className={`dv-tab ${loTab === "attach" ? "dv-active" : ""}`} onClick={() => setLoTab("attach")}>
            Attachments
          </div>
        </div>
        <div className="dv-card dv-lo">
          {loTab === "review" && (
            <div className="dv-pane dv-on">
              <div className="dv-mini">
                <button
                  className="dv-rf"
                  onClick={() => {
                    onRefresh();
                    void rlReload();
                  }}
                  disabled={rlLoading}
                >
                  ↻ {rlLoading ? "Loading…" : "Refresh"}
                </button>
                {rlError && (
                  <span className="dv-note" style={{ color: "var(--ai-red)", marginLeft: 10 }}>
                    Failed to load: {rlError}
                  </span>
                )}
                <span className="dv-gap" />
                {gridActionsFor("lo", loExport)}
              </div>
              <div className="dv-scroll">
                <ReviewLogTable
                  hideSignature={isPlm}
                  rows={visibleReview}
                  batchLabel={batch.batchId ?? ""}
                  checkedKeys={rlChecked}
                  /* onToggleAll removed — ALPACA has no header select-all here. */
                  onToggleRow={(key, checked) => {
                    // ALPACA parity (DataPackageViewController / DataReviewController
                    // Grid_SelectionChanged): ticking ANY Review Log row requires at
                    // least one Batch-Results sample selected — else pop a CENTERED
                    // dialog "Select atleast one sample." (client 2026-08-04 "needs to
                    // show as center popup") and REVERT the tick (don't check the box,
                    // don't stamp). notify.warning auto-routes to the shared Tier-1
                    // centered modal (§18.6).
                    if (checked && brChecked.size === 0) {
                      notify.warning("Select atleast one sample.");
                      return;
                    }
                    // Keep the "step reviewed" gate (rlChecked drives mark.rl).
                    rlToggle.onToggleRow(key, checked);
                    // ALPACA parity (Grid_SelectionChanged foreach, lines 1688-1705):
                    // the handler loops EVERY Review Log row against the WHOLE current
                    // selection, not just the toggled one. A selected row is stamped
                    // Operator→signer, DateTime→now, DecisionStatus→Accept; a deselected
                    // *Pending* row is cleared (Operator/Date nulled, legacy leaves a
                    // signed row alone); and ReviewScope is set on ALL rows — All Results
                    // when every sample is ticked, else Selected Results. This lets the
                    // auto-fill overwrite another row's signer, exactly as OG's
                    // ObjectSpace.CommitChanges persists every dirtied row. Compute the
                    // post-toggle checked set locally — setRlChecked above hasn't flushed.
                    const nextChecked = new Set(rlChecked.keys());
                    if (checked) nextChecked.add(key);
                    else nextChecked.delete(key);
                    const total = batchRows.length;
                    const picked = batchRows.filter((r) => brChecked.has(batchResultRowKey(r))).length;
                    const scope = total > 0 && picked >= total ? "All Results" : "Selected Results";
                    setRlOverrides((prev) => {
                      const next: Record<string, Record<string, string>> = { ...prev };
                      for (const r of rlRows) {
                        const cell = { ...(next[r.id] ?? {}) };
                        if (nextChecked.has(r.id)) {
                          cell.decisionStatus = "Accept";
                          cell.operatorName = currentUserName;
                          // User = the signer CODE (Employee.DisplayName), the value the
                          // User column shows once saved — previewed on tick like DateTime
                          // (G6 feedback 2026-10-15, Bugs 2/3: both stayed blank).
                          cell.user = currentUserName;
                          cell.date = nowClientWire();
                          if (currentUserRole) cell.role = currentUserRole;
                        } else if ((r.status ?? "") === "Pending") {
                          delete cell.operatorName;
                          delete cell.user;
                          delete cell.date;
                          delete cell.role;
                        }
                        cell.reviewScope = scope;
                        next[r.id] = cell;
                      }
                      return next;
                    });
                  }}
                  filters={rlFilters}
                  onFiltersChange={setRlFilters}
                  fmt={RL_FMT}
                  dropdownRowsFor={rlDropdownRowsFor}
                  sorter={rlSorter}
                  rlOverrides={rlOverrides}
                  onRlChange={(rowKey, col, value) =>
                    setRlOverrides((prev) => ({
                      ...prev,
                      [rowKey]: { ...(prev[rowKey] ?? {}), [col]: value },
                    }))
                  }
                />
              </div>
            </div>
          )}
          {loTab === "attach" && (
            <div className="dv-pane dv-on">
              <div className="dv-mini">
                <button
                  className="dv-rf"
                  onClick={() => {
                    onRefresh();
                    void atReload();
                  }}
                  disabled={atLoading}
                >
                  ↻ {atLoading ? "Loading…" : "Refresh"}
                </button>
                {/* "New", legacy's caption — it opens the Data Package Attachments
                    dialog rather than going straight to a file picker. */}
                {atCanModify && (
                  <button className="dv-rf" disabled={atBusy} onClick={openAtNew}
                          title="Add an attachment to this data package">
                    <i className="ti ti-paperclip" /> {atBusy ? "Uploading…" : "New"}
                  </button>
                )}
                {/* Delete — legacy's nested-grid Delete, acting on the TICKED rows.
                    The per-row trash icon stays for a single quick removal; this is
                    what makes the action discoverable at all (a per-row icon is
                    invisible while the grid is empty). Disabled, not hidden, when
                    nothing is ticked, so its existence is never in doubt. */}
                {atCanModify && (
                  <button
                    className="dv-rf"
                    disabled={atSel.size !== 1}
                    title={atSel.size === 1
                      ? "Rename or re-categorise the selected attachment"
                      : "Tick exactly one attachment to edit it"}
                    onClick={() => {
                      const id = [...atSel][0];
                      const row = atRows.find((r) => r.id === id);
                      if (row) openAtEdit(row);
                    }}
                  >
                    <i className="ti ti-pencil" /> Edit
                  </button>
                )}
                {atCanModify && (
                  <button
                    className="dv-rf"
                    disabled={atSel.size === 0}
                    title={atSel.size === 0
                      ? "Tick an attachment to delete it"
                      : `Delete ${atSel.size} selected attachment${atSel.size === 1 ? "" : "s"}`}
                    onClick={() => { void onDeleteSelectedAttachments(); }}
                  >
                    <i className="ti ti-trash" /> Delete
                  </button>
                )}
                <span className="dv-gap" />
                {atError && (
                  <span className="dv-note" style={{ color: "var(--ai-red)", marginLeft: 10 }}>
                    Failed to load: {atError}
                  </span>
                )}
                {gridActionsFor("lo", loExport)}
              </div>
              <div className="dv-scroll">
                <AttachmentsTable
                  rows={visibleAttachments}
                  filters={atFilters}
                  onFiltersChange={setAtFilters}
                  fmt={AT_FMT}
                  dropdownRowsFor={atDropdownRowsFor}
                  sorter={atSorter}
                  canModify={atCanModify}
                  onDelete={onDeleteAttachment}
                  selectedIds={atSel}
                  onToggleSelect={atToggle}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attachment NEW — legacy's "Data Package Attachments" dialog: Name* ·
          Category · Attachment* (Browse…) on the left, Date · Operator on the
          right. The two right-hand fields are shown READ-ONLY because the server
          stamps them on create — they record who attached the file and when, so
          offering them for entry would invite rewriting the provenance. */}
      {atNew && (
        <AppDialog
          open
          title="Data Package Attachments"
          icon="ti-paperclip"
          width={720}
          onClose={() => setAtNew(false)}
          footer={
            <>
              <button className="ca-btn" onClick={() => setAtNew(false)} disabled={atBusy}>Cancel</button>
              <button className="ca-btn ca-btn-solid" onClick={() => { void saveAtNew(); }} disabled={atBusy}>
                {atBusy ? "Saving…" : "OK"}
              </button>
            </>
          }
        >
          <div className="dv-att-new">
            <div className="dv-att-grid">
              <label className="dv-req">Name:</label>
              <input className="dv-edit-txt" value={atNewName} placeholder="Attachment name"
                     onChange={(e) => setAtNewName(e.target.value)} />
              <label>Category:</label>
              <select className="dv-edit-txt" value={atNewCat}
                      onChange={(e) => setAtNewCat(e.target.value)}>
                <option value="">N/A</option>
                {atOpts.categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name ?? ""}</option>
                ))}
              </select>
              <label className="dv-req">Attachment:</label>
              <div className="dv-att-file">
                <div className="dv-fld" title={atNewFile?.name || undefined}>
                  <span>{atNewFile?.name || ""}</span>
                </div>
                {/* Clear — once a file is picked there was no way to UNpick it: the
                    only escape was Cancel and reopening, losing the Name and Category
                    already typed (user request 2026-09-24). Hidden until there is
                    something to clear, so it never reads as a third action. */}
                {atNewFile && (
                  <button
                    type="button"
                    className="ca-btn"
                    title="Remove the chosen file"
                    onClick={() => setAtNewFile(null)}
                  >
                    Clear
                  </button>
                )}
                <label className="ca-btn" style={{ cursor: "pointer" }}>
                  Browse…
                  <input
                    type="file"
                    hidden
                    onChange={(e) => {
                      // Read the File out BEFORE clearing the input: `files` is a
                      // LIVE list owned by it, so resetting .value empties the very
                      // reference we just took (the bug that made the old Add button
                      // do nothing at all — user report 2026-09-24).
                      const f = e.target.files?.[0] ?? null;
                      e.currentTarget.value = "";
                      setAtNewFile(f);
                      // Legacy pre-fills Name from the file, still editable.
                      if (f && !atNewName.trim()) setAtNewName(f.name);
                    }}
                  />
                </label>
              </div>
            </div>
            <div className="dv-att-grid">
              <label>Date:</label>
              <div className="dv-fld">{fmtLocalDate(nowClientWire()) || ""}</div>
              <label>Operator:</label>
              <select className="dv-edit-txt" value={atNewOp}
                      onChange={(e) => setAtNewOp(e.target.value)}>
                <option value="">N/A</option>
                {atOpts.operators.map((o) => (
                  <option key={o.id} value={o.id}>{o.name ?? ""}</option>
                ))}
              </select>
            </div>
          </div>
        </AppDialog>
      )}

      {/* Attachment Edit — OG's nested-grid Edit (DataPackageAttachments_DetailView,
          Name · Category · Attachment | Date · Operator). Two fields only: the blob
          is replaced by an UPLOAD, not an edit, and Date/Operator are the server's
          provenance stamps, so neither is offered here. */}
      {atEdit && (
        <AppDialog
          open
          title="Edit Attachment"
          icon="ti-paperclip"
          width={420}
          onClose={() => setAtEdit(null)}
          footer={
            <>
              <button className="ca-btn" onClick={() => setAtEdit(null)} disabled={atEditBusy}>Cancel</button>
              <button className="ca-btn ca-btn-solid" onClick={() => { void saveAtEdit(); }} disabled={atEditBusy}>
                {atEditBusy ? "Saving…" : "Save"}
              </button>
            </>
          }
        >
          <div className="dv-att-grid">
            <label className="dv-req">Name:</label>
            <input
              className="dv-edit-txt"
              value={atEditName}
              onChange={(e) => setAtEditName(e.target.value)}
              placeholder="Attachment name"
            />
            <label>Category:</label>
            <select className="dv-edit-txt" value={atEditCat}
                    onChange={(e) => setAtEditCat(e.target.value)}>
              <option value="">N/A</option>
              {atOpts.categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? ""}</option>
              ))}
            </select>
            <label>Operator:</label>
            <select className="dv-edit-txt" value={atEditOp}
                    onChange={(e) => setAtEditOp(e.target.value)}>
              <option value="">N/A</option>
              {atOpts.operators.map((o) => (
                <option key={o.id} value={o.id}>{o.name ?? ""}</option>
              ))}
            </select>
            <label>Attachment:</label>
            <div className="dv-fld">{atEdit.file || "—"}</div>
          </div>
        </AppDialog>
      )}

      {/* ── Project Notes popup (CRUD + author-only edit/delete) ── */}
      {notesOpen && (
        <NotesListPopup
          batchId={batch.id}
          batchTest={batch.test ?? ""}
          batchLabel={batch.batchId ?? "—"}
          onClose={() => setNotesOpen(false)}
          // useNotesCount listens to the same module-level store, so this
          // callback is a no-op for the badge — kept for explicit signalling.
          onChanged={() => {
            /* badge re-renders via useNotesCount */
          }}
        />
      )}

      {/* ── Check Points Qualifier picker (ALPACA Qualifiers_ListView_
            DataPackage): opened from a Qualifier cell; OK writes the chosen
            Symbols (comma-joined) back into that row's local override. ── */}
      {qualPicker && (
        <SdmsQualifierPicker
          initial={qualPicker.value}
          onOk={(symbols) => {
            setQualOverrides((prev) => ({ ...prev, [qualPicker.rowKey]: symbols }));
            // Selecting a qualifier is a Check-Points edit → count that row as
            // reviewed (same live-checked-set the row checkbox uses).
            cpToggle.onToggleRow(qualPicker.rowKey, true);
            setQualPicker(null);
          }}
          onCancel={() => setQualPicker(null)}
        />
      )}

      {/* ── Per-batch Audit Trail popup (Date / User / Op / Property /
            Old → New / Reason; grid + timeline views; reads dbo.AuditData
            via /api/v1/audit/trail).  Scoped to this batch's Oid. ── */}
      {auditOpen && auditAllowed && (
        <AuditTrailPopup
          formName={approval ? "Result Approval" : "SDMS Result Validation"}
          sourceId={batch.id}
          onClose={() => setAuditOpen(false)}
        />
      )}

      {/* ── SDMS Excel-style spreadsheet popup ──────────────────── */}
      {sdmsOpen && (
        <SdmsSpreadsheet
          batch={batch}
          allBatches={allBatches}
          dfOverlay={sheetDfOverlay}
          /* Windowed = inside the centered Data Package modal; open the sheet full-screen
             so it isn't clipped to the modal box (user UI feedback 2026-09-16). */
          startFullscreen={windowed}
          onClose={() => setSdmsOpen(false)}
          onSaved={() => {
            // Sheet Update writes the edited cells to SpreadSheetEntry_SampleInfo,
            // the table this grid reads — re-pull so it never shows the pre-edit row.
            void brReload();
          }}
          onRolledBack={() => {
            // The Rollback ribbon action mutates the row server-side; tell
            // the list view to re-pull authoritative state and close the
            // package detail so the user is back at the list.
            onRefresh();
            onClose();
          }}
        />
      )}

      {/* "QC Batches" opens THIS batch's QC Batch detail as a modal popup over the
          data-package page, keeping it mounted behind (user request 2026-10-30). */}
      {qcBatchOpen && (
        <QcBatchModal batchOid={batch.id} batchId={batch.batchId} onClose={() => setQcBatchOpen(false)} />
      )}
    </div>
  );
  // OG opens the Data Package as a modal window (TargetWindow.NewModalWindow). The SDMS
  // Result Entry ribbon passes `windowed` → render as a centered popup over a dimmed
  // backdrop, portalled to <body> so it escapes the entry page's layout. Result Validation
  // (no `windowed`) keeps the full-content overlay above.
  // The windowed Data Package popup FILLS the content area (not portalled to <body>,
  // not a floating modal) — like the full-content SDMS Result Validation detail — so
  // the sidebar + top bar stay visible instead of being covered (user report
  // 2026-10-27). .dv-ov--popup fills via the base .dv-ov position:absolute; inset:0.
  return tree;
}

// ── Batch Results table ──────────────────────────────────────────────────
// Column order + set is a 1:1 port of the legacy XAF
// `SpreadSheetEntry_ListView_Copy_DataReview` (Model.xafml lines
// 59505-59550), columns at indices 0-35:
//
//   0  SysSampleCode      1  Parameter       2  NumericResult
//   3  Result             4  Units           5  AnalyzedDate
//   6  AnalyzedBy         7  SubmittedDate   8  SubmittedBy
//   9  ValidatedDate     10  ValidatedBy    11  ApprovedDate
//  12  ApprovedBy        13  DateCollected  14  Collector
//  15  DateReceived      16  SampleName     17  SiteName
//  18  Client            19  ProjectID      20  DF
//  21  RPTLIMIT          22  MDL            23  UQL
//  24  MCL               25  LOQ            26  SpikeAmount
//  27  DLD               28  RPD            29  RPDCtrlLimit
//  30  %Rec              31  RecCtrlLimit   32  Qualifier
//  33  QCStatus          34  EnteredBy      35  EnteredDate
// ── Stable header cells (shared by all three grids on this page) ─────────────
// A FRESH `H`/`Th` function identity on every render was remounting the
// ColFilterButton inside each header, destroying its open/closed state — so the
// column-filter popup CLOSED the instant you picked a value (client feedback
// 2026-08-01: "the filter does not stay when you make a change"; Custom
// Reporting, which renders headers through the stable AppDataGrid, doesn't have
// this). This hook returns MEMOIZED (stable-identity) H/Th, so React reconciles
// the header instead of remounting it and the popup stays open across the
// filtered reload. The latest per-render deps are read via a ref so the stable
// components still see the current filters/sort.
// Monotonic id for a per-grid CSS scope class (see the body-hide <style> below).
let dvcmSeq = 0;

function useStableHeaderCells<T>(deps: {
  sorter: UseSortApi<T>;
  filters: Record<string, ColumnFilterValue>;
  onFiltersChange: (u: (p: Record<string, ColumnFilterValue>) => Record<string, ColumnFilterValue>) => void;
  fmt: Record<string, (r: T) => string>;
  dropdownRowsFor: (key: string) => T[];
}, persistKey?: string, opts?: { reorderable?: boolean }) {
  const ref = useRef(deps);
  ref.current = deps;
  // Column REORDER (drag a header onto another) is OPT-IN per grid because it
  // requires the grid to array-drive its cells (build keyed header/body maps and
  // render `applyOrder(ids).map(...)`). Only Batch Results does that today; the
  // other SDMS detail grids render Th's in fixed JSX order, so their headers stay
  // non-draggable (reorderable=false → Th omits the drag handlers).
  const reorderable = opts?.reorderable ?? false;
  const reorderableRef = useRef(reorderable);
  reorderableRef.current = reorderable;
  // Right-click header context menu (Sort · … the global grid features), the SAME
  // <HeaderContextMenu> AppDataGrid uses.  State lives here (shared by every Th on
  // the grid); `menuRef` mirrors it so the stable-identity Th can read the current
  // value without breaking its memoisation.  `labelMap` records each column's
  // human label as its <H> renders, so Th (which only carries the id) can title
  // the menu.  All columns on these grids are sortable (Th wires setSort), so the
  // menu's Sort items are always enabled.
  const [menu, setMenu] = useState<{ id: string; label: string; x: number; y: number } | null>(null);
  const menuRef = useRef(menu);
  menuRef.current = menu;
  const labelMap = useRef<Record<string, string>>({});
  const openMenu = useCallback((id: string, label: string, e: React.MouseEvent) => {
    e.preventDefault();
    setMenu({ id, label, x: e.clientX, y: e.clientY });
  }, []);
  const closeMenu = useCallback(() => setMenu(null), []);

  // ── Column management (Hide / Show All / Column Chooser) for these hand-rolled
  // grids ──────────────────────────────────────────────────────────────────────
  // Columns are identified by their stable id (labelMap, filled as each <H>
  // renders). Hiding is ID-BASED (position-independent → the conditional Approved
  // columns are handled): the header <th> hides inline (Th below), and a generated
  // <style> hides the matching BODY <td> by its DOM position — read from the
  // header, where display:none cells still count for :nth-child so the mapping
  // stays stable. Columns are also user-RESIZABLE (a drag handle per header sets
  // the <th> width; the body follows). hidden + widths persist per grid via
  // persistKey. (Column REORDER on these hand-rendered grids would need an
  // array-driven refactor — a per-grid change, not doable in this factory.)
  const saved = useMemo(() => loadGridPrefs(persistKey), [persistKey]);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(saved.hidden ?? []));
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => saved.widths ?? {});
  // Persisted column order (reorderable grids only). null = natural order.
  const [order, setOrder] = useState<string[] | null>(() => (reorderable ? saved.order ?? null : null));
  useEffect(() => {
    // Only WRITE `order` when it's set — saveGridPrefs merges, so passing
    // undefined would clobber a stored order; non-reorderable grids never touch it.
    const patch: Partial<GridPrefs> = { hidden: [...hidden], widths: colWidths };
    if (order) patch.order = order;
    saveGridPrefs(persistKey, patch);
  }, [persistKey, hidden, colWidths, order]);
  const hiddenRef = useRef(hidden); hiddenRef.current = hidden;
  const colWidthsRef = useRef(colWidths); colWidthsRef.current = colWidths;

  // ── Column REORDER (opt-in; Batch Results) ──────────────────────────────────
  // `applyOrder(naturalIds)` returns the ordered id list: the saved order filtered
  // to ids that still exist, then any new ids (e.g. Approved cols toggling on)
  // appended in natural order. It stamps `lastOrderedRef` (read during render) so
  // the drop handler can splice the CURRENT visible order without re-deriving it.
  const lastOrderedRef = useRef<string[]>([]);
  const applyOrder = useCallback(
    (naturalIds: string[]): string[] => {
      let result: string[];
      if (!order || order.length === 0) result = naturalIds.slice();
      else {
        const known = new Set(naturalIds);
        const inOrder = order.filter((id) => known.has(id));
        const missing = naturalIds.filter((id) => !order.includes(id));
        result = [...inOrder, ...missing];
      }
      lastOrderedRef.current = result;
      return result;
    },
    [order],
  );
  const reorderColumns = useCallback((from: string, to: string) => {
    if (!from || from === to) return;
    setOrder(() => {
      const cur = lastOrderedRef.current.slice();
      const fi = cur.indexOf(from);
      if (fi < 0) return cur;
      cur.splice(fi, 1);
      const ti = cur.indexOf(to);
      cur.splice(ti < 0 ? cur.length : ti, 0, from);
      return cur;
    });
  }, []);
  // Native HTML5 drag on a header <th>. A resize in progress suppresses drag
  // (resizingRef, set by startResize) so the two gestures never fight.
  const [dragField, setDragField] = useState<string | null>(null);
  const [dragOverField, setDragOverField] = useState<string | null>(null);
  const dragFieldRef = useRef<string | null>(null); dragFieldRef.current = dragField;
  const resizingRef = useRef(false);
  const dragProps = useCallback(
    (id: string) => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        if (resizingRef.current) { e.preventDefault(); return; }
        // Set the ref SYNCHRONOUSLY (not just via setState) so a drop that fires
        // before the dragstart re-render still sees the source column. The render
        // mirror below keeps it in sync afterwards.
        dragFieldRef.current = id;
        setDragField(id);
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', id); } catch { /* older browsers */ }
      },
      onDragOver: (e: React.DragEvent) => {
        if (!dragFieldRef.current || dragFieldRef.current === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverField(id);
      },
      onDragLeave: () => setDragOverField((f) => (f === id ? null : f)),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        // Prefer the live ref; fall back to the drag payload (set in onDragStart)
        // so the drop is robust to render timing.
        const from = dragFieldRef.current || (() => { try { return e.dataTransfer.getData('text/plain') || null; } catch { return null; } })();
        if (from) reorderColumns(from, id);
        dragFieldRef.current = null;
        setDragField(null);
        setDragOverField(null);
      },
      onDragEnd: () => { dragFieldRef.current = null; setDragField(null); setDragOverField(null); },
    }),
    [reorderColumns],
  );
  const dragClass = useCallback(
    (id: string) => {
      let c = '';
      if (dragField === id) c += ' adg-th-dragging';
      if (dragOverField === id && dragField && dragField !== id) c += ' adg-th-dropbefore';
      return c;
    },
    [dragField, dragOverField],
  );
  const dragPropsRef = useRef(dragProps); dragPropsRef.current = dragProps;
  const dragClassRef = useRef(dragClass); dragClassRef.current = dragClass;
  // Drag-to-resize: the handle sets the header <th> width (each .dv-tbl th is
  // position:sticky, so the absolute .adg-resize handle anchors to its edge). An
  // auto-layout table's body columns follow the header width; a fixed-layout one
  // takes it directly. stopPropagation keeps the handle's mousedown from firing
  // the header's sort onClick.
  const startResize = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const th = (e.currentTarget as HTMLElement).closest('th') as HTMLElement | null;
    if (!th) return;
    resizingRef.current = true;   // suppress the header's HTML5 drag while resizing
    const startX = e.clientX;
    const startW = th.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) =>
      setColWidths((prev) => ({ ...prev, [id]: Math.max(50, startW + (ev.clientX - startX)) }));
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Clear on the next tick so the click/dragend that ends the resize doesn't
      // immediately arm a drag.
      window.setTimeout(() => { resizingRef.current = false; }, 0);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  const startResizeRef = useRef(startResize);
  startResizeRef.current = startResize;
  const [chooserAt, setChooserAt] = useState<{ x: number; y: number } | null>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const tableClassRef = useRef<string | null>(null);
  if (tableClassRef.current === null) tableClassRef.current = `dvcm-${++dvcmSeq}`;
  const tableClass = tableClassRef.current;

  // ≥1 column stays visible; counts are computed from labelMap (all column ids).
  const visCount = (h: Set<string>) => Object.keys(labelMap.current).filter((k) => !h.has(k)).length;
  const colHandlers: HeaderMenuColumns = {
    hideColumn: (id) => setHidden((prev) => (visCount(prev) <= 1 ? prev : new Set(prev).add(id))),
    showAllColumns: () => setHidden(new Set()),
    hasHidden: hidden.size > 0,
    canHide: () => visCount(hiddenRef.current) > 1,
    openChooser: (x, y) => setChooserAt({ x, y }),
  };
  const colHandlersRef = useRef(colHandlers);
  colHandlersRef.current = colHandlers;

  // Generate the body-cell hide CSS from the LIVE header positions and push it to
  // a per-grid <style> in <head> (imperative → no JSX wrapper needed, so the grid
  // only edits its <table> tag). Runs every render (cheap — header cells only) so
  // a column-set change (approval toggles the Approved cols) or a hide/show
  // re-maps correctly.
  useLayoutEffect(() => {
    const table = tableRef.current;
    const ths = table
      ? (Array.from(table.querySelectorAll(':scope > thead > tr:first-child > th')) as HTMLElement[])
      : [];
    const sel: string[] = [];
    ths.forEach((th, i) => {
      const id = th.getAttribute('data-col');
      if (id && hiddenRef.current.has(id)) sel.push(`.${tableClass}>tbody>tr>td:nth-child(${i + 1})`);
    });
    const css = sel.length ? `${sel.join(',')}{display:none!important}` : '';
    const styleId = `style-${tableClass}`;
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (css) {
      if (!el) { el = document.createElement('style'); el.id = styleId; document.head.appendChild(el); }
      if (el.textContent !== css) el.textContent = css;
    } else if (el) {
      el.remove();
    }
  });
  // Remove the per-grid <style> when the grid unmounts.
  useEffect(() => () => { document.getElementById(`style-${tableClass}`)?.remove(); }, [tableClass]);

  const H = useMemo(
    () =>
      function H({ id, label, align }: { id: string; label: string; align?: "left" | "center" }) {
        const { sorter, filters, onFiltersChange, fmt, dropdownRowsFor } = ref.current;
        labelMap.current[id] = label;   // so the right-click menu can title itself
        return (
          <span className="dv-h" style={align === "center" ? { justifyContent: "center" } : undefined}>
            <SortableHeader label={label} sortKey={id} sort={sorter.sort} />
            <span onClick={(e) => e.stopPropagation()}>
              <ColFilterButton<T>
                variant="inline"
                col={id}
                label={label}
                rows={dropdownRowsFor(id)}
                formatter={(r) => fmt[id]?.(r) ?? ""}
                value={filters[id] ?? null}
                onChange={(v) => onFiltersChange((p) => ({ ...p, [id]: v }))}
                sortDir={sorter.sort.key === id ? sorter.sort.dir : null}
              />
            </span>
          </span>
        );
      },
    [],
  );
  const Th = useMemo(
    () =>
      function Th({
        id,
        children,
        className,
        style,
        pinned,
        label,
        sortable = true,
      }: {
        id: string;
        children: React.ReactNode;
        className?: string;
        style?: React.CSSProperties;
        // Frozen column (checkbox / SysSampleCode): never drag-reorderable, so it
        // stays anchored while the rest of the grid rearranges.
        pinned?: boolean;
        // Column label for the right-click menu title + the Column Chooser catalog.
        // `<H>` registers this automatically; a Th whose children are NOT `<H>`
        // (a bare SortableHeader, an action column, a plain-text header) MUST pass
        // `label` so it still appears in the chooser and is hideable.
        label?: string;
        // false → an action column (e.g. RawData): clicking the header does NOT
        // sort, and the menu's Sort items are disabled. Hide / Chooser / drag /
        // resize still work.
        sortable?: boolean;
      }) {
        const active = menuRef.current;
        const isHidden = hiddenRef.current.has(id);
        const w = colWidthsRef.current[id];
        // Register the label so this column shows in the menu title + Column Chooser
        // even without an `<H>` child (mirrors what `<H>` does).
        if (label !== undefined) labelMap.current[id] = label;
        // Drag-reorder is opt-in (reorderable grids) and never on a pinned column.
        const canDrag = reorderableRef.current && !pinned;
        const drag = canDrag ? dragPropsRef.current(id) : null;
        const dragCls = canDrag ? dragClassRef.current(id) : "";
        return (
          <th
            data-col={id}
            className={`${className ?? ""}${dragCls}`}
            // Resize width (+ hide) win over any caller style; sticky/position from CSS.
            style={{ cursor: "pointer", ...style, ...(w ? { width: w, minWidth: w } : {}), ...(isHidden ? { display: "none" } : {}) }}
            onClick={sortable ? () => ref.current.sorter.setSort(id) : undefined}
            // Right-click → the shared header menu (Sort · Hide · Show All ·
            // Column Chooser); preventDefault (in openMenu) suppresses the native menu.
            onContextMenu={(e) => openMenu(id, labelMap.current[id] ?? id, e)}
            {...(drag ?? {})}
          >
            {children}
            {/* Drag handle — every column is resizable (shared .adg-resize). */}
            <span className="adg-resize" onMouseDown={(e) => startResizeRef.current(id, e)} onClick={(e) => e.stopPropagation()} />
            {active?.id === id && createPortal(
              <HeaderContextMenu
                column={{ field: id, label: active.label, sortable }}
                sort={ref.current.sorter.sort}
                position={{ x: active.x, y: active.y }}
                onSort={(dir) => ref.current.sorter.setSortExplicit(dir === null ? null : id, dir)}
                onHideColumn={() => colHandlersRef.current.hideColumn(id)}
                canHide={colHandlersRef.current.canHide(id)}
                onShowAllColumns={colHandlersRef.current.showAllColumns}
                hasHiddenColumns={colHandlersRef.current.hasHidden}
                onColumnChooser={() => colHandlersRef.current.openChooser(active.x, active.y)}
                onClose={closeMenu}
              />,
              document.body,
            )}
          </th>
        );
      },
    [openMenu, closeMenu],
  );

  // Portal-only node the grid drops right AFTER its <table> opening tag (a portal
  // renders to <body>, so it needs no wrapping fragment / close-tag edit): the
  // Column Chooser popup. The body-hide CSS is handled imperatively above.
  const columnManager = chooserAt
    ? createPortal(
        <ColumnChooser
          columns={Object.entries(labelMap.current).map(([field, label]) => ({ field, label }))}
          hidden={hidden}
          position={chooserAt}
          onToggle={(id) =>
            setHidden((prev) => {
              const n = new Set(prev);
              if (n.has(id)) n.delete(id);
              else if (visCount(prev) > 1) n.add(id);
              return n;
            })
          }
          onShowAll={() => setHidden(new Set())}
          onClose={() => setChooserAt(null)}
        />,
        document.body,
      )
    : null;

  // colWidthStyle/startResize/dragProps/dragClass are exposed for the few
  // non-<Th> headers a reorderable grid renders by hand (e.g. Batch Results'
  // "RawData" action column) so they get the SAME resize + drag as every Th.
  const colWidthStyle = (id: string): React.CSSProperties => {
    const w = colWidthsRef.current[id];
    return w ? { width: w, minWidth: w } : {};
  };
  return {
    H, Th, tableRef, tableClass, columnManager,
    applyOrder, dragProps, dragClass, startResize, colWidthStyle,
  };
}

function BatchResultsTable({
  cat,
  rows,
  batch,
  approval,
  showApprovedCols,
  multiTab,
  currentUserName,
  submitMode,
  locked,
  rawData,
  checkedKeys,
  onToggleRow,
  onToggleAll,
  filters,
  onFiltersChange,
  fmt,
  dropdownRowsFor,
  sorter,
  enteredByRoster,
  brOverrides,
  onBrChange,
  canSeeForm,
}: {
  // Active category tab — the Sample Results tab hides the QC-only columns.
  cat: ResultCategory;
  rows: BatchResultRow[];
  batch: SdmsPackageRow;
  // Validation vs Approval mode — decides which pair of columns (Validated*
  // vs Approved*) gets the client-side live-tick preview.
  approval: boolean;
  // ApprovedDate / ApprovedBy column visibility — gated on Default Settings ›
  // "SDMS Result Approval" (see showApprovedCols in the parent). Always true on
  // the Approval form.
  showApprovedCols: boolean;
  // Multi-Tab OFF → the single legacy "Batch Results" tab (ALL columns, old order,
  // no per-category hiding); ON → this category tab's filtered column set.
  multiTab: boolean;
  currentUserName: string;
  // DataPackage SUBMIT popup: preview SubmittedDate/By on the CHECKED rows (OG
  // DataPackageViewController SelectionChanged :918-943), the same live-tick the
  // Validated*/Approved* columns already do. False on the Validation/Approval forms.
  submitMode: boolean;
  // Grid is read-only once the package is submitted (dpStatus ≥ 2) — OG makes the
  // DataPackage grid read-only from PendingValidation on. Disables the row + select-all
  // checkboxes and the editable cells.
  locked: boolean;
  // "RawData" Preview column source (dbo.RawDataAttachment) — ALPACA parity.
  rawData: ApiRawDataInfo;
  checkedKeys: Map<string, string>;
  onToggleRow: (key: string, checked: boolean) => void;
  onToggleAll: (keys: string[], checked: boolean) => void;
  filters: Record<string, ColumnFilterValue>;
  onFiltersChange: (u: (p: Record<string, ColumnFilterValue>) => Record<string, ColumnFilterValue>) => void;
  fmt: Record<string, (r: BatchResultRow) => string>;
  dropdownRowsFor: (key: string) => BatchResultRow[];
  sorter: UseSortApi<BatchResultRow>;
  // Full Employee roster for the EnteredBy dropdown (ALPACA parity); empty until fetched.
  enteredByRoster: string[];
  // Editable-cell overrides (RPTLIMIT / MCL / SpikeAmount / DLD / QCStatus only).
  brOverrides: Record<string, Record<string, string>>;
  onBrChange: (rowKey: string, col: string, value: string) => void;
  // View-visibility gate for the PrepBatchID → Sample Prep Batches link (user 2026-10-28).
  canSeeForm: (id: string) => boolean;
}) {
  usePermitRowColors();   // Settings ▸ Permit Row Colors — re-colour the rows when the lab's rules load
  // Stable header cells — see useStableHeaderCells (keeps the filter popup open
  // across the filtered reload, client feedback 2026-08-01).
  const { H, Th, tableRef, tableClass, columnManager, applyOrder, colWidthStyle } =
    useStableHeaderCells<BatchResultRow>(
      { sorter, filters, onFiltersChange, fmt, dropdownRowsFor },
      // Separate saved-order key per layout so a reorder in one mode never
      // scrambles the other (the two modes have different column sets/order).
      multiTab ? "sdms-batchresults" : "sdms-batchresults-legacy",
      { reorderable: true },
    );
  // Stable, content-based row key — module-scoped (batchResultRowKey) so the
  // parent's save-edits handler uses the exact same formula to map an
  // override's rowKey back to the row's real `id` for the API.
  const keyOf = batchResultRowKey;
  const notify = useNotify(); // shared notifications (§18.6)
  // Trend routing — resolve every row's chart ONCE per data change into a map
  // keyed by row (pairing needs the whole batch, so per-cell resolution would be
  // O(n²) — painful on a 600-row VOC batch; guide "Performance"). The cell reads
  // the map O(1). Internal-standard analytes are lab-supplied (never hardcoded);
  // none configured yet, so the set is empty. `micro` is a whole-batch property
  // (one test/method per batch): a micro batch routes its QC rows to the SM 9020 B
  // charts (Wilson's engine) instead of the chemistry ones.
  const isMicro = useMemo(() => isMicroTest(batch.test, batch.method), [batch.test, batch.method]);
  const trendRouteMap = useMemo(
    () => resolveRoutes(rows.map(toTrendRow), { micro: isMicro }),
    [rows, isMicro],
  );
  const [openTrend, setOpenTrend] = useState<
    { route: TrendRoute; label: string; permitLimit: string | null; permitWarn: string | null } | null
  >(null);
  // "Preview" link (RawData column) — ALPACA PreViewRawData_Execute parity:
  // resolve the FileData Oid for this row's sample, then download through the
  // SAME authenticated blob-download path the Attachments tab uses (a plain
  // <a href> would 401 and bounce to /login — the JWT lives in an
  // Authorization header, not a cookie). No match → the exact legacy info
  // message, never a broken/dead link.
  const onPreviewRawData = async (sysSampleCode: string) => {
    const fileOid = resolveRawDataFileOid(rawData, sysSampleCode);
    if (!fileOid) {
      notify.warning("Raw data is not available for this sample.");
      return;
    }
    try {
      // Shared download (features/analysis/rawDataPreview) — same authenticated blob path.
      await downloadRawDataFile(fileOid, rawData.files.find((f) => f.fileOid === fileOid)?.fileName);
    } catch (err) {
      console.warn("[RawData] download failed", fileOid, err);
      notify.error("Failed to download the raw data file. Please try again.");
    }
  };
  // Fallback when a row doesn't carry its own validated/approved stamps
  // (e.g. demo fallback): use the batch-level values from the list endpoint
  // so the column isn't empty for an already-validated batch.
  const validated = isValidated(batch.statusText);
  const batchValDate = validated && batch.validatedDateMax ? fmtLocalDateTime(batch.validatedDateMax) : "";
  const batchValBy = validated ? (batch.validatedByList ?? "") : "";
  // Empty cells render as blank (no em-dash placeholder) — matches the legacy
  // ALPACA Batch Results behaviour where missing columns are left visually empty.
  const blank = (v: string | undefined | null) => (v && v !== "" ? v : "");

  // Permit Limit column width follows its content (RTCFR "Dynamic Permit Limit Column
  // Width", 2026-09-27) — the SAME shared rule as the Express grids' AppDataGrid
  // autoFit (permitLimitColumnWidth: longest limit line or header, clamped to
  // PERMIT_LIMIT_WIDTH, wraps past the max). Re-measured whenever the rendered rows
  // change (load, refresh, filter, sort, another batch). A user-dragged width wins.
  const [gridFont, setGridFont] = useState(DEFAULT_GRID_FONT);
  useLayoutEffect(() => {
    const f = fontOf(tableRef.current);
    setGridFont((prev) => (prev === f ? prev : f));
  }, [tableRef]);
  const permitLimitWidth = useMemo(
    () => permitLimitColumnWidth(rows.flatMap((r) => permitLimitLines(r.permitLimits, r.permitLimit ?? "")), gridFont),
    [rows, gridFont],
  );
  const permitLimitThStyle: React.CSSProperties =
    Object.keys(colWidthStyle("permitLimit")).length > 0
      ? {}
      : { width: permitLimitWidth, minWidth: permitLimitWidth, maxWidth: permitLimitWidth };
  // EnteredBy is an Employee lookup in ALPACA (QCResults.EnteredBy → Employee).
  // Prefer the full Employee roster from the API; fall back to the distinct
  // analyst codes present in THIS batch until the roster loads.
  const enteredByOptions =
    enteredByRoster.length > 0
      ? enteredByRoster
      : [...new Set(rows.map((r) => (r.enteredBy ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  // EnteredDate is a DateEdit in ALPACA (QCResults.EnteredDate → DateTime?).  An
  // <input type=date> needs YYYY-MM-DD; convert MM/DD/YYYY (and pass through ISO).
  const toDateInput = (s: string): string => {
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
    if (us) return `${us[3]}-${us[1]!.padStart(2, "0")}-${us[2]!.padStart(2, "0")}`;
    const iso = /^(\d{4}-\d{2}-\d{2})/.exec(s);
    return iso ? iso[1]! : "";
  };

  // ── Array-driven columns (enables drag-REORDER) ────────────────────────────
  // The checkbox + SysSampleCode stay FROZEN (rendered explicitly, pinned); every
  // other column is an entry keyed by its id. Header and body BOTH render
  // `orderedIds.map(id => …[id])`, so a reorder moves the header and body cell
  // together and the position-based hide/resize keep aligning. `applyOrder` folds
  // in the user's saved order (persistKey "sdms-batchresults"); the Approved pair
  // is present only when the approval stage is on. Natural order = ALPACA's.
  // Column ids + their header labels come from the SHARED catalogue in
  // sdmsGridExport.ts, which the tab-bar Export button reads too — so the file
  // and the header row can never list different columns in a different order.
  // The Sample Results tab omits the QC-only columns (see colsForCategory);
  // every other category tab shows the full catalogue.
  // Multi-Tab ON: this category tab's catalogue minus that tab's hidden columns.
  // Multi-Tab OFF: the single Batch Results tab uses the LEGACY order with ALL
  // columns (no per-category hiding) — the exact pre-split layout (Option A).
  const natIds: string[] = multiTab
    ? colsForCategory(reorderableIds(batchResultColumnCatalog(showApprovedCols), BR_FROZEN_ID), cat)
    : reorderableIds(legacyBatchResultColumnCatalog(showApprovedCols), BR_FROZEN_ID);
  const orderedIds = applyOrder(natIds);

  // Header cells, keyed by column id (row-independent → built once per render).
  const hdrCell: Record<string, React.ReactNode> = {
    param: <Th key="param" id="param"><H id="param" label="Parameter" /></Th>,
    numeric: <Th key="numeric" id="numeric"><H id="numeric" label="NumericResult" /></Th>,
    result: <Th key="result" id="result"><H id="result" label="Result" /></Th>,
    units: <Th key="units" id="units"><H id="units" label="Units" /></Th>,
    analyzedDate: <Th key="analyzedDate" id="analyzedDate"><H id="analyzedDate" label="AnalyzedDate" /></Th>,
    analyzedBy: <Th key="analyzedBy" id="analyzedBy"><H id="analyzedBy" label="AnalyzedBy" /></Th>,
    submittedDate: <Th key="submittedDate" id="submittedDate"><H id="submittedDate" label="SubmittedDate" /></Th>,
    submittedBy: <Th key="submittedBy" id="submittedBy"><H id="submittedBy" label="SubmittedBy" /></Th>,
    validatedDate: <Th key="validatedDate" id="validatedDate"><H id="validatedDate" label="ValidatedDate" /></Th>,
    validatedBy: <Th key="validatedBy" id="validatedBy"><H id="validatedBy" label="ValidatedBy" /></Th>,
    prepBatch: <Th key="prepBatch" id="prepBatch"><H id="prepBatch" label="PrepBatchID" /></Th>,
    // RawData Preview — ALPACA PreViewRawData_Execute (right after SiteName). Not
    // sortable/filterable (an action link), so a <Th sortable={false}> — its header
    // click doesn't sort, but it gets the SAME menu / Hide / Column-Chooser / drag /
    // resize as every other column (`label` registers it in the chooser).
    rawData: (
      <Th key="rawData" id="rawData" label="RawData" sortable={false} style={{ textAlign: "center" }}>
        RawData
      </Th>
    ),
    dateCollected: <Th key="dateCollected" id="dateCollected"><H id="dateCollected" label="DateCollected" /></Th>,
    collector: <Th key="collector" id="collector"><H id="collector" label="Collector" /></Th>,
    dateReceived: <Th key="dateReceived" id="dateReceived"><H id="dateReceived" label="DateReceived" /></Th>,
    sampleName: <Th key="sampleName" id="sampleName"><H id="sampleName" label="SampleName" /></Th>,
    siteName: <Th key="siteName" id="siteName"><H id="siteName" label="SiteName" /></Th>,
    client: <Th key="client" id="client"><H id="client" label="Client" /></Th>,
    projectId: <Th key="projectId" id="projectId"><H id="projectId" label="ProjectID" /></Th>,
    df: <Th key="df" id="df"><H id="df" label="DF" /></Th>,
    trend: (
      <Th key="trend" id="trend" label="Trend" sortable={false} style={{ textAlign: "center" }}>
        Trend
      </Th>
    ),
    rptlimit: <Th key="rptlimit" id="rptlimit"><H id="rptlimit" label="RL" /></Th>,
    ucl: <Th key="ucl" id="ucl"><H id="ucl" label="UCLLimit" /></Th>,
    mdl: <Th key="mdl" id="mdl"><H id="mdl" label="MDL" /></Th>,
    uql: <Th key="uql" id="uql"><H id="uql" label="UQL" /></Th>,
    mcl: <Th key="mcl" id="mcl"><H id="mcl" label="MCL" /></Th>,
    loq: <Th key="loq" id="loq"><H id="loq" label="LOQ" /></Th>,
    spike: <Th key="spike" id="spike"><H id="spike" label="SpikeAmount" /></Th>,
    dld: <Th key="dld" id="dld"><H id="dld" label="DLD" /></Th>,
    rpd: <Th key="rpd" id="rpd"><H id="rpd" label="RPD" /></Th>,
    rpdCtrl: <Th key="rpdCtrl" id="rpdCtrl"><H id="rpdCtrl" label="RPDCtrlLimit" /></Th>,
    rec: <Th key="rec" id="rec"><H id="rec" label="%Rec" /></Th>,
    recCtrl: <Th key="recCtrl" id="recCtrl"><H id="recCtrl" label="RecCtrlLimit" /></Th>,
    qualifier: <Th key="qualifier" id="qualifier"><H id="qualifier" label="Qualifier" /></Th>,
    qc: <Th key="qc" id="qc"><H id="qc" label="QCStatus" /></Th>,
    permitLimit: (
      <Th key="permitLimit" id="permitLimit" style={permitLimitThStyle}>
        <H id="permitLimit" label="PermitLimit" />
      </Th>
    ),
    alert: <Th key="alert" id="alert"><H id="alert" label="Alert" /></Th>,
    enteredBy: <Th key="enteredBy" id="enteredBy"><H id="enteredBy" label="EnteredBy" /></Th>,
    enteredDate: <Th key="enteredDate" id="enteredDate"><H id="enteredDate" label="EnteredDate" /></Th>,
    // Approved* — shown only when the SDMS approval stage is enabled
    // (Default Settings › SDMS Result Approval).
    ...(showApprovedCols
      ? {
          approvedDate: <Th key="approvedDate" id="approvedDate"><H id="approvedDate" label="ApprovedDate" /></Th>,
          approvedBy: <Th key="approvedBy" id="approvedBy"><H id="approvedBy" label="ApprovedBy" /></Th>,
        }
      : {}),
  };

  return (
    <>
    <table ref={tableRef} className={`dv-tbl ${tableClass}`}>
      {columnManager}
      <thead>
        <tr>
          <th className="dv-chk">
            <input
              type="checkbox"
              disabled={locked}
              checked={rows.length > 0 && rows.every((r) => checkedKeys.has(keyOf(r)))}
              onChange={(e) => onToggleAll(rows.map(keyOf), e.target.checked)}
            />
          </th>
          {/* SysSampleCode frozen alongside the checkbox (user request 2026-07-17) —
              pinned: never drag-reorderable, stays anchored on the left. */}
          <Th id="sys" className="dv-sys-col" pinned>
            <H id="sys" label="SysSampleCode" />
          </Th>
          {orderedIds.map((id) => hdrCell[id])}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          // Stable, content-based row key so an edit stays on its row across
          // sorting.  editVal returns the local override (if any) else the value.
          const rowKey = batchResultRowKey(r);
          const editVal = (col: string, raw: unknown) => brOverrides[rowKey]?.[col] ?? blank(raw as never);
          // QCStatus colour — Pass green, Fail red (client feedback 2026-08-01).
          const qcVal = editVal("qc", r.qc);
          // Class name intentionally contains "status" — see the CSS note: that
          // substring is what exempts this cell from blackText.css's !important
          // ink override, so Pass/Fail keep their colour.
          const qcCls = /^pass$/i.test(qcVal.trim())
            ? "dv-qc-status-pass"
            : /^fail$/i.test(qcVal.trim())
              ? "dv-qc-status-fail"
              : "";
          // Body cells keyed by column id — SAME id set + order as the header, so
          // `orderedIds.map` keeps them aligned through a reorder. The live
          // client-side Validated*/Approved* preview (ALPACA parity,
          // DataReviewController.cs SelectionChanged) and the editable cells
          // (RPTLIMIT / MCL / SpikeAmount / DLD / QCStatus / EnteredBy / EnteredDate)
          // are unchanged — only re-homed into the map.
          const cell: Record<string, React.ReactNode> = {
            param: <td key="param">{blank(r.param)}</td>,
            numeric: <td key="numeric">{blank(r.numeric)}</td>,
            result: <td key="result">{blank(r.result)}</td>,
            units: <td key="units">{blank(r.units)}</td>,
            analyzedDate: <td key="analyzedDate">{blank(r.analyzedDate)}</td>,
            analyzedBy: <td key="analyzedBy">{blank(r.analyzedBy)}</td>,
            // SUBMIT popup preview (OG SelectionChanged): a ticked row shows the current
            // user + the tick time until the real submit persists them; else the stored value.
            submittedDate: (
              <td key="submittedDate">
                {submitMode && checkedKeys.has(rowKey) && !r.submittedDate
                  ? fmtLocalDateTime(checkedKeys.get(rowKey)!)
                  : r.submittedDate || ""}
              </td>
            ),
            submittedBy: (
              <td key="submittedBy">
                {submitMode && checkedKeys.has(rowKey) && !r.submittedBy
                  ? currentUserName
                  : r.submittedBy || ""}
              </td>
            ),
            validatedDate: (
              // Not in the DataPackage SUBMIT popup: there a tick is a submit, so only
              // Submitted* preview — Validated* fills on validation (G6 feedback 2026-10-15, Bug 1).
              <td key="validatedDate">
                {!approval && !submitMode && checkedKeys.has(rowKey) && !(r.validatedDate || batchValDate)
                  ? fmtLocalDateTime(checkedKeys.get(rowKey)!)
                  : r.validatedDate || batchValDate || ""}
              </td>
            ),
            validatedBy: (
              <td key="validatedBy">
                {!approval && !submitMode && checkedKeys.has(rowKey) && !(r.validatedBy || batchValBy)
                  ? currentUserName
                  : r.validatedBy || batchValBy || ""}
              </td>
            ),
            // PrepBatchID links to the Sample Prep Batches detail form (user request
            // 2026-10-28, replacing the removed toolbar button). One sample can carry
            // SEVERAL prep batches in a ", "-joined label — each ID is its own link.
            // Gated by canSeeForm('prepbatches'); falls back to plain text otherwise.
            prepBatch: (
              <td key="prepBatch">
                {(() => {
                  const ids = (r.prepBatch ?? "").split(",").map((s) => s.trim()).filter(Boolean);
                  if (ids.length === 0 || !canSeeForm("prepbatches")) return blank(r.prepBatch);
                  return ids.map((pb, i) => (
                    <span key={pb}>
                      {i > 0 && ", "}
                      <a
                        href="#"
                        className="sa-openpkg"
                        title="Open this prep batch in the Sample Prep Batches form"
                        onClick={(e) => { e.preventDefault(); navigateToPage("prepbatches", pb); }}
                      >{pb}</a>
                    </span>
                  ));
                })()}
              </td>
            ),
            dateCollected: <td key="dateCollected">{blank(r.dateCollected)}</td>,
            collector: <td key="collector">{blank(r.collector)}</td>,
            dateReceived: <td key="dateReceived">{blank(r.dateReceived)}</td>,
            sampleName: <td key="sampleName">{blank(r.sampleName)}</td>,
            siteName: <td key="siteName">{blank(r.siteName)}</td>,
            rawData: (
              <td key="rawData" className="dv-raw-cell">
                <a
                  href="#"
                  className="dv-raw-prev"
                  onClick={(e) => {
                    e.preventDefault();
                    void onPreviewRawData(r.sys ?? "");
                  }}
                >
                  Preview
                </a>
              </td>
            ),
            client: <td key="client">{blank(r.client)}</td>,
            projectId: <td key="projectId">{blank(r.projectId)}</td>,
            df: <td key="df" className="dv-numl">{blank(r.df)}</td>,
            // Trend routing ICON — resolves (silently) which chart belongs to this
            // row and opens it. Not text: the eight routing fields drive it.
            trend: (
              <td key="trend" style={{ textAlign: "center" }}>
                <TrendIcon
                  route={trendRouteMap.get(r.id ?? keyOf(r))}
                  onOpen={() => {
                    const route = trendRouteMap.get(r.id ?? keyOf(r));
                    if (route?.routable) {
                      // Thread the row's assigned permit limit (Limit1 → Permit
                      // line) and warning threshold (Limit3 → Warning line) so the
                      // Site trend draws both from the permit (client 2026-09-03).
                      setOpenTrend({
                        route,
                        label: `${r.sys}${r.param ? ` · ${r.param}` : ""}`,
                        permitLimit: r.permitLimit ?? null,
                        permitWarn: r.permitWarn ?? null,
                      });
                    }
                  }}
                />
              </td>
            ),
            rptlimit: (
              <td key="rptlimit" className="dv-edit-cell dv-numl">
                <input
                  className="dv-edit-inp"
                  value={editVal("rptlimit", r.rptlimit)}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "rptlimit", e.target.value)}
                />
              </td>
            ),
            // UCLLimit — read-only blank Upper Control Limit (se.UCL); QC tab only.
            ucl: <td key="ucl" className="dv-numl">{blank(r.ucl)}</td>,
            mdl: <td key="mdl" className="dv-numl">{blank(r.mdl)}</td>,
            uql: <td key="uql" className="dv-numl">{blank(r.uql)}</td>,
            mcl: (
              <td key="mcl" className="dv-edit-cell dv-numl">
                <input
                  className="dv-edit-inp"
                  value={editVal("mcl", r.mcl)}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "mcl", e.target.value)}
                />
              </td>
            ),
            loq: <td key="loq" className="dv-numl">{blank(r.loq)}</td>,
            spike: (
              <td key="spike" className="dv-edit-cell dv-num">
                <input
                  type="number"
                  step="any"
                  className="dv-edit-inp dv-spin"
                  value={editVal("spike", r.spike)}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "spike", e.target.value)}
                />
              </td>
            ),
            dld: (
              <td key="dld" className="dv-edit-cell dv-numl">
                <input
                  className="dv-edit-inp"
                  value={editVal("dld", r.dld)}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "dld", e.target.value)}
                />
              </td>
            ),
            rpd: <td key="rpd" className="dv-numl">{blank(r.rpd)}</td>,
            rpdCtrl: <td key="rpdCtrl" className="dv-numl">{blank(r.rpdCtrl)}</td>,
            rec: <td key="rec" className="dv-numl">{blank(r.rec)}</td>,
            recCtrl: <td key="recCtrl" className="dv-numl">{blank(r.recCtrl)}</td>,
            qualifier: <td key="qualifier">{blank(r.qualifier)}</td>,
            qc: (
              <td key="qc" className={`dv-edit-cell ${qcCls}`}>
                <input
                  className={`dv-edit-inp ${qcCls}`}
                  value={qcVal}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "qc", e.target.value)}
                />
              </td>
            ),
            // Stage 3 (Permit-To-Notification): stack EVERY applicable permit limit, one
            // line each, red when the result is OUTSIDE it — the SAME shared cell as the
            // Result Entry Express grids (permitLimitCell.tsx, standing rule 8; ALPACA
            // compares/flags, never determines compliance). No permit ⇒ the scalar value.
            permitLimit: (
              <td key="permitLimit" className="dv-numl dv-autofit">
                {renderPermitLimitCell(r.permitLimits, permitResultValue(r.result, r.numeric), r.units, blank(r.permitLimit))}
              </td>
            ),
            alert: <td key="alert">{blank(r.alert)}</td>,
            enteredBy: (
              <td key="enteredBy" className="dv-edit-cell">
                <select
                  className="dv-edit-sel"
                  value={editVal("enteredBy", r.enteredBy)}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "enteredBy", e.target.value)}
                >
                  <option value=""></option>
                  {enteredByOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </td>
            ),
            enteredDate: (
              <td key="enteredDate" className="dv-edit-cell">
                <input
                  type="date"
                  className="dv-edit-inp dv-date"
                  value={toDateInput(editVal("enteredDate", r.enteredDate))}
                  disabled={locked}
                  onChange={(e) => onBrChange(rowKey, "enteredDate", e.target.value)}
                />
              </td>
            ),
            ...(showApprovedCols
              ? {
                  approvedDate: (
                    <td key="approvedDate">
                      {approval && checkedKeys.has(rowKey) && !r.approvedDate
                        ? fmtLocalDateTime(checkedKeys.get(rowKey)!)
                        : blank(r.approvedDate)}
                    </td>
                  ),
                  approvedBy: (
                    <td key="approvedBy">
                      {approval && checkedKeys.has(rowKey) && !r.approvedBy ? currentUserName : blank(r.approvedBy)}
                    </td>
                  ),
                }
              : {}),
          };
          return (
            <tr
              key={`${r.sys}:${r.param}:${i}`}
              /* Permit-limit row tint — the lab's rules from Settings ▸ Permit Row Colors, as on the Express grids. */
              className={permitRowClassFor(r.permitLimits, r.result, r.numeric, r.units, r.mdl)}
            >
              <td className="dv-chk">
                <input
                  type="checkbox"
                  className="dv-row-chk"
                  disabled={locked}
                  checked={checkedKeys.has(rowKey)}
                  onChange={(e) => onToggleRow(rowKey, e.target.checked)}
                />
              </td>
              {/* SysSampleCode frozen alongside the checkbox (pinned; matches header). */}
              <td className="dv-sys-col">{blank(r.sys)}</td>
              {orderedIds.map((id) => cell[id])}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={2 + orderedIds.length} style={{ textAlign: "center", color: "var(--ai-muted)", padding: 20 }}>
              No results match.
            </td>
          </tr>
        )}
      </tbody>
    </table>
    {openTrend && (
      <TrendChartPopup
        route={openTrend.route}
        batchOid={batch.id ?? ""}
        sampleLabel={openTrend.label}
        method={batch.method ?? null}
        permitLimit={openTrend.permitLimit}
        permitWarn={openTrend.permitWarn}
        onClose={() => setOpenTrend(null)}
      />
    )}
    </>
  );
}

// ── Check Points table ───────────────────────────────────────────────────
function CheckPointsTable({
  rows,
  checkedKeys,
  onToggleRow,
  onToggleAll,
  filters,
  onFiltersChange,
  fmt,
  dropdownRowsFor,
  sorter,
  qualOverrides,
  onQualifierClick,
  commentOverrides,
  onCommentChange,
  decisionOverrides,
  onDecisionChange,
  locked,
}: {
  rows: CheckPointRow[];
  checkedKeys: Map<string, string>;
  // Read-only once the package is past Pending Submission (OG DataPackageViewController
  // :176-186 — the ChecklistQC grid AllowEdit=false from PendingValidation onward).
  locked: boolean;
  onToggleRow: (key: string, checked: boolean) => void;
  onToggleAll: (keys: string[], checked: boolean) => void;
  filters: Record<string, ColumnFilterValue>;
  onFiltersChange: (u: (p: Record<string, ColumnFilterValue>) => Record<string, ColumnFilterValue>) => void;
  fmt: Record<string, (r: CheckPointRow) => string>;
  dropdownRowsFor: (key: string) => CheckPointRow[];
  sorter: UseSortApi<CheckPointRow>;
  // Per-checkpoint (keyed by the checkpoint label — stable across sort/filter)
  // local Qualifier override + the click handler that opens the picker.
  qualOverrides: Record<string, string>;
  onQualifierClick: (rowKey: string, value: string) => void;
  // Editable Comment override + change handler (ALPACA free-text Comment column).
  commentOverrides: Record<string, string>;
  onCommentChange: (rowKey: string, value: string) => void;
  // Editable Yes/No/NA decision override + change handler (ALPACA checkboxes).
  decisionOverrides: Record<string, string>;
  onDecisionChange: (rowKey: string, decision: string) => void;
}) {
  // Stable header cells — see useStableHeaderCells (keeps the filter popup open
  // across the filtered reload, client feedback 2026-08-01).
  const { H, Th, tableRef, tableClass, columnManager, applyOrder, colWidthStyle } =
    useStableHeaderCells<CheckPointRow>(
      { sorter, filters, onFiltersChange, fmt, dropdownRowsFor },
      "sdms-checkpoints",
      { reorderable: true },
    );
  // Column widths matched to legacy ALPACA LIMS Check Points (proportional, so
  // they hold at any table width): CheckPoint widest (~31%), Yes / No / NA each
  // a wide equal column (~13%), Qualifier + Comment take the tail.  (Was 90px
  // Yes/No/NA + an auto-fill CheckPoint that dominated the row.)
  //
  // Array-driven columns (enables drag-REORDER): the row-select checkbox stays
  // FROZEN first; the other six are keyed by id. Header, body AND the <colgroup>
  // all render `orderedIds.map` so a reorder moves the column with its width.
  // Shared catalogue — same single source of truth the tab-bar Export reads.
  const cpNatIds = reorderableIds(checkPointColumnCatalog());
  const orderedIds = applyOrder(cpNatIds);
  const cpWidth: Record<string, string> = {
    checkPoint: "31%", yes: "13%", no: "13%", na: "13%", qualifier: "13%", comment: "14%",
  };
  const hdrCell: Record<string, React.ReactNode> = {
    checkPoint: <Th key="checkPoint" id="checkPoint"><H id="checkPoint" label="CheckPoint" /></Th>,
    yes: <Th key="yes" id="yes" style={{ textAlign: "center" }}><H id="yes" label="Yes" align="center" /></Th>,
    no: <Th key="no" id="no" style={{ textAlign: "center" }}><H id="no" label="No" align="center" /></Th>,
    na: <Th key="na" id="na" style={{ textAlign: "center" }}><H id="na" label="NA" align="center" /></Th>,
    qualifier: <Th key="qualifier" id="qualifier"><H id="qualifier" label="Qualifier" /></Th>,
    comment: <Th key="comment" id="comment"><H id="comment" label="Comment" /></Th>,
  };
  return (
    <table ref={tableRef} className={`dv-tbl dv-cp-tbl ${tableClass}`} style={{ width: "100%", tableLayout: "fixed" }}>
      {columnManager}
      <colgroup>
        <col style={{ width: "3%" }} /> {/* row-select (frozen) */}
        {/* Default % width, OVERRIDDEN by the user's dragged px width (colWidthStyle):
            in table-layout:fixed the <col> width wins over the <th>, so a resize has
            to land HERE for the column to actually change width. */}
        {orderedIds.map((id) => (
          <col key={id} style={{ width: cpWidth[id], ...colWidthStyle(id) }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className="dv-chk">
            <input
              type="checkbox"
              disabled={locked}
              checked={rows.length > 0 && rows.every((r) => checkedKeys.has(r.label))}
              onChange={(e) =>
                onToggleAll(
                  rows.map((r) => r.label),
                  e.target.checked,
                )
              }
            />
          </th>
          {orderedIds.map((id) => hdrCell[id])}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          // Current decision (local override wins).  Yes/No/NA are EDITABLE and
          // mutually exclusive (ALPACA lets the analyst change a row's answer):
          // picking one sets it, clicking the checked one clears it.
          const decision = decisionOverrides[r.label] ?? r.decision;
          const pick = (d: CheckDecision) => { if (!locked) onDecisionChange(r.label, decision === d ? "" : d); };
          const cell: Record<string, React.ReactNode> = {
            checkPoint: <td key="checkPoint">{r.label}</td>,
            // Yes/No/NA — EDITABLE checkboxes (ALPACA parity), one answer per row.
            // Locked (read-only) once the package is past Pending Submission.
            yes: (
              <td key="yes" style={{ textAlign: "center" }}>
                <input type="checkbox" disabled={locked} checked={decision === "Yes"} onChange={() => pick("Yes")} />
              </td>
            ),
            no: (
              <td key="no" style={{ textAlign: "center" }}>
                <input type="checkbox" disabled={locked} checked={decision === "No"} onChange={() => pick("No")} />
              </td>
            ),
            na: (
              <td key="na" style={{ textAlign: "center" }}>
                <input type="checkbox" disabled={locked} checked={decision === "NA"} onChange={() => pick("NA")} />
              </td>
            ),
            // Qualifier cell — click opens the Qualifiers picker (ALPACA parity);
            // the chosen Symbol(s) show here.  Local override wins over the server
            // value until Validate persists it (phase 2).
            qualifier: (
              <td
                key="qualifier"
                className="dv-qual-cell"
                title={locked ? "" : "Click to select qualifier(s)"}
                style={locked ? { cursor: "default" } : undefined}
                onClick={locked ? undefined : () => onQualifierClick(r.label, qualOverrides[r.label] ?? r.qualifier ?? "")}
              >
                {qualOverrides[r.label] ?? r.qualifier ?? ""}
              </td>
            ),
            // Comment — editable free-text (ALPACA parity). Local edit until Validate persists it (phase 2).
            comment: (
              <td key="comment" className="dv-cmt-cell">
                <input
                  className="dv-cmt-inp"
                  type="text"
                  disabled={locked}
                  value={commentOverrides[r.label] ?? r.comment ?? ""}
                  onChange={(e) => onCommentChange(r.label, e.target.value)}
                />
              </td>
            ),
          };
          return (
            <tr key={`${r.label}:${i}`}>
              <td className="dv-chk">
                <input
                  type="checkbox"
                  className="dv-row-chk"
                  disabled={locked}
                  checked={checkedKeys.has(r.label)}
                  onChange={(e) => onToggleRow(r.label, e.target.checked)}
                />
              </td>
              {orderedIds.map((id) => cell[id])}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={1 + orderedIds.length} style={{ textAlign: "center", color: "var(--ai-muted)", padding: 20 }}>
              No check points match.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ── Review Log table ─────────────────────────────────────────────────────
// Column order (client 2026-08-01 — diverges from the legacy XAF
// `DataPackage_ReviewLog_ListView_DataReview`):
//
//   0  ProcessMode   1  Role   2  DecisionStatus   3  ReviewScope
//   4  Comment       5  Electronic Signature
//
// DateTime and User are REMOVED as their own columns — the signer (real name +
// code) and the timestamp now live inside the Electronic Signature card, along
// with a computed integrity digest. Sort is still server-side DESC by Date
// (newest review action first).
function ReviewLogTable({
  rows,
  batchLabel,
  checkedKeys,
  onToggleRow,
  filters,
  onFiltersChange,
  fmt,
  dropdownRowsFor,
  sorter,
  rlOverrides,
  onRlChange,
  hideSignature = false,
}: {
  rows: ApiReviewLogRow[];
  batchLabel: string; // QC Batch label shown in the Electronic Signature card
  checkedKeys: Map<string, string>;
  onToggleRow: (key: string, checked: boolean) => void;
  filters: Record<string, ColumnFilterValue>;
  onFiltersChange: (u: (p: Record<string, ColumnFilterValue>) => Record<string, ColumnFilterValue>) => void;
  fmt: Record<string, (r: ApiReviewLogRow) => string>;
  dropdownRowsFor: (key: string) => ApiReviewLogRow[];
  sorter: UseSortApi<ApiReviewLogRow>;
  // Editable-cell overrides (Comment / DecisionStatus / ReviewScope only).
  rlOverrides: Record<string, Record<string, string>>;
  onRlChange: (rowKey: string, col: string, value: string) => void;
  /** Drop the ElectronicSignature column. Legacy's Review Log has no such column —
   *  dbo.DataPackageReviewLog has no signature field at all (Oid · Date · Action ·
   *  Operator · Status · DataPackage · OptimisticLockField · GCRecord · Remark ·
   *  DecisionStatus · ReviewScope), and the card AI renders is built from Operator
   *  + Date + decision plus a server-COMPUTED integrity digest
   *  (ReviewLogReader.ComputeSignatureHash), not a stored value.
   *
   *  It stays on SDMS Result Validation, where it was built and refined with a
   *  client (see the dated notes around the card), and is hidden on a PLM package
   *  so that screen matches legacy's seven columns exactly (owner decision
   *  2026-09-24). */
  hideSignature?: boolean;
}) {
  // Stable header cells — see useStableHeaderCells (keeps the filter popup open
  // across the filtered reload, client feedback 2026-08-01).
  const { H, Th, tableRef, tableClass, columnManager, applyOrder } =
    useStableHeaderCells<ApiReviewLogRow>(
      { sorter, filters, onFiltersChange, fmt, dropdownRowsFor },
      "sdms-reviewlog",
      { reorderable: true },
    );
  // Pick a colored mode badge per ProcessMode value so the badge gives an
  // at-a-glance read of which step the row belongs to (blue=Peer, orange=SDMS).
  const modeClass = (pm: string | null): string => {
    const p = (pm ?? "").toLowerCase();
    if (p.includes("peer")) return "dv-peer";
    if (p.includes("approval")) return "dv-sdms";
    if (p.includes("sdms") || p.includes("validation")) return "dv-sdms";
    return "dv-muted";
  };

  // Comment is now a CHECKBOX that opens a text-entry dialog (client 2026-08-04:
  // the Comment column is rarely used and was too wide).  Clicking the checkbox
  // opens the shared prompt (textarea) pre-filled with the current comment; OK
  // saves it (and the box shows checked whenever a non-empty comment exists),
  // Cancel leaves it unchanged.  `required:false` lets a user CLEAR a comment.
  const { prompt } = useConfirm();
  const editComment = async (rowKey: string, current: string) => {
    const result = await prompt({
      title: "Comment",
      message: "Enter a comment for this review entry:",
      initialValue: current,
      placeholder: "Comment…",
      required: false,
    });
    if (result !== null) onRlChange(rowKey, "comment", result); // null = Cancel
  };

  // Array-driven columns (enables drag-REORDER): the row-select checkbox stays
  // FROZEN first; the other six are keyed by id. Header AND body render
  // `orderedIds.map`, so a reorder moves each header with its body cell. The
  // per-column widths are INLINE + id-based (rlWidth) — the previous
  // `.dv-rl-tbl th:nth-child(N)` CSS was position-based and would misapply widths
  // after a reorder — so they follow the column (a user resize still overrides).
  // Column order (client 2026-08-01): ProcessMode · Role · DecisionStatus ·
  // ReviewScope · Comment · Electronic Signature. DateTime + User were removed as
  // columns — they live inside the signature card.
  // Legacy's Review Log column ORDER (verified against the running app 2026-09-24):
  // ProcessMode · DateTime · User · Role · Comment · DecisionStatus · ReviewScope.
  // DateTime and User were missing entirely — the row said a decision had been made
  // but not by whom or when, which is the point of a review log. ElectronicSignature
  // is AI's own addition (ADR-0009) and stays, last, where it does not displace a
  // legacy column.
  const rlNatIds = [
    "processMode", "date", "user", "role", "comment", "decisionStatus", "reviewScope",
    ...(hideSignature ? [] : ["electronicSignature"]),
  ];
  const orderedIds = applyOrder(rlNatIds);
  const rlWidth: Record<string, React.CSSProperties> = {
    processMode: { width: 150 },
    date: { width: 140 },
    user: { width: 120 },
    role: { width: 150 },
    decisionStatus: { width: 160 },
    reviewScope: { width: 150 },
    comment: { width: 110, minWidth: 110, textAlign: "center" },
    electronicSignature: { width: "auto" }, // absorbs the leftover width
  };
  const hdrCell: Record<string, React.ReactNode> = {
    processMode: <Th key="processMode" id="processMode" style={rlWidth.processMode}><H id="processMode" label="ProcessMode" /></Th>,
    date: <Th key="date" id="date" style={rlWidth.date}><H id="date" label="DateTime" /></Th>,
    user: <Th key="user" id="user" style={rlWidth.user}><H id="user" label="User" /></Th>,
    role: <Th key="role" id="role" style={rlWidth.role}><H id="role" label="Role" /></Th>,
    decisionStatus: <Th key="decisionStatus" id="decisionStatus" style={rlWidth.decisionStatus}><H id="decisionStatus" label="DecisionStatus" /></Th>,
    reviewScope: <Th key="reviewScope" id="reviewScope" style={rlWidth.reviewScope}><H id="reviewScope" label="ReviewScope" /></Th>,
    // Comment header — a plain sortable label (no filter funnel): the column is a
    // narrow CHECKBOX now, so the funnel only crowded "Comment" into "Co…". `label`
    // registers it in the Column Chooser (it has no <H> child to do that).
    comment: (
      <Th key="comment" id="comment" label="Comment" style={rlWidth.comment}>
        <SortableHeader label="Comment" sortKey="comment" sort={sorter.sort} />
      </Th>
    ),
    // Electronic Signature — sortable (by signer + time), no filter funnel (each
    // card is unique per row). A <Th> with a bare SortableHeader child + `label` so
    // it gets the SAME menu / Hide / Column-Chooser / drag / resize as every other
    // column (previously a plain <th>, which left it out of the chooser).
    electronicSignature: (
      <Th key="electronicSignature" id="electronicSignature" label="ElectronicSignature" style={rlWidth.electronicSignature}>
        <SortableHeader label="ElectronicSignature" sortKey="electronicSignature" sort={sorter.sort} />
      </Th>
    ),
  };

  return (
    <table ref={tableRef} className={`dv-tbl dv-rl-tbl ${tableClass}`}>
      {columnManager}
      <thead>
        <tr>
          {/* No header select-all — ALPACA sets the Review Log grid's
              SelectAllCheckboxMode = None (DataReviewController.Grid_Load line 833);
              only the single actionable SDMS Validation/Approval row is selectable. */}
          <th className="dv-chk" />
          {orderedIds.map((id) => hdrCell[id])}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const rlVal = (col: string, raw: string | null) => rlOverrides[r.id]?.[col] ?? raw ?? "";
          // Signature-card values read the SAME rlOverrides the dropdowns write to,
          // so the auto-fill on checkbox-tick (ALPACA Grid_SelectionChanged) previews
          // the signer + stamp + decision the instant the SDMS row is ticked.
          const sigName = rlVal("operatorName", r.operatorName);
          const sigCode = rlVal("user", r.user);
          const sigDate = rlVal("date", r.date);
          const decisionStatusV = rlVal("decisionStatus", r.decisionStatus);
          const reviewScopeV = rlVal("reviewScope", r.reviewScope);
          const roleV = rlVal("role", r.role);
          // ALPACA disables (does NOT hide) the Review Log select checkbox when the
          // row's Status == "Approved" (DataReviewController.Grid_CommandButton-
          // Initialize lines 942-948, e.Enabled = false) — the completed Peer Review
          // row shows a GREYED, unclickable checkbox, not a missing one (client
          // 2026-08-04 "I want the checkbox but only disabled").
          const selectable = (r.status ?? "").toLowerCase() !== "approved";
          const cell: Record<string, React.ReactNode> = {
            // ProcessMode — read-only badge (ALPACA).
            processMode: (
              <td key="processMode">
                {r.processMode ? <span className={`dv-rl-mode ${modeClass(r.processMode)}`}>{r.processMode}</span> : ""}
              </td>
            ),
            // DateTime — when the row was signed. Through the shared fmtLocal*
            // helper (§14b.6): local, 24-hour, never AM/PM, never inline.
            // Reads the tick preview (rlOverrides) like the signature card, so a
            // ticked row shows its DateTime / User before it is saved.
            date: <td key="date">{sigDate ? fmtLocalDateTime(sigDate) || "" : ""}</td>,
            // User — the signer CODE ALPACA shows (Employee.DisplayName, e.g.
            // E169217); the real name rides in the signature card beside it.
            user: <td key="user">{sigCode}</td>,
            // Role — read-only badge (previews the signer's role on tick).
            role: <td key="role">{roleV ? <span className="dv-rl-role">{roleV}</span> : ""}</td>,
            // DecisionStatus — editable dropdown (Accept / Reject).
            decisionStatus: (
              <td key="decisionStatus" className="dv-edit-cell">
                <select
                  className="dv-edit-sel"
                  value={rlVal("decisionStatus", r.decisionStatus)}
                  onChange={(e) => onRlChange(r.id, "decisionStatus", e.target.value)}
                >
                  {/* N/A = the unset/null state (ALPACA DecisionStatus is a NULLABLE
                  DPDecisionStatus enum — Accept/Reject only; null shows as N/A). */}
                  <option value="">N/A</option>
                  <option value="Accept">Accept</option>
                  <option value="Reject">Reject</option>
                </select>
              </td>
            ),
            // ReviewScope — editable dropdown (All Results / Selected Results).
            reviewScope: (
              <td key="reviewScope" className="dv-edit-cell">
                <select
                  className="dv-edit-sel"
                  value={rlVal("reviewScope", r.reviewScope)}
                  onChange={(e) => onRlChange(r.id, "reviewScope", e.target.value)}
                >
                  {/* N/A = unset/null (ALPACA ReviewScope is a NULLABLE DPReviewScope
                  enum — All Results / Selected Results only; null shows as N/A). */}
                  <option value="">N/A</option>
                  <option value="All Results">All Results</option>
                  <option value="Selected Results">Selected Results</option>
                </select>
              </td>
            ),
            // Comment — a CHECKBOX (checked = a comment exists) that opens a
            // text-entry dialog on click (client 2026-08-04). `checked` is derived
            // from the comment, so clicking never toggles it directly — it opens the
            // prompt and the OK'd text drives the checked state. Centered inline
            // (was the .dv-rl-tbl nth-child(6) rule, now id-based for reorder-safety).
            comment: (
              <td key="comment" className="dv-rl-cmt-cell" style={{ textAlign: "center" }}>
                {(() => {
                  const cmt = rlVal("comment", r.comment);
                  return (
                    <input
                      type="checkbox"
                      className="dv-rl-cmt-chk"
                      checked={cmt.trim().length > 0}
                      onChange={() => editComment(r.id, cmt)}
                      title={cmt || "Click to add a comment"}
                      aria-label={cmt ? "Edit comment" : "Add comment"}
                    />
                  );
                })()}
              </td>
            ),
            // Electronic Signature — consolidates the signer (real name + code, the
            // old User column) and the timestamp (the old DateTime column) into a
            // signature card, plus a computed integrity digest that "Verifies" by
            // recomputation. Laid out on TWO lines (client 2026-08-03): (1) signer +
            // signing time; (2) the review metadata (left) + integrity digest (right).
            electronicSignature: (
              <td key="electronicSignature" className="dv-rl-sig-cell">
                <div className={`dv-rl-sig ${modeClass(r.processMode)}`}>
                  {/* Line 1 — signer on the left, signing stamp on the right.
                      The stamp is NOT a grid date: ISO + explicit zone so the
                      signing moment is unambiguous (see fmtSignatureStamp). */}
                  <div className="dv-rl-sig-head">
                    <span className="dv-rl-sig-name">
                      {/* A placeholder row is NOT signed yet — saying "Signed by" would
                          claim a signature nobody has given (client 2026-08-19). */}
                      {r.id.startsWith("__") ? "Awaiting signature by " : "Signed by "}
                      <b>{sigName || sigCode || "—"}</b>
                      {sigName && sigCode && sigCode !== sigName ? <span className="dv-rl-sig-code"> ({sigCode})</span> : null}
                    </span>
                    {sigDate ? <span className="dv-rl-sig-time">{fmtSignatureStamp(sigDate)}</span> : null}
                  </div>
                  {/* Line 2 — review metadata on the LEFT, integrity digest pushed
                      to the RIGHT, on ONE row: the whole card is now TWO lines
                      (client 2026-08-03), which the wide signature column affords. */}
                  <div className="dv-rl-sig-row2">
                    {(() => {
                      const roleMode = [roleV, r.processMode].filter(Boolean).join(" · ");
                      const lead = roleMode
                        ? `${roleMode}${decisionStatusV ? ` : ${decisionStatusV}` : ""}`
                        : decisionStatusV || "";
                      const segs: ReactNode[] = [];
                      if (lead) segs.push(lead);
                      if (reviewScopeV) segs.push(reviewScopeV);
                      if (batchLabel)
                        segs.push(
                          <>
                            QC Batch <b>{batchLabel}</b>
                          </>,
                        );
                      if (!segs.length) return null;
                      return (
                        <span className="dv-rl-sig-sub">
                          {segs.map((s, i) => (
                            <Fragment key={i}>
                              {i > 0 ? " · " : null}
                              {s}
                            </Fragment>
                          ))}
                        </span>
                      );
                    })()}
                    {r.signatureHash ? (
                      <span className="dv-rl-sig-hash">
                        <span className="dv-rl-sig-sha">
                          SHA-256 {r.signatureHash.slice(0, 8)}…{r.signatureHash.slice(-6)}
                        </span>
                        <span className="dv-rl-verified-badge">✓ Verified</span>
                      </span>
                    ) : null}
                  </div>
                </div>
              </td>
            ),
          };
          return (
            <tr key={r.id}>
              <td className="dv-chk">
                {/* Checkbox on every row; DISABLED (greyed, unclickable) when the
                    row is already approved — ALPACA parity (shown, not hidden). */}
                <input
                  type="checkbox"
                  className="dv-row-chk"
                  checked={checkedKeys.has(r.id)}
                  disabled={!selectable}
                  onChange={(e) => {
                    if (selectable) onToggleRow(r.id, e.target.checked);
                  }}
                />
              </td>
              {orderedIds.map((id) => cell[id])}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={1 + orderedIds.length} style={{ textAlign: "center", color: "var(--ai-muted)", padding: 20 }}>
              No review log entries for this batch yet.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ── Attachments table ────────────────────────────────────────────────────
function AttachmentsTable({
  rows,
  filters,
  onFiltersChange,
  fmt,
  dropdownRowsFor,
  sorter,
  canModify = false,
  onDelete,
  selectedIds = new Set<string>(),
  onToggleSelect,
}: {
  rows: AttachmentRow[];
  filters: Record<string, ColumnFilterValue>;
  onFiltersChange: (u: (p: Record<string, ColumnFilterValue>) => Record<string, ColumnFilterValue>) => void;
  fmt: Record<string, (r: AttachmentRow) => string>;
  dropdownRowsFor: (key: string) => AttachmentRow[];
  sorter: UseSortApi<AttachmentRow>;
  canModify?: boolean;
  onDelete?: (row: AttachmentRow) => void;
  /** Ticked attachment ids — backs the toolbar Delete. */
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
}) {
  const notify = useNotify(); // shared notifications (§18.6) — replaces the native alert dialog
  // Shared header factory (same as the other SDMS detail grids): gives Attachments
  // the right-click menu (Sort · Hide · Show All · Column Chooser), drag-reorder,
  // resize and persistence instead of the old bespoke H/Th (which had none).
  const { H, Th, tableRef, tableClass, columnManager, applyOrder } =
    useStableHeaderCells<AttachmentRow>(
      { sorter, filters, onFiltersChange, fmt, dropdownRowsFor },
      "sdms-attachments",
      { reorderable: true },
    );
  const natIds = ["name", "category", "file", "date", "operator"];
  const orderedIds = applyOrder(natIds);
  const hdrCell: Record<string, React.ReactNode> = {
    name: <Th key="name" id="name"><H id="name" label="Name" /></Th>,
    category: <Th key="category" id="category"><H id="category" label="Category" /></Th>,
    file: <Th key="file" id="file"><H id="file" label="Attachment" /></Th>,
    date: <Th key="date" id="date"><H id="date" label="Date" /></Th>,
    operator: <Th key="operator" id="operator"><H id="operator" label="Operator" /></Th>,
  };
  return (
    <table ref={tableRef} className={`dv-tbl ${tableClass}`}>
      {columnManager}
      <thead>
        <tr>
          <th className="dv-chk" />
          {orderedIds.map((id) => hdrCell[id])}
          {canModify && <th className="dv-att-act" style={{ width: 44 }} />}
        </tr>
      </thead>
      <tbody>
        {rows.map((a, i) => {
          // Live rows carry a fileOid — click fetches the bytes through the
          // authenticated axios client (JWT lives in an Authorization header,
          // so a plain <a href target=_blank> gets 401'd and bounced to /login;
          // that's the "opens the login screen" bug from earlier).  Blob →
          // object URL → invisible anchor with `download` attribute → click.
          // Seed rows have no fileOid, render as inert text.
          const onDownload = a.fileOid
            ? async (e: React.MouseEvent) => {
                e.preventDefault();
                try {
                  const res = await api.get<Blob>(
                    `/api/v1/analysis/result-validation/attachments/${encodeURIComponent(a.fileOid!)}/download`,
                    { responseType: "blob" },
                  );
                  const blob = res.data;
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = a.file || "attachment";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  // Release the memory once the browser has picked up the blob.
                  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
                } catch (err) {
                  console.warn("[Attachments] download failed", a.fileOid, err);
                  notify.error("Failed to download this attachment. Please try again.");
                }
              }
            : (e: React.MouseEvent) => e.preventDefault();
          const cell: Record<string, React.ReactNode> = {
            name: <td key="name">{a.name}</td>,
            category: <td key="category">{a.category}</td>,
            file: (
              <td key="file">
                <a
                  className="dv-att-link"
                  href={a.fileOid ? "#" : undefined}
                  onClick={onDownload}
                  title={a.fileOid ? `Download ${a.file}` : a.file}
                >
                  {a.file}
                </a>
              </td>
            ),
            date: <td key="date">{a.date ?? ""}</td>,
            operator: <td key="operator">{a.operator ?? ""}</td>,
          };
          return (
            <tr key={`${a.name}:${i}`}>
              <td className="dv-chk">
                <input
                  type="checkbox"
                  checked={!!a.id && selectedIds.has(a.id)}
                  disabled={!a.id}
                  onChange={() => a.id && onToggleSelect?.(a.id)}
                  aria-label={`Select ${a.name || a.file || "attachment"}`}
                />
              </td>
              {orderedIds.map((id) => cell[id])}
              {canModify && (
                <td className="dv-att-act" style={{ textAlign: "center" }}>
                  {a.id && (
                    <button
                      type="button"
                      title="Remove attachment"
                      onClick={() => onDelete?.(a)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ai-red)", padding: 2 }}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  )}
                </td>
              )}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={1 + orderedIds.length + (canModify ? 1 : 0)} style={{ textAlign: "center", color: "var(--ai-muted)", padding: 20 }}>
              No attachments.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
